/**
 * Variant C shell — the root container the App mounts when the
 * ``?ui=variant-c`` flag is on.
 *
 * Owns:
 *   - The run-state reducer (idle | mid-run | approval | post-run).
 *   - The LoomFresh selection captured at Start Draft (so the top
 *     bar can show it through the run lifecycle).
 *   - The active doc-type config (drives the initial section list).
 *   - The SSE subscription via useVariantCRun.
 *
 * Does NOT own:
 *   - Library/workspace fetching (LoomFresh handles its own picker).
 *   - Per-section status logic (useVariantCRun + sseAdapter).
 *   - Visual presentation (TopBarC, LoomFresh, LoomSpine).
 *
 * Composition:
 *
 *   idle     → <TopBarC/> + <LoomFresh/>
 *   mid-run  → <TopBarC heartbeat/> + <LoomSpine/>
 *   approval → <TopBarC heartbeat/> + <LoomSpine/> + (popout in P5)
 *   post-run → <TopBarC/> + <LoomSpine/> + (review surface in P7)
 */

import { makeStyles } from "@fluentui/react-components";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  type DocTypeConfig,
  type SectionIssue,
  adhocDraftSection,
  blobToBase64,
  deleteDocument,
  downloadBlob,
  editDraftSection,
  fetchDocumentDownload,
  fetchRunDocument,
  fetchSpine,
  getDocTypeConfig,
  getRunAuditLibrary,
  type RunAuditLibrary,
  getRunIssues,
  getRunProposals,
  getSectionIssues,
  listActiveRuns,
  listRuns,
  getRunSnapshot,
  getSectionState,
  createLibrary,
  keepalivePauseRun,
  type ArtefactStageEvent,
  type CheckoutItem,
  type CheckoutRun,
  getDocTypes,
  getWorkspaceBilling,
  logout,
  runDocumentUrl,
  type StartedPaidDraft,
  streamTableArtefact,
  type TableArtefactPreview,
  uploadContent,
  uploadFile,
  upsertDocumentSession,
} from "../api/client";
import { buildOrder, billedRoots, isRefusal, type Order, totalRootCount } from "./payments/order";
import { docPriceOf } from "./payments/pricing";
import { useCheckout } from "./payments/useCheckout";
import OrderDialog from "./payments/OrderDialog";
import CheckoutDialog from "./payments/CheckoutDialog";
import BillingDialog from "./payments/BillingDialog";
import { fetchImageDataUri } from "../api/imageSearchApi";
import { REVIEW_AGENT_ENABLED } from "../config/reviewAgent";
import { isDevBuild } from "../config/buildEnv";
import { useFlag } from "../context/FeatureFlagsContext";
import { useUserInputBridge } from "../hooks/useUserInputBridge";
import { type EmptyDocWatcherHandle, startEmptyDocWatcher } from "../services/emptyDocDetector";
import {
  beginLiveDocLoad,
  deferUntilLiveDocSettled,
  endLiveDocLoad,
  isLiveDocLoading,
} from "../services/liveDocLoadState";
import { initSyncEngine, pauseSync, resumeSync } from "../services/syncEngine";
import { checkDocHasContent, subscribeSelectionChange } from "../services/wordProbe";
import { coalesceInFlight, queueWordRead } from "../services/wordReadQueue";
import { getLogger } from "../observability";
import {
  clearActiveRunId,
  detectCursorSectionNumberWithFallback,
  getActiveRunId,
  getDraftPointer,
  hydrateLiveDocFromBase64,
  insertInlineImageAtSelection,
  isImageSelected,
  isSyncReady,
  isValidLibraryId,
  loadDocSelectionProps,
  markSyncReady,
  navigateToSection,
  replaceSelectedImage,
  rotateDocumentId,
  saveDocSelectionProps,
  saveDocumentId,
  saveWordInsertionMode,
  selectMarkerParagraph,
  setActiveRunId,
  setDraftPointer,
} from "../services/wordWriter";
import type { Library, MentionAttachment, ProgressEvent, Workspace } from "../types";
import type { RunStatus } from "../types/index";
import { useChatHistoryView } from "./chatHistoryWiring";
import { type CommandSnapshot, findCommandById } from "./commandRegistry";
import { suggestPostAction } from "./commandSuggester";
import {
  buildComingSoonMessage,
  buildJumpErrorMessage,
  resolveJumpTarget,
  resolveNextTarget,
} from "./commands/dispatchHelpers";
import { exportFilename, toCsv, toJson, triggerDownload } from "./commands/export";
import type { CommandIntent } from "./commands/grammar";
import { type MenuRenderState, buildMenuGroups } from "./commands/menu";
import AsthraBubbleSurface from "./components/AsthraBubbleSurface";
// CommandPalette retired 2026-04-23 (Spec 29 palette-merge). The
// slash menu is now the sole command surface.
import CommandRail, { type CommandRailHandle } from "./components/CommandRail";
import type { ContextSlot } from "./components/ContextStrip";
import DigestReturnPill from "./components/DigestReturnPill";
import DoctypePicker, { lookupDoctypeChoice } from "./components/DoctypePicker";
import AdhocProposalMessage from "./components/AdhocProposalMessage";
import EditProposalMessage from "./components/EditProposalMessage";
import { HomeScene, type RunLoadContext } from "./components/HomeScene";
import { ImageSearchPane } from "./components/ImageSearchPane";
import Inspector from "./components/Inspector";
import KillRunConfirmation from "./components/KillRunConfirmation";
// LoomFresh stays imported for the LoomFreshSelection type (still
// the shape handleStartDraft consumes). The component itself is no
// longer rendered after Spec 29 Phase A — the conversational flow
// replaced its takeover.
import type { LoomFreshSelection, SectionItem } from "./components/LoomFresh";
import LoomPlanReview, { type PlanData } from "./components/LoomPlanReview";
import LoomSelection, { type LoomSelectionCommit } from "./components/LoomSelection";
import CustomDraftPicker from "./components/CustomDraftPicker";
import LoomSpine from "./components/LoomSpine";
import ImportExistingDocModal from "./components/ImportExistingDocModal";
import NewProjectConfirmModal from "./components/NewProjectConfirmModal";
import RegenOne, { type RegenConfirmation, type RegenTarget } from "./components/RegenOne";
import SaveAndStartFreshModal from "./components/SaveAndStartFreshModal";
import ResolveControlBar from "./components/ResolveControlBar";
import ResolveIssuesWizard from "./components/ResolveIssuesWizard";
import { SaveProgressCard } from "./components/SaveProgressCard";
import { LoadProgressCard } from "./components/LoadProgressCard";
import {
  buildSaveSteps,
  buildLoadSteps,
  buildCustomImportSteps,
  CUSTOM_IMPORT_STEP_DEFS,
  LOAD_STEP_DEFS,
} from "./components/HomeLoadProgress";
import SpineControlBar from "./components/SpineControlBar";
import SubmissionIssuesTab, { type ResolveSelectionTarget } from "./components/SubmissionIssuesTab";
import TabBar, { type TabId } from "./components/TabBar";
import TimelineView, { buildIdleGreeting, IdleGreeting } from "./components/TimelineView";
import { TocPopup } from "./components/TocPopup";
import TopBarC from "./components/TopBarC";
import UserInputInlineRow from "./components/UserInputInlineRow";
import { WordOpsPanel } from "./components/WordOpsPanel";
import WorkspacePicker from "./components/WorkspacePicker";
import { type DoctypeChoice, buildCustomSkipSelection, buildLoomFreshSelection } from "./draftFlow";
import { entriesNewestFirst } from "./eventLog";
import { decideGreetingEmission } from "./greetingPolicy";
import { useAllowedDocCodes } from "./hooks/useAllowedDocCodes";
import { useAppConfig } from "./hooks/useAppConfig";
import { parseWriteApprovalPayload } from "../components/WriteApprovalView";
import { describeToolCall, getToolDisplay } from "../config/toolDisplayNames";
import { ActionGroup, StepRow } from "./components/ActionGroup";
import MessageBubble from "./components/MessageBubble";
import WriteApprovalMessage from "./components/WriteApprovalMessage";
import { type ChatDispatchTarget, buildStreamingStatus, useChatBridge } from "./hooks/useChatBridge";
import { useChatSystem } from "./hooks/useChatSystem";
import { useDocumentContext } from "./hooks/useDocumentContext";
import { useDraftFlow } from "./hooks/useDraftFlow";
import { type AdhocProposal, useAdhocProposalFlow } from "./hooks/useAdhocProposalFlow";
import { useHighlightEditFlow } from "./hooks/useHighlightEditFlow";
import { useRegenFlow } from "./hooks/useRegenFlow";
import { useSelectionChip } from "./hooks/useSelectionChip";
import {
  type AgentActivityLine,
  type AgentActivityMap,
  applyActivityEvent,
  clearAllActivity,
  hasAnyActivity,
} from "./services/agentActivityFeed";
import { useResolveIssuesFlow } from "./hooks/useResolveIssuesFlow";
import { useRunControl } from "./hooks/useRunControl";
import { useRunVerdictWatcher } from "./hooks/useRunVerdictWatcher";
import { useSelectionCitations } from "./hooks/useSelectionCitations";
import { useSessionBootstrap } from "./hooks/useSessionBootstrap";
import { type SpineSection, computeLockedModuleIds, useVariantCRun } from "./hooks/useVariantCRun";
import { isAuditActiveForRun, subscribeAuditActivity } from "./reviewStore";
import { useWordEdits } from "./hooks/useWordEdits";
import { useWordWriteQueue } from "./hooks/useWordWriteQueue";
import { useWorkspaceLibrary } from "./hooks/useWorkspaceLibrary";
import { useWriterPresence } from "./hooks/useWriterPresence";
import { intentLabel } from "./intentLabels";
// Spec 34 — chat-first Library Hub (replaces the legacy modal).
import { ContextChipsBar } from "./library-hub/components/ContextChipsBar";
import { type HubMode, LibraryHubSlideOver } from "./library-hub/components/LibraryHubSlideOver";
import ArtefactBuilderPanel from "./library-hub/artefact/ArtefactBuilderPanel";
import ArtefactPreviewModal from "./library-hub/artefact/ArtefactPreviewModal";
import { inventoryDtoToView } from "./library-hub/inventoryAdapter";
import { mergeUploadsAndInventory } from "./library-hub/mergeUploadsAndInventory";
import { resolveProgressLabel } from "./library-hub/resolveProgressLabel";
import type { UploadSlot } from "./library-hub/types";
import { deriveDocTypeOptions, isTargetDoctype } from "./libraryDoctypeVocabulary";
import type { ChatMessage } from "./messageProjection";
// DisclosureBanner retired from always-visible chrome (Spec 29) —
// copy relocates into the agent's greeting message (Phase 3).
// Import kept commented out in case it's needed temporarily during
// the migration; remove in Phase 4 when the greeting carries the
// regulatory copy.
// useCommandPalette + commands/registry retired 2026-04-23 with
// the palette. All commands live in commands/menu via the slash
// menu; see Spec 29 palette-merge.
import { formatSectionLabel } from "./sectionLabel";
import { type GateMeta, type SelectionNode, type WriteStrategy, buildGateMeta, buildSelectionTree } from "./selection";
import { leafIdentityPatch, leafOptions, scopedCatalog } from "./leafDocuments";
import {
  CHAT_PAGE_SIZE,
  fetchDocumentChat,
  fetchRunChat,
  flushAllPendingSaves,
  getCachedDocId,
  rememberRunRecoveryHint,
  scheduleSave,
  setCachedDocId,
} from "./services/documentSessionService";
import { performForeignDocRotation } from "./services/foreignDocRotation";
import { getLibraryDoctype, setLibraryDoctype } from "./services/libraryDoctypeStore";
import { loadCustomRunIntoLiveDoc } from "./services/loadCustomRunIntoLiveDoc";
import { loadRunIntoLiveDoc } from "./services/loadRunIntoLiveDoc";
import {
  type LiveDocLoadMap,
  clearLiveDocLoad,
  outcomeFromSectionsWritten,
  setLiveDocFailed,
  setLiveDocLoading,
} from "./services/liveDocLoadTracker";
import { reinsertModuleIntoLiveDoc } from "./services/reinsertModuleIntoLiveDoc";
import { openExternal } from "./services/openExternal";
import { MISSING_INFO_INSTRUCTION, buildResolveInstruction } from "./services/resolveInstruction";
import { proposalsSincePassStart, type ResolvePassTarget, summariseResolvePass } from "./services/resolvePassSummary";
import {
  RUN_FAILED_AFTER_DRAFTING_ERROR,
  deriveRunSummaryFromSpine,
  mapCustomSpineDTO,
  mapSpineDTO,
  reconcileSpineWithRunStatus,
  restoresToIdle,
  restoresToPostRun,
  submissionSectionTotal,
} from "./services/runHydration";
import { shouldShowPostRunBanner } from "./postRunBanner";
import { buildRetrievalSubline } from "./sseAdapter";
import type { RetrievalProgress, RunIdentity } from "./sseAdapter";
import {
  type TimelineFilter,
  type VariantCState,
  type ViewMode,
  entryMatchesFilter,
  initialState,
  isActiveRun,
  variantCReducer,
} from "./state";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "./tokens";
import { deriveQualitySignals, translateVerdictToApproval } from "./verdictTranslator";

/**
 * Auto-load a single-section redraft into the live Word document without a
 * confirmation step — for BOTH redraft entry points (the Inspector "Regenerate
 * this section" flow and the chat ``/redraft`` command). Kill-switch: set
 * ``VITE_REDRAFT_AUTO_LOAD=0`` (or "false") to fall back to the prior behavior
 * (Inspector regen requires a manual Load; chat redraft shows an Approve card).
 * Default ON.
 */
const REDRAFT_AUTO_LOAD =
  import.meta.env.VITE_REDRAFT_AUTO_LOAD !== "0" && import.meta.env.VITE_REDRAFT_AUTO_LOAD !== "false";

/**
 * Poll Word until a freshly-inserted docx has finished rendering AND the
 * heading structure has stopped changing.  Called after
 * hydrateLiveDocFromBase64 / loadRunIntoLiveDoc to ensure sync doesn't resume —
 * and, via the liveDocLoadState guard, that background re-projection doesn't
 * fire — while the body is still empty or mid-insert.
 *
 * A bare "paragraph count > 1" check returns as soon as the FIRST content
 * appears, but the native-numbering / built-in-style rebuild (hydrate
 * STEP3/3.5) keeps rewriting the leading headings for a beat after that. During
 * that window a section-boundary scan is degraded, which is exactly when a
 * racing delete-then-reinsert blanks a large region. So we also require the
 * count of heading-styled paragraphs to be non-zero and STABLE across two
 * consecutive polls before treating the doc as settled.
 *
 * Resolves once settled, or after `timeoutMs` (then resumes regardless so the
 * engine is never stuck paused).
 */
async function _waitForDocumentLoaded(timeoutMs = 15_000): Promise<void> {
  const POLL_MS = 500;
  const deadline = Date.now() + timeoutMs;
  let pollCount = 0;
  let prevHeadingCount = -1;

  while (Date.now() < deadline) {
    pollCount++;
    try {
      const { paraCount, headingCount } = await Word.run(async (ctx) => {
        const paras = ctx.document.body.paragraphs;
        paras.load("items/styleBuiltIn");
        await ctx.sync();
        const headings = paras.items.filter((p) => {
          const style = (p.styleBuiltIn as string) || "";
          return style.includes("Heading") || style.includes("heading");
        }).length;
        return { paraCount: paras.items.length, headingCount: headings };
      });
      console.log(
        `[load-draft] _waitForDocumentLoaded poll#${pollCount} paragraphCount=${paraCount} headingCount=${headingCount} prevHeadingCount=${prevHeadingCount}`,
      );
      // Settled = content present AND at least one recognized heading whose count
      // held steady since the previous poll (numbering/styles done re-attaching).
      if (paraCount > 1 && headingCount > 0 && headingCount === prevHeadingCount) {
        console.log(
          `[load-draft] _waitForDocumentLoaded satisfied at poll#${pollCount} paragraphCount=${paraCount} headingCount=${headingCount}`,
        );
        return;
      }
      prevHeadingCount = headingCount;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.log(`[load-draft] _waitForDocumentLoaded poll#${pollCount} threw — Word busy — error="${message}"`);
    }
    await new Promise<void>((r) => setTimeout(r, POLL_MS));
  }
  console.log(`[load-draft] _waitForDocumentLoaded TIMED OUT after poll#${pollCount} — resuming sync anyway`);
}

/** Sentinel commandId for the persisted idle greeting (Phase 9.5
 *  follow-up). Lets the one-shot greeting share the ``agent_note``
 *  EventKind without inventing a new one (avoids 4 closed-enum
 *  guard updates), while still being detectable for the reload
 *  no-emit check. The double-underscore signals "internal — not a
 *  registry command id". */
const GREETING_SENTINEL_ID = "__greeting__";

/** Stable empty activity-lines array (module-level so the Inspector prop keeps
 *  a constant identity across renders when a section has no live feed). */
const EMPTY_ACTIVITY_LINES: readonly AgentActivityLine[] = [];

/** Upper bound on the click-time cursor-section read behind the section
 *  chips; past it the chip reports "no section" instead of hanging. */
const CURSOR_SECTION_READ_TIMEOUT_MS = 10_000;

/**
 * Doctype codes the picker is narrowed to when ``show.all-doctypes`` is
 * off. Codes are DoctypePicker catalog codes (doc-catalog.ts), not
 * backend doc_type values — "CSR-Abbr" is a separate code from "CSR" and
 * would have to be listed explicitly to appear.
 *
 * Module-level so the array identity is stable across renders; an inline
 * literal would re-run the picker's filtering memos on every pass.
 */
const RESTRICTED_DOC_CODES: readonly string[] = ["CSR", "CTD M2"];

const useStyles = makeStyles({
  root: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: surfaceColors.paper,
    fontFamily: fonts.body,
    color: inkColors.ink,
    position: "relative",
  },
  startingOverlay: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "24px",
    textAlign: "center",
  },
  startingMark: {
    fontFamily: fonts.display,
    fontSize: "16px",
    fontWeight: 700,
    color: brandColors.primary,
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "999px",
    border: `1px solid ${brandColors.primary}33`,
    background: `${brandColors.primary}12`,
    color: brandColors.primary,
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  startingHint: {
    fontSize: "12px",
    color: inkColors.ink3,
  },
  errorBanner: {
    margin: "12px",
    padding: "10px",
    background: "#FEF2F2",
    border: "1px solid #FCA5A5",
    borderRadius: "3px",
    fontSize: "12px",
    color: "#7F1D1D",
  },
  resetButton: {
    marginTop: "8px",
    fontSize: "11px",
    fontFamily: fonts.body,
    padding: "6px 12px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "3px",
    background: "#fff",
    color: inkColors.ink2,
    cursor: "pointer",
  },
});

// ---------------------------------------------------------------------------
// Helpers (pure functions extracted to utils/spineSnapshot.ts)
// ---------------------------------------------------------------------------
import {
  buildPersistedSpineSnapshotSection,
  deriveDocCaption,
  isEmptyFilter,
  normalisePersistedSpineSnapshot,
  normalisePersistedTimelineFilter,
  restorePersistedSpineSnapshotSection,
  shortHash,
  spineSectionsFromConfig,
  spineSectionsFromOutline,
  synthesizeProjectName,
  toPersistedSpineSnapshot,
} from "./utils/spineSnapshot";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VariantCShellProps {
  /** Logged-in user's identifier (typically email) for display in
   *  TopBarC. Null/empty hides the label. Sourced from App.tsx's
   *  session state — auth SSOT lives in App, not duplicated here. */
  userIdentifier?: string | null;
}

export const VariantCShell: React.FC<VariantCShellProps> = ({ userIdentifier }) => {
  const styles = useStyles();
  const [state, dispatch] = useReducer(variantCReducer, initialState);
  // Monotonic counter passed to the Drafts panel so it refreshes the moment a
  // run completes or a section finishes, instead of waiting for its 15s poll.
  const [draftsRefreshNonce, setDraftsRefreshNonce] = useState(0);
  const draftedProgressRef = useRef(-1);
  // Document session — fetched once on mount after Office.js resolves
  // the AsthrDocumentId. Gates the workspace fetch so the session's
  // workspace preference is available before pickInitialWorkspace runs.
  const [activeSelection, setActiveSelection] = useState<LoomFreshSelection | null>(null);
  const [docConfig, setDocConfig] = useState<DocTypeConfig | null>(null);
  // Which eCTD leaf this document IS. Seeded from the persisted session on
  // bootstrap and set when the writer picks one. `null` for a doctype filed as
  // ONE document and for every document created before the leaf split — both
  // must show the whole doctype, which is what `scopedCatalog` does with null.
  const [leafDocumentId, setLeafDocumentId] = useState<string | null>(null);
  // Diagnostic — fires when docConfig transitions from non-null → null,
  // which is the exact state that disables every Drafts panel Load
  // button. The stack trace pinpoints the offending caller (rotate-
  // project, reset, section cancel, or some unexpected path). Gated on
  // the prior-value ref so the initial null mount does not log.
  const docConfigPrevRef = useRef<DocTypeConfig | null>(null);
  useEffect(() => {
    if (docConfigPrevRef.current !== null && docConfig === null) {
      console.info("[VariantCShell] docConfig cleared", new Error("docConfig=null stack").stack);
    }
    docConfigPrevRef.current = docConfig;
  }, [docConfig]);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  // Persistent, dismissable warning raised when a Stop request could not be
  // confirmed against the backend (network/5xx after retries) — the run may
  // still be drafting server-side. Rendered at the shell root (below) so it
  // survives the post-run / idle transition that a stop triggers; the idle-only
  // ``startError`` banner would not.
  const [stopWarning, setStopWarning] = useState<string | null>(null);
  // Guards against concurrent or rapid-repeated "Load drafted document" calls.
  const loadDraftInFlightRef = useRef(false);
  const [loadDraftInFlight, setLoadDraftInFlight] = useState(false);
  // Live progress for the floating LoadProgressCard on the spine "Load doc"
  // button: which checkpoint is in flight (0 prepare → 1 download → 2 rebuild →
  // 3 restore → 4 spine; LOAD_STEP_DEFS.length = all done). ``-1`` = idle.
  const [loadDocStepIndex, setLoadDocStepIndex] = useState(-1);
  const [loadDocError, setLoadDocError] = useState<string | null>(null);
  const [loadDocDone, setLoadDocDone] = useState(false);
  // Live progress for the floating "Build on this document" (custom-import)
  // card: 0 outline → 1 create project → 2 build spine → 3 load sections;
  // CUSTOM_IMPORT_STEP_DEFS.length = all done. ``-1`` = idle (card hidden).
  const [customImportStep, setCustomImportStep] = useState(-1);
  const [customImportError, setCustomImportError] = useState<string | null>(null);
  const [customImportDone, setCustomImportDone] = useState(false);
  // Submission mode of the currently loaded run ("custom" for build-on-existing
  // -document, else "standard"/null). Drives the progress denominator: a custom
  // run counts against its own heading total, not the doctype template's.
  const [runSubmissionMode, setRunSubmissionMode] = useState<string | null>(null);
  // Custom submission "Draft more sections" — the template-tree LoomSelection
  // can't render CUSTOM-N headings, so custom runs use the dedicated
  // CustomDraftPicker (instruction-bearing sections only). True while it's open.
  const [customDraftPickerActive, setCustomDraftPickerActive] = useState(false);
  // Live token counts accumulated from SSE token_usage events during an
  // active run. Reset whenever the runId changes (new run or idle reset).
  // Passed to TabBar → TokenUsageDisplay to show real-time accumulation
  // rather than polling the quota API.
  const [liveTokens, setLiveTokens] = useState<{
    input: number;
    output: number;
    thinking: number;
  } | null>(null);
  // Inspector state — which section the writer drilled into. Cleared
  // on reset and on inspector close. Lives in the shell (not the
  // reducer) because it's UI navigation, not run lifecycle.
  const [focusedSectionId, setFocusedSectionId] = useState<string | null>(null);
  // Monotonic counter bumped each time the writer clicks a spine ``N⚖``
  // regulator badge — handed to <Inspector> so it opens the Review tab even
  // when the section is already focused (where a section-change reset wouldn't
  // fire). See LoomSpine.onOpenReview / Inspector.openReviewSignal.
  const [reviewOpenSignal, setReviewOpenSignal] = useState(0);
  // Monotonic counter bumped when a highlighted-span edit is approved. Handed
  // to <Inspector> → <DataGapsTabPanel> so the open-items list (missing-info +
  // regulator flags) re-fetches against the freshly-reconciled section without
  // the writer clicking Refresh. The server reconciles the ledger inline in
  // section_state.transition, so by the time the approve write-back resolves
  // the ledger is already current — a re-read is all that's needed.
  const [issuesRefreshSignal, setIssuesRefreshSignal] = useState(0);
  // Bumped when the resolve review queue may have changed — a resolve pass
  // produced a proposal (``resolve_proposal_ready`` / ``resolve_section_proposed``)
  // or the pass finished — so the "Proposals to review" band re-fetches its
  // pending list without the writer pressing Refresh.
  const [proposalsRefreshSignal, setProposalsRefreshSignal] = useState(0);
  // Bumped when a section's Action History may have changed (a lifecycle write,
  // a regen, a resolve section finishing, a run ending) so the inspector's
  // Action History tab re-fetches without the writer switching sections.
  const [actionsRefreshSignal, setActionsRefreshSignal] = useState(0);
  // Open-issue tally for the Issues tab badge. The shell fetches it proactively
  // (so the badge draws the writer toward the tab before they open it) and
  // SubmissionIssuesTab reports live updates back via onOpenCountChange after
  // in-tab dismiss/reopen — keeping the badge correct without a refetch. Null
  // hides the chip (no run, still loading, or fetch failed).
  const [issuesOpenCount, setIssuesOpenCount] = useState<number | null>(null);
  // Per-module OPEN regulatory-issue counts from the section_issues ledger — the
  // authoritative live source the spine's ⚖ badge must reflect. Bridged from the
  // same getRunIssues fetch that drives the tab badge (below); null until the
  // first fetch (spine then falls back to the SSE/DB regulatorFlagCount). Fixes
  // "spine doesn't show section N's open regulatory issues": the audit writes
  // kind="regulatory" rows the ISSUES tab reads, but the spine never read them.
  const [regulatoryOpenByModule, setRegulatoryOpenByModule] = useState<Record<string, number> | null>(null);
  // Bulk-resolve wizard: non-null holds the selected targets and opens the
  // takeover; null = closed. Populated from the Issues tab's "Resolve selected".
  const [resolveWizardTargets, setResolveWizardTargets] = useState<ResolveSelectionTarget[] | null>(null);
  // Cursor-derived section number for the chat rail's scope chip. Sourced by
  // a debounced + run-gated subscriber (below) so the chip reads §X.X.X while
  // the writer browses the doc post-draft, and resets to null whenever a new
  // run starts. ``handleSendChat`` still resolves the cursor section on
  // demand for the dispatch target — this state is only for the rail chip.
  const [cursorSectionNumber, setCursorSectionNumber] = useState<string | null>(null);
  // Commands button now opens the slash menu instead.
  // Library pick (Phase 7b) lifted from LoomFresh so the lifted
  // approval modal + the palette command + LoomFresh's picker all
  // read from one source of truth.
  const [selectedLibraryId, setSelectedLibraryId] = useState<string>("");
  // The active run's dynamic audit library (precedent-aware audit), fetched for
  // the Library Hub's Source↔Audit toggle. Null until fetched / when no run.
  const [hubAuditLibrary, setHubAuditLibrary] = useState<RunAuditLibrary | null>(null);
  // Live doctype pick (Phase 7.2 loose-coupled state). Hoisted here
  // alongside selectedLibraryId so the inventory-refresh effect below
  // can read it; the original declaration site (~810) was below the
  // refresh callback and produced a TDZ error when referenced.
  const [selectedDocCode, setSelectedDocCode] = useState<string | null>(null);
  // Phase 6.2 — DoctypePicker (Fluent Dialog modal, the canonical
  // doctype-selection surface) open-state.
  const [doctypePickerOpen, setDoctypePickerOpen] = useState<boolean>(false);
  const [tocOpen, setTocOpen] = useState<boolean>(false);
  // Section the writer is currently hovering in the TOC popup. Feeds the
  // rail's scope chip (via commandRailInspectorSectionNumber below) so the
  // chip previews the hovered section ahead of a click; cleared whenever
  // the popup closes so the chip falls back to the cursor section.
  const [tocHoveredSectionNumber, setTocHoveredSectionNumber] = useState<string | null>(null);
  const [showKillConfirmation, setShowKillConfirmation] = useState<boolean>(false);
  const [killConfirming, setKillConfirming] = useState<boolean>(false);
  const [imageSelected, setImageSelected] = useState<boolean>(false);
  // Mirror the writer's active doctype selection into libraryDoctypeStore
  // for the active library. (Source priority: selectedDocCode > activeSelection.docCode)
  useEffect(() => {
    const docCode = selectedDocCode ?? activeSelection?.docCode;
    if (!selectedLibraryId || !docCode) return;
    if (!isTargetDoctype(docCode)) return;
    if (getLibraryDoctype(selectedLibraryId) === docCode) return;
    setLibraryDoctype(selectedLibraryId, docCode);
  }, [selectedLibraryId, selectedDocCode, activeSelection?.docCode]);

  // Opens the Library Hub for the currently-selected library.
  // Logout handler (palette command). Best-effort: the existing
  // api/client.logout() POSTs to the backend and clears session
  // state. After it resolves, force a full reload — simplest correct
  // way to return the user to the login screen regardless of which
  // Variant C surface they triggered logout from. Declared here
  // (above paletteCommands which captures it via useMemo) so JS
  // hoisting doesn't trip.
  const handleLogout = useCallback(async () => {
    // Pause first, then logout. ``logout()`` clears the session token
    // client-side as soon as the POST resolves, and the beforeunload
    // handler in useSessionBootstrap also fires a keepalive pause —
    // but by that point the token is gone and the backend returns
    // 401, leaving the run stuck in "running" on the server. On
    // re-login the run-restore path then doesn't know to re-attach
    // SSE while loadRunIntoLiveDoc fights for the Office.js context,
    // freezing the UI. Fire the pause synchronously here so it goes
    // out with a valid token.
    const activeRunId = runIdRef.current;
    if (activeRunId && isActiveRun(runStateRef.current)) {
      keepalivePauseRun(activeRunId);
    }
    try {
      await logout();
      // No provider-side sign-out to undo: the Microsoft/Google flows run in a
      // short-lived Office dialog whose MSAL/GIS cache is discarded when the
      // dialog closes, and each sign-in re-prompts with the account picker
      // (prompt=select_account). Clearing our own session (above) is the act.
    } catch (err) {
      console.warn("[VariantCShell] logout request failed (continuing):", err);
    } finally {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    }
  }, []);
  // Coalesced: the 1.8 s poll and selection-change events call this
  // continuously; while one lookup is in flight, further calls share it
  // instead of queueing more. Without this the lookups piled up in
  // queueWordRead whenever one outlasted the poll interval on a long
  // document, and every read behind them (chip clicks, the empty-doc check)
  // waited longer the longer the session ran (observed 0.7 s → >10 s).
  const refreshCursorSectionNumber = useMemo(
    () =>
      coalesceInFlight(async () => {
        const runState = runStateRef.current;
        if (isActiveRun(runState)) {
          return;
        }
        // Light-probe miss/throw handling (bookmark-less docs, hosts without
        // Range.getBookmarks) lives in the shared helper so this chip and the
        // citations popover resolve sections identically. Reads stay serialized
        // through queueWordRead so they can't race the empty-doc probes.
        const num = await detectCursorSectionNumberWithFallback(queueWordRead);
        setCursorSectionNumber(num);
      }),
    [],
  );

  const handleScopeChipClick = useCallback(() => {
    // Best-effort refresh before opening TOC so the left chip label and
    // navigator highlight track the writer's latest cursor location.
    void refreshCursorSectionNumber();
    setTocOpen(true);
  }, [refreshCursorSectionNumber]);

  // The TOC popup can close without a hover ever ending on a row (Escape,
  // backdrop click, or navigating away) — reset the hover preview whenever
  // it closes so a stale hovered section can't linger on the scope chip.
  useEffect(() => {
    if (!tocOpen) {
      setTocHoveredSectionNumber(null);
    }
  }, [tocOpen]);
  // Backend runtime config — sandbox flag + which doc-types have
  // fixtures. Source of truth for the TopBar mode pill + LoomFresh's
  // doc availability gate.
  const {
    appConfig,
    modeToggling,
    modeToggleError,
    planModeToggling,
    planModeToggleError,
    refreshAppConfig,
    handleModeToggle,
    handlePlanModeToggle,
    dismissModeToggleError,
    dismissPlanModeToggleError,
  } = useAppConfig();

  // ----- Word-write side-effect path (SSE → Word document) -------------
  const { handleProgressEvent, scaffoldedRef, insertionModeRef, agentLastWriteAtRef, enqueueWordWrite } =
    useWordWriteQueue(docConfig);

  // -- Intent-routed chat: live drafting activity in the chat thread ----
  // Set when a chat message triggered a regen / ad-hoc draft; updated
  // from raw run SSE below; rendered through TimelineView's existing
  // streamingStatus / streamingDraft transient bubble. Ref mirror so
  // the SSE callback reads current state without re-subscribing.
  interface ChatRunActivityState {
    moduleId: string;
    kind: "regen" | "adhoc";
    instruction: string;
    phase: "starting" | "retrieving" | "drafting";
    draftSoFar: string;
    startedAt: number;
    // Live retrieval subline (which items are resolving, resolved/total,
    // cascade level) — the SAME string the spine shows on its drafting row,
    // built from the shared ``buildRetrievalSubline`` helper. Lets the chat
    // regen row stream retrieval detail instead of a coarse phase word.
    detail?: string;
    retrieval?: RetrievalProgress | null;
  }
  const [chatRunActivity, setChatRunActivity] = useState<ChatRunActivityState | null>(null);
  const chatRunActivityRef = useRef<ChatRunActivityState | null>(null);
  const updateChatRunActivity = useCallback((next: ChatRunActivityState | null) => {
    chatRunActivityRef.current = next;
    setChatRunActivity(next);
  }, []);

  // ``adhoc_draft_ready`` capture — the offer function lives on a ref
  // because the proposal flow hook is instantiated further down (it
  // needs resolveSectionLabel, which closes over ``sections``), while
  // the SSE wrapper below is declared before either exists. Same
  // pattern as chatRunActivityRef.
  const adhocProposalOfferRef = useRef<
    ((event: { module_id?: string; run_id?: string; section_response?: unknown }) => void) | null
  >(null);
  // Filled from the resolve flow (declared far below) so the progress handler —
  // declared above it — can advance the resolve phase machine on each event.
  const resolveFlowOnEventRef = useRef<((event: ProgressEvent) => void) | null>(null);
  // The bulk resolve pass in flight (set on the wizard's Start) and the
  // completion handler (declared far below). On a clean finish the shell opens
  // the Issues tab — where its proposals land — and posts a one-line summary.
  const resolvePassRef = useRef<{ targets: ResolvePassTarget[]; startedAt: number } | null>(null);
  const onResolvePassCompleteRef = useRef<((pass: { targets: ResolvePassTarget[]; startedAt: number }) => void) | null>(
    null,
  );

  // Post-approve auto-save hook — filled by an effect next to
  // handleSave (declared far below); called by the proposal decision
  // handlers when a chat-driven edit is accepted.
  const autoSaveRef = useRef<(() => Promise<void>) | null>(null);
  // Section-scoped auto-save (by module id) — set alongside autoSaveRef; called
  // by the proposal decision handlers so an accepted edit persists just its
  // own section instead of the whole document.
  const autoSaveModuleRef = useRef<((moduleId: string | null) => Promise<void>) | null>(null);

  // Terminal draft-launch hook — filled by an effect next to launchDraft
  // (declared below, after startCustomFlow). The doctype/library orchestration
  // callbacks (tryAdvanceDraft, handleDoctypePick, advanceDraftWithLibrary) are
  // declared above launchDraft, so they call it through this ref to avoid a
  // temporal-dead-zone reference in their dependency arrays.
  const launchDraftRef = useRef<((selection: LoomFreshSelection) => void) | null>(null);

  // Per-section "loading into Word…" tracker shown on the spine row after a
  // redraft, cleared only on a CONFIRMED write (sectionsWritten > 0) and flipped
  // to a failed state when nothing lands. Shell-owned + passed to LoomSpine as a
  // prop (NOT on SpineSection) so it survives the SSE section_state_changed
  // subline-wipe and spine rebuilds. Keyed by module_id — the same id the regen
  // SSE path uses for its spine updates.
  const [liveDocLoadBySection, setLiveDocLoadBySection] = useState<LiveDocLoadMap>(() => new Map());

  // Per-section LIVE agent activity feed (what tool Asthra is calling right now),
  // shell-owned like liveDocLoadBySection so it survives the section_state_changed
  // subline wipe + spine rebuilds. Fed by every AsthraAgent/tool event now
  // forwarded on the run stream (drafting, regen AND bulk resolve).
  const [agentActivityBySection, setAgentActivityBySection] = useState<AgentActivityMap>(() => new Map());

  // Best-effort re-projection of one module's section(s) from the server SSOT
  // into the live Word doc, serialised through the single Word-write queue so it
  // can't interleave with a Load / audit re-projection, and deferred while a
  // load is still settling (same guard reviewStore.reprojectSection uses — a
  // delete-then-reinsert during the settle window can blank a region). Drives the
  // spine tracker: ``loading`` up front (persists across the settle defer), then
  // ``clear`` on a confirmed write or ``failed`` when nothing lands. One bounded
  // retry covers a doc that was still settling on the first attempt. A silent
  // no-op (sectionsWritten 0) means no matching heading — the doc likely needs a
  // full Load, which the failed subline tells the writer.
  const reprojectSectionIntoLiveDoc = useCallback(
    (runId: string | null, moduleId: string) => {
      if (!runId) return;
      setLiveDocLoadBySection((m) => setLiveDocLoading(m, moduleId, Date.now()));
      const attempt = () => enqueueWordWrite(() => reinsertModuleIntoLiveDoc(runId, moduleId));
      const run = async () => {
        try {
          let { sectionsWritten } = await attempt();
          if (outcomeFromSectionsWritten(sectionsWritten) === "failed") {
            // The doc may still be settling (styles / native numbering
            // re-attaching) so the heading scan missed. Give it one retry.
            await new Promise((resolve) => setTimeout(resolve, 700));
            ({ sectionsWritten } = await attempt());
          }
          if (outcomeFromSectionsWritten(sectionsWritten) === "success") {
            setLiveDocLoadBySection((m) => clearLiveDocLoad(m, moduleId));
          } else {
            console.warn(
              `[VariantCShell] redraft re-projection wrote 0 sections for ${moduleId} — heading not found in the live doc (needs a full Load?)`,
            );
            setLiveDocLoadBySection((m) => setLiveDocFailed(m, moduleId, Date.now()));
          }
        } catch (err) {
          console.warn("[VariantCShell] redraft live re-projection failed (non-fatal):", err);
          setLiveDocLoadBySection((m) => setLiveDocFailed(m, moduleId, Date.now()));
        }
      };
      if (isLiveDocLoading()) {
        deferUntilLiveDocSettled(`redraft:${runId}:${moduleId}`, () => void run());
      } else {
        void run();
      }
    },
    [enqueueWordWrite],
  );

  // Wraps handleProgressEvent to also accumulate token_usage fields from
  // each SSE event into ``liveTokens``. Passed to useVariantCRun in place
  // of the raw handler so the TabBar can show real-time token counts.
  const handleProgressEventWithTokens = useCallback(
    (event: Parameters<typeof handleProgressEvent>[0]) => {
      handleProgressEvent(event);
      // Live per-section agent activity feed (tool calls, retrieval steps,
      // rounds…) for the spine. Same event stream for drafting, regen and
      // bulk resolve, so all three now show what the agent is doing live.
      setAgentActivityBySection((m) => applyActivityEvent(m, event));
      // Advance the resolve pass phase machine (progress / paused / resumed).
      resolveFlowOnEventRef.current?.(event);
      // Resolve pass lifecycle for the post-pass handoff: a Stop (cancel)
      // abandons it; a Pause keeps it (Resume re-posts the rest and the same
      // pass finishes later); a clean pipeline_complete completes it.
      if (event.event_type === "resolve_stopped") {
        const p = (event.section_response ?? {}) as Record<string, unknown>;
        if (p.mode !== "pause") resolvePassRef.current = null;
      }
      if (event.event_type === "pipeline_complete" && resolvePassRef.current) {
        const pass = resolvePassRef.current;
        resolvePassRef.current = null;
        onResolvePassCompleteRef.current?.(pass);
      }
      // A completed run (including a bulk resolve-issues run) or a completed
      // single-section regenerate may have changed the issue ledger — refresh
      // the Issues tab list + open-count badge. ``regen_complete`` is emitted
      // separately from ``pipeline_complete`` (it's deliberately non-terminal so
      // the SSE stream stays open) and the regen reseed now resolves markers the
      // redraft removed and opens new ones, so it must trigger the same refresh.
      if (event.event_type === "pipeline_complete" || event.event_type === "regen_complete") {
        setIssuesRefreshSignal((n) => n + 1);
      }
      // A finished regulatory audit added/cleared regulator flags on a section —
      // refresh the run-wide Issues tab so they surface without a manual Save.
      if (event.event_type === "audit_complete") {
        setIssuesRefreshSignal((n) => n + 1);
      }
      // A resolve pass finished a section (or produced a proposal): the review
      // queue changed. Refresh the "Proposals to review" band + Action History —
      // NOTHING is written into Word here. The change lands in the document only
      // when the writer Approves an individual proposal (see useProposalReviewFlow).
      if (
        (event.event_type === "resolve_section_proposed" || event.event_type === "resolve_proposal_ready") &&
        event.module_id
      ) {
        setProposalsRefreshSignal((n) => n + 1);
        setActionsRefreshSignal((n) => n + 1);
      }
      if (event.event_type === "pipeline_complete") {
        setProposalsRefreshSignal((n) => n + 1);
      }
      // Action History for this section may have changed — re-fetch it if open.
      if (
        event.event_type === "section_state_changed" ||
        event.event_type === "regen_complete" ||
        event.event_type === "pipeline_complete"
      ) {
        setActionsRefreshSignal((n) => n + 1);
      }
      // Inspector "Regenerate this section" (Path A) auto-load: the regen
      // committed the section's SSOT server-side, but the streaming
      // ``section_ready`` write is dropped by the batch-mode guard after a run
      // completes (useWordWriteQueue). Re-project just this module from the
      // server into the live doc so the redraft appears without a manual Load.
      // ``regen_complete`` carries ``module_id = regen_target``.
      if (REDRAFT_AUTO_LOAD && event.event_type === "regen_complete" && event.module_id) {
        reprojectSectionIntoLiveDoc(runIdRef.current, event.module_id);
      }
      // Ad-hoc draft proposal ready → offer it to the Approve/Reject
      // flow (transient card in the thread; the durable EventLog entry
      // lands at decision time — see useAdhocProposalFlow).
      if (event.event_type === "adhoc_draft_ready") {
        adhocProposalOfferRef.current?.(event);
      }
      // Feed the chat thread's live drafting bubble while a
      // chat-initiated regen / ad-hoc draft is running on this module.
      const activity = chatRunActivityRef.current;
      if (activity && event.module_id === activity.moduleId) {
        switch (event.event_type) {
          case "text_chunk":
            // Chunks are incremental (~50 chars) — accumulate.
            updateChatRunActivity({
              ...activity,
              phase: "drafting",
              draftSoFar: activity.draftSoFar + (event.message || ""),
            });
            break;
          case "retrieval_plan":
          case "retrieval_plan_retry":
          case "retrieval_progress":
          case "retrieval_item_added":
          case "retrieval_item_resolved":
          case "retrieval_item_advance":
          case "draft_starting": {
            // Capture the live retrieval subline (same string the spine
            // shows) so the chat regen row streams which items are being
            // fetched and how many are resolved/written-off — not just a
            // coarse "gathering data…". ``draft_starting`` marks the
            // hand-off to writing.
            const r = buildRetrievalSubline(
              event.event_type,
              event.section_response as Record<string, unknown> | undefined,
            );
            updateChatRunActivity({
              ...activity,
              phase: event.event_type === "draft_starting" ? "drafting" : "retrieving",
              detail: r?.subline ?? activity.detail,
              retrieval: r ? r.retrieval : activity.retrieval,
            });
            break;
          }
          case "section_ready":
          case "adhoc_draft_ready":
          case "regen_complete":
          case "adhoc_draft_complete":
          case "resolve_section_proposed":
          case "resolve_section_failed":
          case "error":
          case "cancelled":
            // Terminal for the activity — the durable EventLog entry
            // (draft / adhoc_draft projection) replaces the bubble.
            updateChatRunActivity(null);
            break;
          default:
            break;
        }
      }
      if (event.token_usage) {
        const usage = event.token_usage as Record<string, number>;
        const input =
          (usage.input_tokens ?? 0) + (usage.cache_creation_input_tokens ?? 0) + (usage.cache_read_input_tokens ?? 0);
        const output = usage.output_tokens ?? 0;
        const thinking = usage.thinking_tokens ?? 0;
        if (input || output || thinking) {
          setLiveTokens((prev) => ({
            input: (prev?.input ?? 0) + input,
            output: (prev?.output ?? 0) + output,
            thinking: (prev?.thinking ?? 0) + thinking,
          }));
        }
      }
    },
    [handleProgressEvent, updateChatRunActivity, reprojectSectionIntoLiveDoc],
  );

  // Reset accumulated live-token counts whenever the runId rotates
  // (RUN_STARTED with a new id) or clears (RESET_TO_IDLE → null).
  // biome-ignore lint/correctness/useExhaustiveDependencies: state.runId is an intentional trigger dep; setLiveTokens is a stable setter
  useEffect(() => {
    setLiveTokens(null);
    setAgentActivityBySection((m) => clearAllActivity(m));
  }, [state.runId]);

  // Wall-clock timestamp the current run started — drives the
  // command-rail stats string ("X / Y · 2m 14s"). Reset on
  // RESET_TO_IDLE; set on RUN_STARTED dispatch path.
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  // Percent complete while run-restore is repainting sections into Word.
  // Null hides the inline indicator next to the stop control.
  const [restoreHydrationProgressPct, setRestoreHydrationProgressPct] = useState<number | null>(null);

  // Plan-review state (owned here so useSessionBootstrap can seed planId/planData from hydrate).
  const [planData, setPlanData] = useState<PlanData | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);

  // Surfaced when the empty-doc detector observes a "had content →
  // empty" transition and there's a project in flight to abandon.
  // Drives the NewProjectConfirmModal render.
  const [newProjectConfirmOpen, setNewProjectConfirmOpen] = useState(false);
  // Surfaced when the writer starts a brand-new draft while the current document
  // already holds a (non-actively-drafting) run. Holds the pending selection so
  // the confirm handler can save the current doc, reset to idle, then launch the
  // new submission's section picker. Drives the SaveAndStartFreshModal render.
  const [freshDraftPending, setFreshDraftPending] = useState<LoomFreshSelection | null>(null);
  const [freshDraftSaving, setFreshDraftSaving] = useState(false);
  // Surfaced (feature-flagged) on login when a non-empty document is opened
  // with no associated run — offers the "build on this document" flow. One-shot
  // per session; the ref latches so a re-render can't re-open it after dismiss.
  const [importExistingDocOpen, setImportExistingDocOpen] = useState(false);
  const importDetectionDoneRef = useRef(false);
  // Latched by the custom-import detector when the OPEN document has content but
  // no run this user can access (a foreign AsthrDocumentId → another user's runs).
  // Consumed by the commit handlers (rotateForeignDocIfNeeded) to rotate the
  // doc_id before startRun so the backend tenancy guard
  // (server/routes/pipeline.py:1847) sees a brand-new document.
  const foreignDocDetectedRef = useRef(false);
  const emptyDocWatcherRef = useRef<EmptyDocWatcherHandle | null>(null);
  // Ref to the CommandRail so clicking ``/command`` links in chat
  // bubbles can programmatically prefill and focus the input.
  const commandRailRef = useRef<CommandRailHandle>(null);
  // Stable callback — safe to declare here because it only closes
  // over the ref (which has a stable identity throughout the
  // component lifetime).
  const handleCommandLinkClick = useCallback((cmd: string) => {
    commandRailRef.current?.prefillInput(cmd);
  }, []);
  const handleReply = useCallback((message: ChatMessage) => {
    commandRailRef.current?.setReplyTarget(message);
  }, []);

  // The LoomSelection commit — captured so actuallyStartRun can pass
  // module_ids + the shell can thread the writer's strategy preview
  // choice forward. Null before the first commit of this run, or
  // after RESET_TO_IDLE clears run-scoped state.
  const [sectionCommit, setSectionCommit] = useState<LoomSelectionCommit | null>(null);

  // Refs for beforeunload handlers — readable without stale closures.
  const runIdRef = React.useRef<string | null>(null);
  const runStateRef = React.useRef(state.state);
  const [runSubscriptionNonce, setRunSubscriptionNonce] = useState(0);
  const [runSubscriptionStartMode, setRunSubscriptionStartMode] = useState<"history" | "latest">("history");
  // True when the restored run's status (from the REST /runs/{id} fetch
  // in useRunControl) is terminal. ``useVariantCRun`` reads this to skip
  // its SSE subscribe effect — Redis stream is evicted ~15 min after
  // completion and a subscribe attempt 404s.
  const [terminalRunStatus, setTerminalRunStatus] = useState(false);
  // True while useRunControl is projecting the backend snapshot into
  // reducer + spine + Word state. useVariantCRun uses this as an SSE gate
  // so restore cannot race with live stream updates.
  const [restoreInFlight, setRestoreInFlight] = useState(false);

  // ----- Document session bootstrap + beforeunload flush -----------------
  const {
    documentSessionId,
    setDocumentSessionId,
    sessionLoading,
    sessionWorkspaceRef,
    restoredIdentity,
    bootstrapRunId,
    bootstrapDismissedRunId,
    setPersistedSpineSnapshot,
    persistedChatMessages,
    setPersistedChatMessages,
    persistedChatHasMore,
    setPersistedChatHasMore,
    restoredConversationId,
    setRestoredConversationId,
    restoredInsertionMode,
    knownHadContent,
    setKnownHadContent,
    restoredInventorySnapshot,
    restoredInventoryDoctype,
  } = useSessionBootstrap({
    setSelectedLibraryId,
    setSelectedDocCode,
    setActiveSelection,
    setDocConfig,
    setLeafDocumentId,
    setSectionCommit,
    setPlanId,
    setPlanData,
    setFocusedSectionId,
    dispatch,
    runIdRef,
    runStateRef,
    insertionModeRef,
  });

  useEffect(() => {
    if (sessionLoading) return;
    const mode = restoredInsertionMode ?? "batch";
    insertionModeRef.current = mode;
    void saveWordInsertionMode(mode);
    const docId = getCachedDocId();
    if (!docId) return;
    scheduleSave(docId, { insertion_mode: mode });
  }, [sessionLoading, restoredInsertionMode, insertionModeRef]);

  // ----- Paragraph sync engine -------------------------------------------
  // The real-time block-level sync engine is GATED on the document having been
  // marked "ready" (the pipeline draft was loaded into Word for this run — see
  // AsthrSyncReady / markSyncReady). Before the first load the body holds only
  // placeholder text ("Asthra is drafting…") that must NEVER be pushed to
  // DocService over the real server-side draft. We start it imperatively (via a
  // ref) so the load handler can kick it off the instant the draft lands — while
  // still paused — preserving the seed→pause→resume-rebaseline ordering.
  const syncCleanupRef = useRef<null | (() => void)>(null);
  const syncStartedRunRef = useRef<string | null>(null);

  const startSyncEngineForCurrentRun = useCallback(() => {
    const runId = state.runId ?? null;
    if (!runId) return;
    if (syncStartedRunRef.current === runId && syncCleanupRef.current) return; // already running
    syncCleanupRef.current?.();
    // The watcher only knows the cursor's dotted section number; hand it the
    // live spine so a scoped auto-save can fetch ONE module by id instead of
    // the whole document. Read via sectionsRef so this callback isn't rebuilt
    // on every spine update.
    syncCleanupRef.current = initSyncEngine(
      () => state.runId ?? null,
      (sectionNumber) => sectionsRef.current.find((s) => s.number === sectionNumber)?.id ?? null,
    );
    syncStartedRunRef.current = runId;
    console.log(`[sync-gate] sync engine started for runId=${runId}`);
  }, [state.runId]);

  const stopSyncEngine = useCallback(() => {
    if (syncCleanupRef.current) {
      syncCleanupRef.current();
      syncCleanupRef.current = null;
      console.log("[sync-gate] sync engine stopped");
    }
    syncStartedRunRef.current = null;
  }, []);

  // On mount / runId change, restore sync only if the document was already marked
  // ready for this run (e.g. reopening an already-loaded draft). A fresh run whose
  // draft hasn't been loaded yet stays gated off until the load handler starts it.
  useEffect(() => {
    if (sessionLoading) return;
    const runId = state.runId ?? null;
    if (!runId) {
      stopSyncEngine();
      return;
    }
    let cancelled = false;
    void Promise.all([isSyncReady(runId), getDraftPointer()]).then(([ready, pointer]) => {
      if (cancelled) return;
      // draft_pointer === "server" means a draft / "Draft more sections" run is
      // in progress and owns the document server-side. Keep sync off even across
      // a task-pane reload mid-run — until handleLoadDraftedDocument flips the
      // pointer back to "live" — so the add-in never pushes the Word body over
      // the server's concurrent writes (the DLL-scramble root cause).
      if (ready && pointer !== "server") startSyncEngineForCurrentRun();
      else stopSyncEngine();
    });
    return () => {
      cancelled = true;
    };
  }, [sessionLoading, state.runId, startSyncEngineForCurrentRun, stopSyncEngine]);

  // Stop the engine on unmount.
  useEffect(() => () => stopSyncEngine(), [stopSyncEngine]);

  // ----- Persist UI state across reload ----------------------------------
  // Spine + chat persistence: wire each piece of writer-visible UI
  // state to ``scheduleSave`` so a refresh / reopen rehydrates it from
  // MongoDB. Each effect is gated on ``sessionLoading === false`` so
  // the bootstrap's restored values do not immediately echo back to
  // the backend (which would be a no-op write but still wasteful and
  // would also overwrite a concurrent same-doc edit from another tab
  // before that tab's hydrate had a chance to merge).

  // Persist the timeline filter on every change after bootstrap.
  useEffect(() => {
    if (sessionLoading) return;
    const docId = getCachedDocId();
    if (!docId) return;
    scheduleSave(docId, {
      timeline_filter: state.timelineFilter as unknown as Record<string, unknown>,
    });
  }, [sessionLoading, state.timelineFilter]);

  // Persist the focused section ID on every change after bootstrap.
  // ``null`` is a meaningful clear (writer dismissed the inspector) so
  // we send it explicitly — the patch builder translates ``null`` into
  // a backend ``clear_fields`` directive.
  useEffect(() => {
    if (sessionLoading) return;
    const docId = getCachedDocId();
    if (!docId) return;
    scheduleSave(docId, { focused_section_id: focusedSectionId });
  }, [sessionLoading, focusedSectionId]);

  // ----- Spec 23 W1 + W2: workspace / library state --------------------
  const {
    workspaces,
    selectedWorkspaceId,
    libraries,
    workspaceFetchError,
    libraryInventory,
    libraryInventoryLoading,
    libraryResolveEvent,
    refreshLibraries,
    refreshLibraryInventory,
    invalidateResolveGuard,
    removeDocumentFromInventory,
    handleSelectedWorkspaceIdChange,
    createAndSelectWorkspace,
  } = useWorkspaceLibrary({
    sessionLoading,
    sessionWorkspaceRef,
    selectedLibraryId,
    setSelectedLibraryId,
    selectedDocCode,
    activeDocCode: activeSelection?.docCode,
    runId: state.runId,
    dispatch,
    userIdentifier,
    restoredInventorySnapshot,
    restoredInventoryDoctype,
  });

  // -- Spine ⇄ Timeline view switch (Spec 17 P4) -------------------------
  // Regen flow — handled by useRegenFlow below (after resolveSectionLabel).

  // -- Spine ⇄ Timeline view switch (Spec 17 P4) -------------------------
  // Single dispatch; reducer is idempotent (same-mode → same object,
  // React skips re-render). See SpineTimelineToggle for the UX.
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    dispatch({ type: "VIEW_MODE_SET", mode });
    scheduleSave(getCachedDocId(), { view_mode: mode });
  }, []);

  // Timeline reads the log newest-first, narrowed by the active
  // filter. Memoised on (log, filter) — the reducer hands fresh refs
  // on either change, so reference-equality tracks semantic change.
  // entryMatchesFilter returns true for every entry when the filter
  // is empty (its "all dims null" semantics), so this single
  // expression covers both filtered and unfiltered cases.
  const timelineEntries = useMemo(
    () => entriesNewestFirst(state.eventLog).filter((e) => entryMatchesFilter(e, state.timelineFilter)),
    [state.eventLog, state.timelineFilter],
  );

  // Variant D Phase 3 — per-document chat history. Seeded from the
  // backend hydrate payload for this Word document and incrementally
  // synced back to the backend as EventLog entries arrive.
  // Phase: run-scoped chat — passing ``state.runId`` makes the view
  // filter to "messages for this run + session-level history". When the
  // run changes (Drafts panel load, RUN_RESTORED, fresh RUN_STARTED),
  // the hook re-seeds from ``persistedChatMessages`` which the shell
  // refetches via ``setPersistedChatMessages`` in ``handleRunLoaded``.
  // Pagination: the seed is the newest CHAT_PAGE_SIZE messages;
  // ``loadOlder`` prepends earlier pages as the timeline scrolls up.
  const {
    messages: chatHistoryMessages,
    hasMoreOlder: chatHasMoreOlder,
    isLoadingOlder: chatIsLoadingOlder,
    loadOlder: chatLoadOlder,
  } = useChatHistoryView(
    documentSessionId,
    state.eventLog.entries,
    persistedChatMessages,
    state.runId ?? null,
    persistedChatHasMore,
  );

  // Spec 23 P7 — run-level counter aggregate drives the event
  // legend strip above the spine. Memoised on the counters map ref
  // Whether any filter dimension is currently active. Drives the
  // empty-state hints (shows "M of N events" vs "N events").
  // (Previously drove SpineTimelineToggle's context strip, retired
  // in Spec 29 Phase 1 when the toggle was replaced by the TabBar.)
  const filterIsActive = useMemo(() => !isEmptyFilter(state.timelineFilter), [state.timelineFilter]);

  // -- Initial section list for the run ------------------------------------
  const initialSections = useMemo<SpineSection[]>(() => {
    if (!docConfig) return [];
    return spineSectionsFromConfig(docConfig);
  }, [docConfig]);

  // -- SSE wiring ----------------------------------------------------------
  // A manual section audit ("Run review") can run on an idle/completed run,
  // which otherwise holds no live SSE stream. Track whether any audit is in
  // flight for this run so useVariantCRun keeps a stream open for it — that is
  // how the audit's live step/counter narration reaches the panel.
  const reviewAuditActive = React.useSyncExternalStore(subscribeAuditActivity, () =>
    state.runId ? isAuditActiveForRun(state.runId) : false,
  );
  // The automatic post-draft audit runs only once the audit library is ready,
  // and a per-run catch-up sweep audits the sections that finished drafting while
  // it was still building — those audits routinely fire AFTER the run is
  // terminal. Keep the audit-only SSE stream open while the library is building
  // (and briefly after it becomes ready) so their live events reach this client;
  // otherwise the loaded doc + spine would only update on a manual reload. Once
  // the sweep's first audit_started arrives, reviewStore's own audit-active
  // signal keeps the stream open, so this only has to bridge the gap.
  const [auditLibPending, setAuditLibPending] = useState(false);
  useEffect(() => {
    const runId = state.runId ?? null;
    if (!runId) {
      setAuditLibPending(false);
      return undefined;
    }
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    const stopPoll = () => {
      if (poll) {
        clearInterval(poll);
        poll = null;
      }
    };
    const tick = async () => {
      let status: string | undefined;
      try {
        const lib = await getRunAuditLibrary(runId);
        status = lib?.audit_library?.status;
      } catch {
        status = undefined;
      }
      if (cancelled) return;
      // ``preparing`` covers the brief pre-ref window at the very start of a
      // custom submission's temp-source bootstrap, before the builder stamps
      // ``acquiring`` — so the audit SSE stream opens in time to carry the
      // temp-library build events to reviewStore's build-phase store.
      const building = status === "acquiring" || status === "processing" || status === "preparing";
      if (building) {
        setAuditLibPending(true);
        return;
      }
      // Settled (ready / empty / failed / none) or no library — stop polling. If
      // it just became ready, hold a grace window so the catch-up sweep's first
      // audit_started can attach before the stream closes.
      stopPoll();
      if (status === "ready") {
        setAuditLibPending(true);
        graceTimer = setTimeout(() => {
          if (!cancelled) setAuditLibPending(false);
        }, 90_000);
      } else {
        setAuditLibPending(false);
      }
    };
    void tick();
    poll = setInterval(() => void tick(), 8000);
    return () => {
      cancelled = true;
      stopPoll();
      if (graceTimer) clearTimeout(graceTimer);
    };
  }, [state.runId]);
  const auditActive = reviewAuditActive || auditLibPending;
  // -- Word-write side effects -------------------------------------------
  // Fired before the spine's own translation for every SSE event the
  // run emits. Two responsibilities:
  //   1. gate_start (first one only) — scaffold the Word doc with the
  //      doc-type's heading skeleton so the writer sees structure
  //      immediately, before any draft text arrives. Section mode only;
  //      batch mode lets the backend assemble the final docx.
  //   2. section_ready — write that section's draft_content into the
  const {
    sections,
    activeSectionId,
    hydrateSections,
    identity,
    streamError,
    initStatus,
    setSectionStatus,
    setSectionInstruction,
    seedStreamCursor,
    clearSectionActivity,
  } = useVariantCRun({
    runId: state.runId,
    initialSections,
    dispatch,
    onProgressEvent: handleProgressEventWithTokens,
    subscriptionNonce: runSubscriptionNonce,
    subscriptionStartMode: runSubscriptionStartMode,
    runState: state.state,
    terminalRunStatus,
    auditActive,
    restoreInFlight,
    initialIdentity: restoredIdentity,
  });

  // Nudge the Drafts panel to refresh the instant a run completes or a section
  // finishes drafting, so a fresh draft shows without waiting for the poll.
  useEffect(() => {
    const draftedNow = sections.filter((s) => s.status === "drafted").length;
    const completed = state.state === "post-run";
    if (draftedNow !== draftedProgressRef.current || completed) {
      draftedProgressRef.current = draftedNow;
      setDraftsRefreshNonce((n) => n + 1);
    }
    // The run_bundles row the panel reads is written around pipeline_complete,
    // so a completed run may not be indexed yet on this first bump — a second
    // bump ~1.5s later beats that write race (the 15s poll is the backstop).
    if (completed) {
      const t = setTimeout(() => setDraftsRefreshNonce((n) => n + 1), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state.state, sections]);

  // -- Payment / checkout (native Dodo checkout for custom workspaces) ----
  // Drafting is free unless the active workspace is billable. The demo
  // workspace is exempt server-side, so its sample libraries never see a price;
  // a custom workspace is billable, so its custom libraries do. Prices are taken
  // from /doc-types (live doc_pricing.json); the charge is computed server-side.
  const [orderRequest, setOrderRequest] = useState<Order | null>(null);
  const pendingCartRef = useRef<{ items: CheckoutItem[]; run: CheckoutRun } | null>(null);
  const finalizePaidRunStartRef = useRef<((s: StartedPaidDraft) => Promise<void>) | null>(null);
  const [billingOpen, setBillingOpen] = useState(false);
  // Whether the active workspace is the demo (billing-exempt) one. Custom
  // libraries may only be created in a custom workspace, so a confirmed exempt
  // workspace blocks library creation and points the writer at making one.
  // Only ``exempt_workspace`` blocks — any uncertainty allows, so a lookup blip
  // never traps the writer.
  const [activeWorkspaceExempt, setActiveWorkspaceExempt] = useState(false);

  const checkout = useCheckout({
    onStarted: (started) => {
      setOrderRequest(null);
      pendingCartRef.current = null;
      void finalizePaidRunStartRef.current?.(started);
    },
  });

  const paymentGate = useCallback(
    async ({
      selection,
      moduleIds,
      continueRunId,
    }: {
      selection: LoomFreshSelection;
      moduleIds: string[] | null;
      continueRunId: string | null;
    }): Promise<boolean> => {
      // "Draft more" and anything without a config/workspace is never gated.
      if (continueRunId) return true;
      const config = docConfig;
      const workspaceId = selection.workspaceId;
      if (!config || !workspaceId) return true;

      // Demo / exempt workspace → free. Fails open, so a lookup blip never
      // fronts a paywall on a free run.
      const billing = await getWorkspaceBilling(workspaceId);
      if (!billing.billing_required) return true;

      // Price STRICTLY from the live doc_pricing.json (/doc-types), not the card.
      let price: ReturnType<typeof docPriceOf>;
      try {
        const entries = await getDocTypes();
        price = docPriceOf(entries.find((e) => e.code === selection.backendDocType));
      } catch {
        return true; // catalogue unreachable → don't block a draft
      }
      if (price === undefined) return true; // not on sale → free

      const docId = getCachedDocId();
      if (!docId) return true;
      const selected = sectionCommit?.resolvedSectionIds ?? [];
      const roots = billedRoots(config, selected);
      const whole = selected.length === 0 || roots.length >= totalRootCount(config);
      const order = buildOrder({
        config,
        docId,
        docType: selection.backendDocType,
        price,
        selected,
        whole,
      });
      // A benign refusal (nothing billable, already covered, sold-whole without
      // a section product) → draft for free rather than dead-ending.
      if (isRefusal(order)) return true;

      pendingCartRef.current = {
        items: [...order.items],
        run: {
          project_name: synthesizeProjectName(selection),
          library_ids: [selection.libraryId],
          library_names: { [selection.libraryId]: selection.libraryName },
          module_ids: order.scope === "document" ? null : (moduleIds ?? null),
          library_inventories: libraryInventory ? { [selection.libraryId]: libraryInventory } : undefined,
          insertion_mode_override: "batch",
          workspace_id: workspaceId,
        },
      };
      setOrderRequest(order);
      return false; // diverted into checkout
    },
    [docConfig, sectionCommit, libraryInventory],
  );

  // -- Draft flow (handleStartDraft → plan → run start) -------------------
  const {
    planLoading,
    planError,
    draftingMore,
    lockedModuleIdsForNextPlan,
    sectionPickerActive,
    pendingDraftStart,
    setPendingDraftStart,
    planApiHealthy,
    handleFetchSections,
    handleStartDraft,
    handlePlanApprove,
    handlePlanSkip,
    handlePlanModify,
    handleSectionCommit,
    handleSectionCancel,
    handleDraftMoreSections,
    resetDraftFlowState,
    handleGenerateSection,
    actuallyStartRun,
    finalizePaidRunStart,
    startCustomSubmission,
  } = useDraftFlow({
    runId: state.runId,
    activeSelection,
    setActiveSelection,
    docConfig,
    setDocConfig,
    sectionCommit,
    setSectionCommit,
    planId,
    setPlanId,
    planData,
    setPlanData,
    setStarting,
    setStartError,
    setRunSubscriptionStartMode,
    setTerminalRunStatus,
    setRunSubscriptionNonce,
    setRunStartedAt,
    dispatch,
    scaffoldedRef,
    insertionModeRef,
    libraryInventory,
    sections,
    submissionMode: runSubmissionMode,
    paymentGate,
  });

  // Bridge the checkout's onStarted (defined above useDraftFlow) to the paid
  // run finaliser (returned by it) without a declaration cycle.
  useEffect(() => {
    finalizePaidRunStartRef.current = finalizePaidRunStart;
  });

  // Resolve whether the active workspace is the demo (exempt) one, for the
  // library-create guard. Blocks only on a confirmed ``exempt_workspace``.
  useEffect(() => {
    if (!selectedWorkspaceId) {
      setActiveWorkspaceExempt(false);
      return;
    }
    let live = true;
    void getWorkspaceBilling(selectedWorkspaceId)
      .then((b) => {
        if (live) setActiveWorkspaceExempt(b.billing_reason === "exempt_workspace");
      })
      .catch(() => {
        if (live) setActiveWorkspaceExempt(false);
      });
    return () => {
      live = false;
    };
  }, [selectedWorkspaceId]);

  // Keep refs in sync on every render so beforeunload / pagehide
  // handlers can read the current run state without stale closures.
  // Intentionally has no dependency array — runs after every render.
  useEffect(() => {
    runIdRef.current = state.runId;
    runStateRef.current = state.state;
  });

  // Save spine snapshot only when the count of "completed" sections changes
  // (drafted | needs_approval | flagged), not on every subline / animation
  // tick. Reduces from ~160 serialise+network calls per 40-module run down
  // to ~40 (one per module that transitions out of queued/drafting).
  const completedSectionCount = sections.reduce(
    (n, s) => n + (s.status === "drafted" || s.status === "flagged" || s.status === "needs_approval" ? 1 : 0),
    0,
  );
  // activeSelection guards entry but does not gate the trigger — picking
  // a new library/doctype must not re-persist the same snapshot.
  const activeSelectionRef = useRef(activeSelection);
  useEffect(() => {
    activeSelectionRef.current = activeSelection;
  });
  // sections / activeSelection intentionally omitted — sections is read for
  // data but completedSectionCount is the intended trigger, and activeSelection
  // gates entry only (read via ref so its churn doesn't re-fire).
  // biome-ignore lint/correctness/useExhaustiveDependencies: completedSectionCount is the intended trigger
  useEffect(() => {
    const docId = getCachedDocId();
    if (!docId || !activeSelectionRef.current || sections.length === 0) return;
    scheduleSave(docId, {
      spine_snapshot: toPersistedSpineSnapshot(sections),
    });
  }, [completedSectionCount]);

  // Persist the full selection tuple unconditionally any time
  // ``activeSelection`` lands. Coupling persistence to the state mutation
  // (rather than to specific gestures like ``handleStartDraft``) means
  // every code path that sets a selection — picker commit, programmatic
  // restore, library-only intermediate pick — gets the full record on
  // disk. This was the root cause of doc-mopwpigy-mcf8j5's null fields:
  // the writer's path through the picker hit ``advanceDraftWithLibrary``
  // (partial save) without ever reaching ``handleStartDraft``, and no
  // effect was watching the state itself. (Bug A — 2026-05-07)
  useEffect(() => {
    const docId = getCachedDocId();
    if (!docId || !activeSelection) return;
    scheduleSave(docId, {
      workspace_id: activeSelection.workspaceId ?? undefined,
      library_id: activeSelection.libraryId,
      library_name: activeSelection.libraryName,
      doc_code: activeSelection.docCode,
      doc_name: activeSelection.docName,
      doc_framework: activeSelection.docFramework,
      backend_doc_type: activeSelection.backendDocType,
    });
    // Mirror to Word custom properties as a secondary source of truth.
    // On reload the bootstrap reads these back if the MongoDB session
    // is missing or was written without library/doctype fields.
    saveDocSelectionProps({
      libraryId: activeSelection.libraryId,
      libraryName: activeSelection.libraryName,
      docCode: activeSelection.docCode,
      docName: activeSelection.docName,
      backendDocType: activeSelection.backendDocType,
      workspaceId: activeSelection.workspaceId,
    });
  }, [activeSelection]);

  // Empty-doc detector. Mounts as soon as the doc id is resolved —
  // we DON'T gate on ``activeSelection`` because a corrupted session
  // (selection fields all null but spine/plan/run still present, the
  // exact Frankenstein state Bug A produced) would otherwise never
  // get the prompt. The "is there really a project here" check lives
  // in ``initialKnownToHaveContent`` instead, sourced from the
  // hydrate payload.
  //
  // Re-mounts when ``knownHadContent`` flips from false → true
  // (bootstrap completes after the doc id arrives). The dependency
  // array deliberately includes it so the watcher reads the
  // up-to-date initial state.
  useEffect(() => {
    if (!documentSessionId) return;
    const handle = startEmptyDocWatcher({
      // An empty body isn't always a "cleared document". If this doc has a
      // run still streaming server-side (e.g. the writer logged out/in, or
      // reopened the file before it hydrated), the destructive rotate-project
      // prompt would orphan that run. Check the live feed first: when a live
      // run targets this doc, stay silent — HomeScene renders (empty
      // classification) and floats its "Reconnect" card instead. The heartbeat
      // check inside /runs/active means a dead/terminal run won't suppress the
      // genuine "start fresh?" prompt.
      onEmpty: () => {
        const docId = getCachedDocId();
        if (!docId) {
          setNewProjectConfirmOpen(true);
          return;
        }
        void listActiveRuns({ docId, limit: 1 })
          .then((resp) => {
            if ((resp.runs ?? []).length > 0) {
              console.info("[VariantCShell] empty doc has a live run — offering reconnect, not rotate", {
                docId,
                runId: resp.runs[0]?.run_id,
              });
              return;
            }
            setNewProjectConfirmOpen(true);
          })
          .catch((err: unknown) => {
            // Feed unreachable — fall back to the existing prompt rather than
            // silently swallowing a genuine clear.
            console.warn("[VariantCShell] live-run check failed; showing rotate prompt:", err);
            setNewProjectConfirmOpen(true);
          });
      },
      initialKnownToHaveContent: knownHadContent,
    });
    emptyDocWatcherRef.current = handle;
    // Trigger-driven check — no ambient subscription. Fire once on
    // setup; ``state.state``-transition effect below also calls check()
    // on run-completion and reset. Routed through queueWordRead so
    // it can't race a selection-change probe that's still draining.
    void queueWordRead("emptyDocCheck:init", () => handle.check());
    return () => {
      handle.stop();
      if (emptyDocWatcherRef.current === handle) {
        emptyDocWatcherRef.current = null;
      }
    };
  }, [documentSessionId, knownHadContent]);

  // Run the empty-doc check on state transitions (esp. mid-run →
  // post-run / idle, which is when the writer is most likely to clear
  // the doc and start over).
  // biome-ignore lint/correctness/useExhaustiveDependencies: state.state intentionally used
  useEffect(() => {
    const handle = emptyDocWatcherRef.current;
    if (!handle) return;
    void queueWordRead("emptyDocCheck:stateTransition", () => handle.check());
  }, [state.state]);

  // -- Custom submission (build on an existing Word doc) -------------------
  // Always on (no feature flag): whenever a non-empty document is opened that
  // cannot be mapped to a run THIS user can access, offer "build on this
  // document". The document session is keyed by doc_id (which lives in the Word
  // file and persists across users), so a doc drafted by another user hydrates
  // THEIR run_id into ``bootstrapRunId`` — which is not a run this user owns.
  // We therefore don't trust ``bootstrapRunId``/``knownHadContent`` as "this
  // user has a project here"; we PROBE the candidate run's accessibility
  // (getRunSnapshot 200 = ours, 403/404 = foreign/gone) and fall back to the
  // user-scoped live-run list.
  // One-shot per session — the ref latches so it never re-opens after dismiss.
  // biome-ignore lint/correctness/useExhaustiveDependencies: state.runId read inline; one-shot
  useEffect(() => {
    if (sessionLoading || !documentSessionId) return;
    if (importDetectionDoneRef.current) return;
    // A run is already loaded into THIS session's UI → not a candidate.
    if (state.runId) {
      importDetectionDoneRef.current = true;
      return;
    }
    let cancelled = false;
    void queueWordRead("customImportDetect", async () => {
      const { hasContent } = await checkDocHasContent();
      if (cancelled || hasContent !== true) return; // empty (or unknown) → not a candidate

      // Is there a run THIS user can access for this doc? Probe the candidate —
      // the restored (doc-keyed, cross-user) ``bootstrapRunId`` first, else the
      // run id stamped in the Word metadata. getRunSnapshot succeeds only for a
      // run this user owns or was granted; a foreign/deleted run throws.
      const candidateRunId = bootstrapRunId || (await getActiveRunId().catch(() => null));
      if (candidateRunId) {
        const accessible = await getRunSnapshot(candidateRunId)
          .then(() => true)
          .catch(() => false);
        if (cancelled) return;
        if (accessible) {
          // This user's own project — leave the normal restore path alone.
          importDetectionDoneRef.current = true;
          return;
        }
        // Foreign/stale pointer (e.g. drafted by another user, reopened under
        // this SSO account) — clear it so it can't wedge this user onto an
        // inaccessible run. The document body is untouched.
        await clearActiveRunId().catch(() => {});
        if (cancelled) return;
      }

      // Belt-and-suspenders: a live run this user owns for this doc (user-scoped
      // ``/runs/active``) means it IS their project — don't offer build-on.
      const docId = getCachedDocId();
      if (docId) {
        const resp = await listActiveRuns({ docId, limit: 1 }).catch(() => null);
        if (cancelled) return;
        if (resp && (resp.runs ?? []).length > 0) {
          importDetectionDoneRef.current = true;
          return;
        }
      }

      // A COMPLETED run this user owns for this doc (``/runs/index`` →
      // ``RunBundleDoc``) is also their project — critically, a custom run is
      // ``completed`` the instant it is registered (no worker), so it never
      // appears in the live-run list above. Without this check a returning
      // writer whose Word ``AsthrRunId`` stamp was lost would be wrongly offered
      // "build on this document" instead of restoring their existing run. Stamp
      // the latest run id so the run-restore effect (useRunControl) re-anchors
      // and hydrates it (custom spine included) rather than re-importing.
      if (docId) {
        const done = await listRuns({ docId, limit: 10 })
          .then(async (resp) => {
            const runs = resp.documents.flatMap((g) => g.runs);
            if (runs.length === 0) return false;
            const latest = [...runs].sort((a, b) => b.completed_at.localeCompare(a.completed_at))[0];
            await setActiveRunId(latest.run_id).catch(() => {});
            return true;
          })
          .catch(() => false);
        if (cancelled) return;
        if (done) {
          importDetectionDoneRef.current = true;
          return;
        }
      }

      if (cancelled) return;
      importDetectionDoneRef.current = true;
      // Latch: this is a content-bearing doc with no run this user can access —
      // its persisted AsthrDocumentId may point at another user's runs. The
      // commit handlers rotate the doc_id before startRun so the backend tenancy
      // guard (server/routes/pipeline.py:1847) sees a brand-new document.
      foreignDocDetectedRef.current = true;
      setImportExistingDocOpen(true);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionLoading, documentSessionId, bootstrapRunId]);

  // Single selection-settle subscriber: empty-doc probe, then cursor-
  // section detection, strictly serialized through queueWordRead.
  // Previously these were two separate subscribers firing concurrent
  // Word.run() invocations on the same event. checkDocEmpty() reads
  // only the first paragraph (O(1)); detectCursorSectionLight() is a
  // bookmark lookup against ASTHRA_SEC_* bookmarks (O(1)). The
  // !isActiveRun gate keeps the cursor probe quiet during a mid-run
  // draft when wordWriter holds the Office.js queue.
  useEffect(() => {
    const unsubscribe = subscribeSelectionChange(() => {
      void (async () => {
        await queueWordRead("emptyDocCheck:selection", () => emptyDocWatcherRef.current?.check() ?? Promise.resolve());
        await refreshCursorSectionNumber();
      })();
    });
    return unsubscribe;
  }, [refreshCursorSectionNumber]);

  // Fallback trigger: some Word hosts may not emit reliable selection-change
  // events for plain cursor moves. Poll lightly while no active run is in
  // progress so the scope chip still tracks the writer's cursor section.
  useEffect(() => {
    if (isActiveRun(state.state)) return;
    const pollId = window.setInterval(() => {
      void refreshCursorSectionNumber();
    }, 1800);
    return () => {
      window.clearInterval(pollId);
    };
  }, [state.state, refreshCursorSectionNumber]);

  // Prime cursor-derived scope once on mount/idle so the left chip
  // doesn't stay on "document" until the first selection-change event.
  useEffect(() => {
    void refreshCursorSectionNumber();
  }, [refreshCursorSectionNumber]);

  // Clear the cursor-section chip the moment a run goes active so a stale
  // During mid-run, clear the cursor section so the §X.X.X label from the
  // prior idle browse doesn't linger over drafting. The cursor is tracked
  // by polling effect below; no need to refresh on state transitions.
  useEffect(() => {
    if (isActiveRun(state.state)) {
      setCursorSectionNumber(null);
    }
  }, [state.state]);

  // Run-level idle reset shared by every "return this document to idle"
  // path (empty-doc project rotation, and the save-and-start-fresh draft
  // flow). Clears the reducer + all run-scoped shell state (selection,
  // config, plan, spine snapshot, focus) so the next draft starts clean.
  // Deliberately does NOT touch chat/conversation state or the document id
  // — callers that need those (rotation) do them separately.
  // biome-ignore lint/correctness/useExhaustiveDependencies: all referenced values are stable setState setters or refs
  const resetShellToIdle = useCallback(() => {
    dispatch({ type: "RESET_TO_IDLE" });
    setActiveSelection(null);
    setDocConfig(null);
    setSectionCommit(null);
    setPlanData(null);
    setPlanId(null);
    resetDraftFlowState();
    setStartError(null);
    setStopWarning(null);
    setFocusedSectionId(null);
    setPersistedSpineSnapshot(null);
    setRunSubmissionMode(null);
    scaffoldedRef.current = false;
    insertionModeRef.current = "batch";
    void saveWordInsertionMode("batch");
  }, [resetDraftFlowState]);

  // Writer confirmed "yes, start fresh" — rotate the AsthrDocumentId
  // and reset all in-memory state so the next render bootstraps as if
  // this were a brand new document. The OLD doc_id's backend record is
  // not touched (orphaned but recoverable).
  // biome-ignore lint/correctness/useExhaustiveDependencies: all referenced values are stable module-level functions, setState setters, or refs
  const handleConfirmRotateProject = useCallback(async () => {
    setNewProjectConfirmOpen(false);
    emptyDocWatcherRef.current?.acknowledge();
    let newId = "";
    try {
      newId = await rotateDocumentId();
    } catch (err) {
      console.warn("[VariantCShell] rotateDocumentId failed:", err);
    }
    if (!newId) {
      // Office.js unavailable or rotation threw — nothing safe to do
      // beyond surfacing the failure quietly. The detector will
      // re-arm on the next content/empty cycle.
      return;
    }
    // Reset shell state. Mirrors handleReset's idle-reset shape plus
    // the persistence-seeded fields the bootstrap normally sets. The
    // run-scoped portion is shared with the save-and-start-fresh flow;
    // rotation additionally wipes chat and the document id below.
    resetShellToIdle();
    setPersistedChatMessages([]);
    setPersistedChatHasMore(false);
    setRestoredConversationId(undefined);
    setKnownHadContent(false);
    // Last — flip the cached doc id so any subsequent saves land on
    // the new project's record. The bootstrap-on-mount effect doesn't
    // re-run for the new id (it's gated on []), so we don't issue a
    // hydrate against the new id; that's fine because there's nothing
    // to hydrate — the new id is brand new.
    //
    // Two doc-id stores must move together:
    //   1. ``_cachedDocId`` (module-level in documentSessionService) —
    //      read by every ``scheduleSave(getCachedDocId(), ...)`` callsite,
    //      including the activeSelection persist effect, run_id save,
    //      and spine_snapshot save.
    //   2. ``documentSessionId`` (React state) — read by
    //      ``useChatHistoryView`` to route chat sync writes.
    //
    // Pre-2026-05-10 only #2 was updated, so post-rotation session/run/
    // spine writes silently kept landing on the old doc_id while chat
    // writes landed on the new one. The next reload's hydrate found
    // an empty session for the new id and the spine never restored.
    //
    // Flush first so any debounced patch already queued under the old
    // id (e.g. the RESET_TO_IDLE side-effects above) drains there
    // instead of getting re-targeted to the new id mid-write.
    flushAllPendingSaves();
    setCachedDocId(newId);
    setDocumentSessionId(newId);
  }, []);

  // Writer chose "Cancel — undo": replay Word's undo stack until the
  // body has content again (or until we hit the loop guard). Office's
  // ``context.document.undo()`` is per-step, so a multi-step delete
  // needs a few iterations.
  const handleCancelRestoreProject = useCallback(async () => {
    setNewProjectConfirmOpen(false);
    emptyDocWatcherRef.current?.acknowledge();
    try {
      await Word.run(async (context) => {
        // Bound the loop so a writer who Ctrl+Z'd into a never-restored
        // state doesn't pin the thread. 8 steps covers reasonable
        // multi-stroke deletes; beyond that we give up and leave the
        // doc in whatever state Word landed on.
        for (let i = 0; i < 8; i++) {
          context.document.undo();
          await context.sync();
          const body = context.document.body;
          body.load("text");
          await context.sync();
          if ((body.text ?? "").trim().length > 0) break;
        }
      });
    } catch (err) {
      console.warn("[VariantCShell] undo restore failed:", err);
    }
  }, []);

  // Phase 9 — single resolver for the writer-facing section label.
  // Closes over ``sections``; every shell-side ``EVENT_APPENDED``
  // dispatch with a ``sectionId`` calls this so the persisted entry
  // carries ``§${number}`` instead of the backend module id. The
  // pure helper lives in ``sectionLabel.ts`` so projection + tests
  // ssetDocumentSessionIdresetDraftFlowStatesetKnownHadContentsetRestoredConversationIdsetPersistedChatMessagessetPersistedSpineSnapshotscaffoldedRefinsertionModeRefhare the same format.
  const resolveSectionLabel = useCallback(
    (sectionId: string | null): string | null => formatSectionLabel(sectionId, sections),
    [sections],
  );

  // -- Regenerate-one handlers (Spec 23 P10.5) ----------------------------
  // Regen runs INSIDE the original run_id (no new run is created). See
  // `addin/src/variant-c/hooks/useRegenFlow.ts` — `handleRegenConfirm`
  // POSTs to /api/runs/{run_id}/sections/{module_id}/regenerate and
  // lets the existing SSE subscription carry the new events. The two
  // setters below are threaded in because the SSE subscribe effect can
  // bail out on terminal-run reloads; we clear the gate + bump the
  // nonce on confirm so the EventSource re-attaches in time.
  const { regenTarget, regenEnabled, handleInspectorRegenerate, openRegenFor, handleRegenConfirm, handleRegenCancel } =
    useRegenFlow({
    focusedSectionId,
    docConfig,
    sections,
    runId: state.runId,
    runState: state.state,
    dispatch,
    resolveSectionLabel,
    setSectionStatus,
    setTerminalRunStatus,
    setRunSubscriptionNonce,
    setFocusedSectionId,
  });

  // Bulk "Resolve issues" — kicks the orchestrator on the same run_id and
  // re-subscribes so it renders live on the spine (mirrors useRegenFlow).
  const resolveIssuesFlow = useResolveIssuesFlow({
    runId: state.runId,
    setSectionStatus,
    setTerminalRunStatus,
    setRunSubscriptionNonce,
    setSubscriptionStartMode: setRunSubscriptionStartMode,
    seedStreamCursor,
    onSwitchToSpine: () => handleViewModeChange("spine"),
  });
  resolveFlowOnEventRef.current = resolveIssuesFlow.onEvent;
  // Resolve pass finished cleanly: its proposals are in the Issues tab's
  // "Proposals to review", so open it, and post one line in the chat saying
  // what the pass did. Reads back the review queue — the pass is unchanged.
  onResolvePassCompleteRef.current = (pass) => {
    handleViewModeChange("issues");
    const runId = runIdRef.current;
    if (!runId) return;
    void getRunProposals(runId, "pending")
      .then((res) => {
        // Only this pass's proposals — an older pending proposal for the same
        // flag must not be counted (server times are naive UTC; see
        // proposalsSincePassStart).
        const proposals = proposalsSincePassStart(
          res.sections.flatMap((s) => s.proposals),
          pass.startedAt,
        );
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: Date.now(),
            actor: "asthra",
            sectionId: null,
            runId,
            payload: { kind: "agent_note", text: summariseResolvePass(pass.targets, proposals) },
          },
        });
      })
      .catch((err: unknown) => {
        // Best-effort: the proposals are already in the Issues tab.
        console.warn("[VariantCShell] resolve summary fetch failed:", err);
      });
  };

  // Check if any section is currently drafting (regen in progress)
  const isAnyRegenInProgress = sections.some((s) => s.status === "drafting");

  const handleChatRegenSectionDetected = useCallback(
    (sectionId: string) => {
      setSectionStatus(sectionId, "drafting");
    },
    [setSectionStatus],
  );

  const handleChatRegenReconnect = useCallback(() => {
    setTerminalRunStatus(false);
    setRunSubscriptionStartMode("latest");
    setRunSubscriptionNonce((value) => value + 1);
  }, []);

  // -- Intent-routed chat --------------------------------------------------
  // Default-ON since 2026-09-08. It used to be opt-in via a
  // `chat_intent_routing` flag that nothing seeded, so any environment
  // without the hand-inserted key silently lost the edit-highlight
  // (Approve/Reject) flow. `chat_intent_routing_disabled` is the kill
  // switch: absent/false → routing on (useFlag is closed-by-default).
  const intentRoutingEnabled = !useFlag("chat_intent_routing_disabled");

  // -- Unfinished UI affordances (show.incomplete-actions) ----------------
  // Single kill-switch for CTAs that are visible but not yet functional.
  // OFF — the default, and the value while flags are loading or if the
  // fetch fails — hides them. AppSource certification rejects
  // non-functional buttons, so "hidden" has to be the state that needs no
  // successful server round-trip; see the closed-by-default semantics of
  // useFlag in addin/src/context/FeatureFlagsContext.tsx.
  //
  // Declared here rather than beside ``advanced-commands`` further down
  // because the IdleGreeting render block reads it earlier in this
  // component body, and a const is not hoisted.
  const showIncompleteActions = useFlag("show.incomplete-actions");

  // -- Internal/developer affordances (show.internal-tools) ---------------
  // Separate from show.incomplete-actions: those CTAs are hidden because
  // they do not work yet, these work fine but expose internals writers
  // have no reason to see (provider roster, raw model ids). Same
  // closed-by-default polarity — hidden unless a flag says otherwise.
  //
  // Gates the LIVE/SANDBOX mode pill and the model-backend switcher. The
  // token counter is the remaining affordance flagged in pre-AppSource
  // testing and belongs on this same flag.
  const showInternalTools = useFlag("show.internal-tools");

  // -- Doctype catalog breadth ------------------------------------------
  // The picker shows exactly the doctypes the signed-in user's tier may
  // draft, straight from the backend's tier-filtered GET /doc-types (a
  // free_user gets only the doctypes/doc_pricing.json codes; paid tiers get
  // all). No client-side allowlist or feature flag — the server is the
  // authority, and the run-start gate enforces it regardless.
  const { allowedDocCodes: tierAllowedDocCodes } = useAllowedDocCodes();

  const handleChatRunActivity = useCallback(
    (
      activity: {
        moduleId: string;
        kind: "regen" | "adhoc";
        instruction: string;
      } | null,
    ) => {
      updateChatRunActivity(
        activity
          ? {
              ...activity,
              phase: "starting",
              draftSoFar: "",
              startedAt: Date.now(),
            }
          : null,
      );
    },
    [updateChatRunActivity],
  );

  // Writer-facing status line for the live drafting bubble. Reuses
  // TimelineView's streamingStatus/streamingDraft transient row, so a
  // chat-initiated regen/ad-hoc reads exactly like chat streaming.
  // Prefixed with a label ([Redraft] / [Draft content]) so the flow
  // stays visible through the whole run. "Redraft" is a literal —
  // /regen is a command now, not a routed intent.
  const chatRunActivityStatus = useMemo(() => {
    if (!chatRunActivity) return null;
    const label = resolveSectionLabel(chatRunActivity.moduleId) ?? chatRunActivity.moduleId;
    const verb = chatRunActivity.kind === "regen" ? "regenerating" : "drafting ad-hoc content for";
    const prefix = `[${chatRunActivity.kind === "regen" ? "Redraft" : intentLabel("draft_content")}] `;
    switch (chatRunActivity.phase) {
      case "retrieving":
        // Stream the live retrieval subline (same detail the spine shows) when
        // available; fall back to the coarse phrase before the first event.
        return `${prefix}Asthra is ${verb} ${label} — ${chatRunActivity.detail ?? "gathering data…"}`;
      case "drafting":
        return `${prefix}Asthra is ${verb} ${label} — writing…`;
      default:
        return `${prefix}Asthra is ${verb} ${label}…`;
    }
  }, [chatRunActivity, resolveSectionLabel]);

  // Records a proposal-Insert outcome as an ``approval`` EventLog entry
  // (the chat audit trail's durable record of the writer's decision).
  const handleProposalDecision = useCallback(
    (note: string, sectionId: string | null) => {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId,
          sectionLabel: sectionId ? (resolveSectionLabel(sectionId) ?? undefined) : undefined,
          runId: runIdRef.current,
          payload: { kind: "approval", decision: "accepted", note },
        },
      });
    },
    [resolveSectionLabel],
  );

  // -- Highlight edit flow (chat "edit highlight" intent) -----------------
  // Two entries per decision: the durable ``edit_proposal`` (drafted
  // output + outcome — the assistant bubble in the thread) at ``ts``,
  // and the ``approval`` system note (audit invariant) at ``ts + 1``
  // (the same pairing convention askwhy uses so ordering is stable).
  const handleHighlightEditDecision = useCallback(
    (
      decision: "accepted" | "rejected",
      note: string,
      sectionId: string | null,
      proposal: import("./hooks/useHighlightEditFlow").HighlightEditDecisionRecord,
    ) => {
      const ts = Date.now();
      const sectionLabel = sectionId ? (resolveSectionLabel(sectionId) ?? undefined) : undefined;
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts,
          actor: "asthra",
          sectionId,
          sectionLabel,
          runId: runIdRef.current,
          payload: {
            kind: "edit_proposal",
            instruction: proposal.instruction,
            originalText: proposal.originalText,
            replacementText: proposal.replacementText,
            decision,
            outcomeNote: note,
          },
        },
      });
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: ts + 1,
          actor: "you",
          sectionId,
          sectionLabel,
          runId: runIdRef.current,
          payload: { kind: "approval", decision, note },
        },
      });
      // Auto-save (2026-07-21): persist the accepted edit to the SSOT,
      // scoped to just this section (sectionId is the module id).
      if (decision === "accepted") {
        void autoSaveModuleRef.current?.(sectionId);
        // Re-check open items for the section: the SSOT write-back has landed
        // (and with it the inline ledger reconcile), so nudge the inspector's
        // Data Gaps tab to re-fetch the updated open missing-info + regulator
        // flags for the full section.
        setIssuesRefreshSignal((n) => n + 1);
      }
    },
    [resolveSectionLabel],
  );

  const handleHighlightEditError = useCallback((message: string) => {
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: runIdRef.current,
        payload: { kind: "agent_note", text: message },
      },
    });
  }, []);

  const highlightEdit = useHighlightEditFlow({
    onDecisionRecorded: handleHighlightEditDecision,
    onError: handleHighlightEditError,
    // @-mention attachments on the edit request resolve against the
    // active source library (mirrors useChatSystem's libraryId pick).
    libraryId: activeSelection?.libraryId ?? (selectedLibraryId || null),
  });

  // Streaming-status line while the rewrite request is in flight —
  // carries the intent label so the classified intent stays visible
  // from submit through the proposal's arrival.
  const highlightEditStatus = useMemo(() => {
    if (highlightEdit.phase !== "requesting") return null;
    const target = highlightEdit.activeRequest?.sectionNumber
      ? `§${highlightEdit.activeRequest.sectionNumber}`
      : "the highlighted text";
    // The rewrite streams SSE progress (checking for context, retrieving,
    // rewriting); surface the latest step, falling back to the generic line
    // before the first event lands.
    const base = highlightEdit.progressLabel ?? `Asthra is drafting a document-ready replacement for ${target}…`;
    return `[${intentLabel("edit_highlight")}] ${base}`;
  }, [highlightEdit.phase, highlightEdit.activeRequest, highlightEdit.progressLabel]);

  // The pending edit proposal, rendered as the newest assistant
  // message in the chat thread (Approve/Reject below the drafted
  // output). Passed to BOTH TimelineView mounts — the intent router
  // allows edit_highlight without a run, so the idle surface must
  // show it too.
  const editProposalNode = highlightEdit.proposal ? (
    <EditProposalMessage
      proposal={highlightEdit.proposal}
      phase={highlightEdit.phase}
      applySteps={highlightEdit.applySteps}
      progressLabel={highlightEdit.progressLabel}
      onApprove={() => void highlightEdit.approve()}
      onReject={highlightEdit.reject}
      onRevise={highlightEdit.revise}
    />
  ) : null;

  // -- Ad-hoc draft proposal flow (chat "draft content" intent) -----------
  // The full-section draft arrives via ``adhoc_draft_ready`` (or, after
  // a pane reload, as an undecided ``pending_adhoc_proposal`` in the
  // sections/state snapshot) and renders as an Approve/Reject
  // ProposalCard — the same decision surface the highlight-edit flow
  // uses. Approve is SSOT-first: the decision endpoint commits Mongo,
  // then the hook replaces the section in Word in-place.

  // ModuleId → numbered skeleton section ("9.2") for the Word insert.
  // Narrative module ids ARE skeleton numbers, so when the spine lookup
  // misses (e.g. a loaded terminal run whose sections list is thin) a
  // numeric moduleId is used directly — otherwise the Word write would
  // be silently skipped. Non-numeric modules (TITLE/spec) return null.
  const resolveSectionNumber = useCallback(
    (moduleId: string): string | null => {
      const number = sections.find((s) => s.id === moduleId)?.number;
      if (number && /^\d+(\.\d+)*$/.test(number)) return number;
      return /^\d+(\.\d+)*$/.test(moduleId) ? moduleId : null;
    },
    [sections],
  );

  // Bumped after every decision so the rehydration effect re-checks the
  // snapshot — if another section still has an undecided proposal, its
  // card surfaces next (single-card surface, newest first).
  const [adhocDecisionNonce, setAdhocDecisionNonce] = useState(0);

  // Two entries per decision, mirroring handleHighlightEditDecision:
  // the durable ``adhoc_draft`` (drafted section + outcome — the
  // assistant bubble in the thread) at ``ts``, and the ``approval``
  // system note (audit invariant) at ``ts + 1``.
  const handleAdhocProposalDecision = useCallback(
    (
      decision: "accepted" | "rejected",
      note: string,
      record: import("./hooks/useAdhocProposalFlow").AdhocProposalDecisionRecord,
    ) => {
      const ts = Date.now();
      const sectionLabel =
        resolveSectionLabel(record.moduleId) ?? (record.sectionNumber ? `§${record.sectionNumber}` : undefined);
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts,
          actor: "asthra",
          sectionId: record.moduleId,
          sectionLabel,
          runId: runIdRef.current,
          payload: {
            kind: "adhoc_draft",
            content: record.content,
            instruction: record.instruction,
            wordCount: record.wordCount,
            moduleId: record.moduleId,
            mode: record.mode,
            decision,
            outcomeNote: note,
          },
        },
      });
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: ts + 1,
          actor: "you",
          sectionId: record.moduleId,
          sectionLabel,
          runId: runIdRef.current,
          payload: { kind: "approval", decision, note },
        },
      });
      // Belt-and-braces spine cleanup: the section_state_changed SSE
      // normally clears the "redraft ready"-style subline, but a missed
      // event must not leave a spinner behind. Accepted decisions also
      // converge the status (edited lifecycle → drafted spine bucket).
      clearSectionActivity(record.moduleId, decision === "accepted" ? "drafted" : undefined);
      // Auto-save (2026-07-21): persist the accepted chat edit to the SSOT,
      // scoped to just this section (its markdown → draft_full + formatting
      // → section_ooxml).
      if (decision === "accepted") {
        void autoSaveModuleRef.current?.(record.moduleId);
      }
      setAdhocDecisionNonce((n) => n + 1);
    },
    [resolveSectionLabel, clearSectionActivity],
  );

  const handleAdhocProposalError = useCallback((message: string) => {
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: runIdRef.current,
        payload: { kind: "agent_note", text: message },
      },
    });
  }, []);

  const adhocFlow = useAdhocProposalFlow({
    onDecisionRecorded: handleAdhocProposalDecision,
    onError: handleAdhocProposalError,
  });

  // Live producer: translate the raw ``adhoc_draft_ready`` SSE event
  // into an offer. Kept on a ref because the SSE wrapper is declared
  // above this hook (see adhocProposalOfferRef).
  const offerAdhocFromEvent = useCallback(
    (event: { module_id?: string; run_id?: string; section_response?: unknown }) => {
      const moduleId = event.module_id;
      const runId = event.run_id ?? runIdRef.current;
      if (!moduleId || !runId) return;
      const sr = (event.section_response ?? {}) as Record<string, unknown>;
      const content = typeof sr.draft_text === "string" ? sr.draft_text : "";
      if (!content) return;
      const mode: AdhocProposal["mode"] = sr.mode === "redraft" ? "redraft" : sr.mode === "edit" ? "edit" : "draft";
      const proposal: AdhocProposal = {
        runId,
        moduleId,
        sectionNumber: resolveSectionNumber(moduleId),
        sectionLabel: resolveSectionLabel(moduleId) ?? moduleId,
        instruction: chatRunActivityRef.current?.instruction ?? "",
        content,
        wordCount: typeof sr.word_count === "number" ? sr.word_count : content.split(/\s+/).length,
        baseSectionVersion: typeof sr.base_section_version === "number" ? sr.base_section_version : null,
        mode,
      };
      // Chat ``/redraft`` (Path B) auto-load: apply the full-section redraft to
      // the live doc with NO Approve/Reject card (falls back to the card on
      // version drift / apply error — see useAdhocProposalFlow.autoApply). The
      // ad-hoc ``draft`` and chat ``edit`` proposals keep the manual card.
      if (REDRAFT_AUTO_LOAD && mode === "redraft") {
        void adhocFlow.autoApply(proposal);
        return;
      }
      adhocFlow.offer(proposal);
    },
    [adhocFlow.offer, adhocFlow.autoApply, resolveSectionNumber, resolveSectionLabel],
  );
  useEffect(() => {
    adhocProposalOfferRef.current = offerAdhocFromEvent;
    return () => {
      adhocProposalOfferRef.current = null;
    };
  }, [offerAdhocFromEvent]);

  // Rehydration producer: after a pane reload (or a decision, via the
  // nonce) check the sections/state snapshot for undecided proposals
  // and offer the newest. The decision endpoint stamps proposals
  // decided, so an approved/rejected card can never come back.
  // biome-ignore lint/correctness/useExhaustiveDependencies: adhocFlow.phase is read but deliberately excluded, and adhocDecisionNonce is a deliberate extra trigger — see the note above the dep array.
  useEffect(() => {
    const runId = state.runId;
    if (!runId || adhocFlow.phase !== "idle") return;
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getSectionState(runId);
        if (cancelled) return;
        const pending = snap.sections
          .filter((s) => s.pending_adhoc_proposal?.content)
          .sort((a, b) =>
            (b.pending_adhoc_proposal?.created_at ?? "").localeCompare(a.pending_adhoc_proposal?.created_at ?? ""),
          );
        const first = pending[0];
        const proposal = first?.pending_adhoc_proposal;
        if (!first || !proposal) return;
        const next: AdhocProposal = {
          runId,
          moduleId: first.module_id,
          sectionNumber: resolveSectionNumber(first.module_id),
          sectionLabel: resolveSectionLabel(first.module_id) ?? first.module_id,
          instruction: proposal.instruction,
          content: proposal.content,
          wordCount: proposal.word_count,
          baseSectionVersion: proposal.base_section_version,
          mode: proposal.mode === "redraft" ? "redraft" : proposal.mode === "edit" ? "edit" : "draft",
        };
        adhocFlow.offer(next);
      } catch {
        // Snapshot fetch failed — the live SSE path still offers new
        // proposals; rehydration just doesn't happen this pass.
      }
    })();
    return () => {
      cancelled = true;
    };
    // adhocFlow.phase is intentionally NOT a dep — re-running on every
    // idle transition would re-offer while the writer is mid-decision;
    // the nonce is the explicit "check again" signal.
    // (The suppression for this sits above the useEffect call, where Biome
    // actually reads it — a biome-ignore here has no effect.)
  }, [state.runId, adhocDecisionNonce, resolveSectionNumber, resolveSectionLabel, adhocFlow.offer]);

  // The pending ad-hoc proposal, rendered as the newest assistant
  // message in the chat thread (Approve/Reject below the drafted
  // section).
  const adhocProposalNode = adhocFlow.proposal ? (
    <AdhocProposalMessage
      proposal={adhocFlow.proposal}
      phase={adhocFlow.phase}
      driftWarning={adhocFlow.driftWarning}
      applySteps={adhocFlow.applySteps}
      onApprove={() => void adhocFlow.approve()}
      onReject={() => void adhocFlow.reject()}
    />
  ) : null;

  // -- Edit-section flow (chat "edit section" intent) ---------------------
  // Synchronous request: the edit-draft endpoint revises the section's
  // existing content per the writer's comments (+ tagged attachments;
  // library lookup only if the model needs it), persists it as a
  // mode:"edit" pending proposal server-side, and the response is
  // offered straight to the Approve/Reject card — no SSE hop. While
  // the request runs, a status line shows in the thread (mirror of the
  // highlight-edit "requesting" line).
  const [sectionEditRequest, setSectionEditRequest] = useState<{
    moduleId: string;
    sectionNumber: string | null;
  } | null>(null);

  const handleSectionEditRequested = useCallback(
    (req: {
      runId: string;
      sectionId: string;
      sectionNumber: string | null;
      instruction: string;
      attachments?: import("../types").MentionAttachment[];
    }) => {
      setSectionEditRequest({ moduleId: req.sectionId, sectionNumber: req.sectionNumber });
      void (async () => {
        try {
          const res = await editDraftSection(req.runId, req.sectionId, {
            instruction: req.instruction,
            libraryId: selectedLibraryId || activeSelection?.libraryId || null,
            attachments: req.attachments,
          });
          adhocFlow.offer({
            runId: req.runId,
            moduleId: req.sectionId,
            sectionNumber: resolveSectionNumber(req.sectionId) ?? res.section_number,
            sectionLabel: resolveSectionLabel(req.sectionId) ?? req.sectionId,
            instruction: req.instruction,
            content: res.content,
            wordCount: res.word_count,
            baseSectionVersion: res.base_section_version,
            mode: "edit",
          });
        } catch (err) {
          const detailObj = (err as { detail?: { error?: string } }).detail;
          const message =
            detailObj?.error === "no_draft_to_edit"
              ? "That section hasn't been drafted yet — nothing to edit. Ask me to draft content for it instead."
              : `Couldn't revise the section: ${err instanceof Error ? err.message : String(err)}`;
          handleAdhocProposalError(message);
        } finally {
          setSectionEditRequest(null);
        }
      })();
    },
    [
      adhocFlow.offer,
      resolveSectionNumber,
      resolveSectionLabel,
      handleAdhocProposalError,
      selectedLibraryId,
      activeSelection?.libraryId,
    ],
  );

  // Streaming-status line while the edit-draft request is in flight —
  // same slot the highlight-edit "requesting" line uses.
  const sectionEditStatus = useMemo(() => {
    if (!sectionEditRequest) return null;
    const label =
      resolveSectionLabel(sectionEditRequest.moduleId) ??
      (sectionEditRequest.sectionNumber ? `§${sectionEditRequest.sectionNumber}` : "the section");
    return `[${intentLabel("edit_section")}] Asthra is revising ${label}…`;
  }, [sectionEditRequest, resolveSectionLabel]);

  // Progress / "ready" notice for an in-flight chat edit, shown in the
  // composer panel while the writer is on ANOTHER view (Spine / Issues).
  // The edit's own progress line and Approve/Reject card render only in the
  // chat view, so off it the writer saw nothing until they switched back.
  // Reuses the exact status strings the chat thread shows; no new flow.
  const offChatEditNotice = useMemo<{ kind: "working"; text: string } | { kind: "ready" } | null>(() => {
    if (state.viewMode === "timeline") return null;
    if (highlightEdit.proposal || adhocFlow.proposal) return { kind: "ready" };
    const text = highlightEditStatus ?? sectionEditStatus ?? chatRunActivityStatus;
    return text ? { kind: "working", text } : null;
  }, [
    state.viewMode,
    highlightEdit.proposal,
    adhocFlow.proposal,
    highlightEditStatus,
    sectionEditStatus,
    chatRunActivityStatus,
  ]);

  // handleCommand — the slash-command dispatcher — is defined
  // later in the shell (just before the JSX return) so it can
  // close over chat, handleStopRun, and planReviewActive — all
  // declared below. Original location; moved 2026-04-23 for the
  // Spec 29 palette-merge commit.

  // -- Freeform chat wiring (Spec 27) — delegated to useChatSystem ------
  const {
    chat,
    handleSendChat,
    handleStopChat,
    buildCommandSnapshot,
    emitSuggestionEvent,
    selectionCitations: chatSelectionCitations,
    captureSelection,
    capturedSelection,
    dismissSelection,
    syncSelectionFromProbe,
    pendingApproval,
    submitApproval,
    rejectApproval,
    bridgeStatus,
  } = useChatSystem({
    runState: state.state,
    runId: state.runId,
    eventLogEntries: state.eventLog.entries,
    documentSessionId,
    activeSelection,
    selectedLibraryId,
    selectedDocCode,
    libraries,
    identity,
    restoredConversationId,
    sections,
    dispatch,
    onRegenSectionDetected: handleChatRegenSectionDetected,
    onRegenReconnect: handleChatRegenReconnect,
    intentRoutingEnabled,
    onChatRunActivity: handleChatRunActivity,
    onHighlightEditRequested: highlightEdit.start,
    onSectionEditRequested: handleSectionEditRequested,
  });

  // -- Context chips (above the composer) --------------------------------
  // True once any section has a draft — gates the chip row and the
  // selection-chip probe subscription (pre-generation there is nothing to
  // act on, so no ambient selection handler is attached).
  const hasDraftedContent =
    (state.runSummary?.draftedSections ?? 0) > 0 ||
    sections.some((s) => s.status === "drafted" || s.status === "needs_approval" || s.status === "flagged");
  // Live text of the writer's chip-eligible highlight (null when none).
  // Drives the selection chips AND the highlighted-text pill: mirrored
  // into the captured selection so the pill shows it immediately and the
  // next send carries it.
  const chipSelectionText = useSelectionChip(hasDraftedContent);
  useEffect(() => {
    syncSelectionFromProbe(chipSelectionText);
  }, [chipSelectionText, syncSelectionFromProbe]);

  // Agent-initiated write approval → the SAME ProposalCard surface
  // writer-initiated highlight edits use, rendered in the chat-thread
  // tail. The WS bridge answering semantics are untouched (approve =
  // {approved:true, content} via submitApproval). Unparseable payloads
  // fall back to UserInputInlineRow below; non-write input types
  // (api_failure, literature approval, generic forms) always stay
  // with UserInputInlineRow.
  const writeApprovalHandledInThread =
    pendingApproval?.inputType === "write_approval" && parseWriteApprovalPayload(pendingApproval.question) !== null;
  const writeApprovalNode = writeApprovalHandledInThread ? (
    <WriteApprovalMessage
      request={pendingApproval}
      bridgeConnected={bridgeStatus === "connected"}
      onSubmit={submitApproval}
    />
  ) : null;

  // Live activity — the step(s) of the current chat turn still executing.
  // Instead of one box per step, collapse them into a single compact
  // "Looking things up… N steps" row (expandable). Resolved steps arrive as
  // durable ``chat_tool_step`` EventLog entries and are grouped in the thread.
  const liveActivityNode =
    chat.liveSteps.length === 0 ? null : chat.liveSteps.length === 1 ? (
      <StepRow
        name={getToolDisplay(chat.liveSteps[0].tool).label}
        summary={
          chat.liveSteps[0].summaryOverride ??
          describeToolCall(chat.liveSteps[0].tool, chat.liveSteps[0].input ?? {}, false)
        }
        status="running"
      />
    ) : (
      <ActionGroup count={chat.liveSteps.length} live>
        {chat.liveSteps.map((step) => (
          <StepRow
            key={step.stepId}
            name={getToolDisplay(step.tool).label}
            summary={step.summaryOverride ?? describeToolCall(step.tool, step.input ?? {}, false)}
            status="running"
          />
        ))}
      </ActionGroup>
    );

  // Verdict watcher (G-P2-A path c). Polls the review-agent sidecar
  // for per-section verdicts; the useEffect below translates new
  // major/refuse verdicts into APPROVAL_REQUESTED dispatches.
  // Only enabled while a run is in flight — idle/post-run shouldn't
  // poll the sidecar. Also gated behind the sidecar kill switch so we
  // stop calling the review_agent container entirely while it's off.
  const verdictWatcherEnabled = REVIEW_AGENT_ENABLED && (state.state === "mid-run" || state.state === "approval");
  const { verdicts: sectionVerdicts } = useRunVerdictWatcher({
    identity,
    enabled: verdictWatcherEnabled,
  });

  // Spec 23 P2.3 — Word-edit detection. Polls the doc body hash every
  // 30s body-hash polling. The hook's contract is "detect WRITER-
  // initiated edits" (per its docstring). Polling can't distinguish
  // source — every body change is observed equally — so the gate
  // here must encode the assumption "writes during this state are
  // writer-driven, not agent-driven".
  //
  // Off during:
  //   - idle / plan-review: no run context to attribute edits to
  //   - mid-run: agent is streaming content into Word (FixtureRunner
  //     replay OR live WriteGate-mediated section drafts). Every body-
  //     hash change in this window is the agent's write, not the
  //     writer's. Capturing them as "You edited the document" is a
  //     mis-attribution that confuses the writer.
  //   - any time chat.isStreaming: chat-initiated WriteGate writes
  //     also touch the body programmatically. Suppress those for the
  //     same reason.
  //
  // On during: approval / paused / post-run AND chat is idle. The
  // writer's manual Word edits in those windows are correctly
  // captured.
  //
  // # DECISION (2026-05-06): tighten the gate to exclude mid-run +
  // # chat-streaming. Pre-2026-05-06 the gate included mid-run on
  // # the assumption that writers might edit while watching a run
  // # progress. In practice, ALL body-hash deltas during mid-run are
  // # agent writes (sandbox replay or live drafts), and the hook
  // # mis-classified them as writer edits. The hook's docstring
  // # already declared the intended exclusion ("WriteGate-mediated
  // # writes are NOT captured here"); this gate now enforces it.
  const wordEditsEnabled =
    !chat.isStreaming && (state.state === "approval" || state.state === "paused" || state.state === "post-run");
  useWordEdits({
    enabled: wordEditsEnabled,
    agentWriteRef: agentLastWriteAtRef,
    onEdit: useCallback(
      (ts: number, snippet: string | null) => {
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts,
            actor: "you",
            sectionId: focusedSectionId,
            sectionLabel: resolveSectionLabel(focusedSectionId),
            runId: state.runId,
            // Spec 24 T4 — capture the diff snippet on the event so
            // WordDetail can render it without re-fetching the doc.
            // Null → snippet field omitted (panel renders honest
            // "no snippet captured" note).
            payload: snippet ? { kind: "word", snippet } : { kind: "word" },
          },
        });
      },
      [focusedSectionId, state.runId, resolveSectionLabel],
    ),
  });

  // Tracks which (sectionId, verdict) pairs we've already translated
  // into approval dispatches — prevents the popout from re-firing on
  // every poll cycle while the writer hasn't yet resolved it.
  const dispatchedApprovalsRef = React.useRef<Set<string>>(new Set());

  // Latest sections snapshot — read by the verdict watcher to derive
  // the popout label without subscribing to per-subline section churn.
  // Subscribing would re-run the watcher on every text_chunk SSE tick,
  // re-evaluating all verdicts and cascading through setSectionStatus.
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  });

  // sections intentionally omitted — read via sectionsRef so per-subline
  // ticks don't re-run verdict translation.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sections read via sectionsRef
  useEffect(() => {
    if (!verdictWatcherEnabled) {
      // Reset between runs so the next run can re-trigger the same
      // section if its verdict matches.
      dispatchedApprovalsRef.current = new Set();
      return;
    }
    for (const [sectionId, record] of Object.entries(sectionVerdicts)) {
      const dispatchKey = `${sectionId}::${record.verdict}::${record.endedAt}`;
      if (dispatchedApprovalsRef.current.has(dispatchKey)) continue;

      const section = sectionsRef.current.find((s) => s.id === sectionId);
      const sectionNumber = section?.number ?? sectionId;

      const approval = translateVerdictToApproval({
        sectionId,
        sectionNumber,
        verdict: record.verdict,
        scorecards: record.scorecards,
      });
      if (!approval) {
        // Pass / minor — no popout. Mark as dispatched anyway so we
        // don't keep re-evaluating identical records.
        dispatchedApprovalsRef.current.add(dispatchKey);
        continue;
      }

      dispatchedApprovalsRef.current.add(dispatchKey);
      setSectionStatus(sectionId, "needs_approval");
      dispatch({ type: "APPROVAL_REQUESTED", approval });
    }
  }, [sectionVerdicts, verdictWatcherEnabled, setSectionStatus, dispatch]);

  // Spec 23 P3 — enrich sections with quality + flaggedCount derived
  // from the sidecar's per-section verdicts. Merge happens at shell
  // level (not in useVariantCRun) because verdicts flow through a
  // different data path than SSE section updates — keeping the
  // merge here preserves the hook's single-input contract.
  //
  // Memoised on (sections, sectionVerdicts) so LoomSpine's memo'd
  // children only re-render when a relevant input actually shifts.
  const enrichedSections = useMemo<SpineSection[]>(() => {
    const hasVerdicts = Object.keys(sectionVerdicts).length > 0;
    // ``regulatoryOpenByModule`` (from the section_issues ledger) is the live,
    // authoritative source for the spine's ⚖ badge — the audit writes
    // kind="regulatory" rows there but the SSE/DB ``regulatorFlagCount`` can lag
    // (it's only set when the audit_complete payload carries the count). Overlay
    // the ledger's OPEN regulatory count once we've fetched it, so an audited
    // section immediately shows its open flags on the spine (and clears when
    // they're resolved). Falls back to the SSE/DB value before the first fetch.
    const hasReg = regulatoryOpenByModule != null;
    if (!hasVerdicts && !hasReg) return sections;
    return sections.map((s) => {
      const record = sectionVerdicts[s.id];
      const next = record ? { ...s, ...deriveQualitySignals(record) } : s;
      if (hasReg) {
        const reg = regulatoryOpenByModule[s.id] ?? 0;
        if (reg !== next.regulatorFlagCount) return { ...next, regulatorFlagCount: reg };
      }
      return next;
    });
  }, [sections, sectionVerdicts, regulatoryOpenByModule]);

  // Spec 24 T3 — closure for TimelineView's ApprovalDetail to look
  // up the verdict by sectionId. Memoised on sectionVerdicts so the
  // child's prop only changes when the underlying record set
  // changes (not on every shell re-render).
  const verdictForSection = useMemo(() => (sectionId: string) => sectionVerdicts[sectionId] ?? null, [sectionVerdicts]);

  // Spec 23 P9 — writer-presence signal. Fires ``isBack`` when the
  // writer returns from an absence ≥ 5min during a run. We gate on
  // run-active states (same set as useWordEdits) so idle / plan-
  // review never trip the pill — there's nothing meaningful to
  // summarise in those states.
  const presenceEnabled = state.state === "mid-run" || state.state === "approval" || state.state === "post-run";
  const presence = useWriterPresence({ enabled: presenceEnabled });

  // 2026-05-11 — the previous useDocumentCursor hook here ran a
  // Word.run on every selection event and froze Word on long CSR
  // drafts. It's been replaced by:
  //   * SuggestionChips subscribing to wordProbe.subscribeChipVisibility
  //     (cheap two-stage probe — short-circuits on ctrl+A).
  //   * handleSendChat reading the selection + cursor section on
  //     demand at click time.
  //   * emptyDocDetector being driven explicitly via state-transition
  //     useEffect above.
  // No ambient cursor state lives in the shell anymore.
  //
  // Spec 29 Phase 3 follow-up — classify the Word doc on mount
  // so the idle greeting adapts ("empty" vs "has-content" vs
  // "CER/CSR/PSUR-like"). Read once; doesn't re-run on every
  // render. See useDocumentContext.ts for the DECISION on
  // one-shot reads. ``documentContextNonce`` is bumped once after a Load
  // replaces the body (handleRunLoaded) so a document that was blank at
  // mount stops being latched as "empty" — otherwise any later return to
  // idle renders the full-screen Home scene over a full Word body.
  const [documentContextNonce, setDocumentContextNonce] = useState(0);
  const documentContext = useDocumentContext(documentContextNonce);

  // Phase 9.5 (Bundle B+D follow-up) — persist the idle greeting as
  // an ``agent_note`` event ONCE per session so it lives in the
  // chat history alongside every other writer-visible action.
  // Pre-this change the greeting only rendered as TimelineView's
  // idle banner and disappeared when the first event landed; that
  // broke the "everything in chat" mental model.
  //
  // Decision table lives in ``greetingPolicy.decideGreetingEmission``
  // so the four branches (already-emitted / wait / skip-and-mark /
  // emit) are testable without mounting the shell.
  const greetingEmittedRef = useRef(false);
  useEffect(() => {
    // When the doc is classified (empty OR has-content), the interactive
    // ``IdleGreeting`` surface (checklist / context card) IS the greeting —
    // so we must NOT also seed the plain "Welcome to Asthra AI" agent_note,
    // or it stacks on top of the card. The seeded note is only the fallback
    // for the loading/unavailable case where no IdleGreeting renders.
    const classifiedState = documentContext.state === "ready" ? documentContext.classification?.state : null;
    if (classifiedState === "empty" || classifiedState === "has-content") {
      greetingEmittedRef.current = true;
      return;
    }
    const decision = decideGreetingEmission({
      alreadyEmittedThisSession: greetingEmittedRef.current,
      classificationReady: documentContext.state === "ready",
      log: state.eventLog.entries,
      greetingSentinelId: GREETING_SENTINEL_ID,
    });
    if (decision === "wait") return;
    if (decision === "skip-and-mark") {
      greetingEmittedRef.current = true;
      return;
    }
    // ``emit`` — first-session greeting.
    greetingEmittedRef.current = true;
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: null,
        payload: {
          kind: "agent_note",
          text: buildIdleGreeting(documentContext.state === "ready" ? documentContext.classification : null),
          commandId: GREETING_SENTINEL_ID,
        },
      },
    });
  }, [documentContext, state.eventLog.entries]);

  // Spec 26 C6 — selection-driven citation lookup (owned by useChatSystem).
  const selectionCitations = chatSelectionCitations;

  // Clickable citation markers in the Word doc. ``citationMarkerWriter``
  // wraps every ``[N]`` token in a tagged inline content control as part of
  // the section write; ``citationMarkerEvents`` subscribes to each control's
  // ``onEntered`` so the cursor entering a marker fires
  // ``selectionCitations.fetchForCitationMarker``. The walker assembles the
  // prose between this marker and the previous one (or the section heading)
  // and pipes it through the same fetch flow the chip uses, so the popover
  // renders identically regardless of trigger.
  useEffect(() => {
    let teardown: (() => Promise<void>) | null = null;
    let cancelled = false;
    void (async () => {
      const { subscribeCitationMarkerEvents } = await import("./services/citationMarkerEvents");
      if (cancelled) return;
      teardown = await subscribeCitationMarkerEvents((event) => {
        void selectionCitations.fetchForCitationMarker({
          sectionNumber: event.sectionNumber,
          controlId: event.controlId,
        });
      });
    })();
    return () => {
      cancelled = true;
      if (teardown) void teardown();
    };
  }, [selectionCitations.fetchForCitationMarker]);

  // "Things want you" count for the pill — sections awaiting
  // approval plus all verdict-flagged items across sections. These
  // are the two distinct writer-attention signals per Spec 23 P8/P9;
  // their sum is what the pill surfaces in one number so the writer
  // doesn't have to decode two counts to decide whether to act.
  const thingsWantYouCount = useMemo(() => {
    const needsApproval = enrichedSections.filter((s) => s.status === "needs_approval").length;
    const flagged = enrichedSections.reduce((sum, s) => sum + (s.flaggedCount ?? 0), 0);
    return needsApproval + flagged;
  }, [enrichedSections]);

  // Clicking the pill body / REVIEW chip: route the writer to the
  // first section that wants them, flip to spine mode so the inspector
  // can open, then acknowledge so the pill dismisses. Falls back to
  // plain acknowledge when nothing actionable is found (shouldn't
  // happen — the pill only renders when count > 0 — but defensive
  // because ``isBack`` and ``count`` are independent signals).
  const handleDigestReview = useCallback(() => {
    const target =
      enrichedSections.find((s) => s.status === "needs_approval") ??
      enrichedSections.find((s) => (s.flaggedCount ?? 0) > 0) ??
      null;
    if (target) {
      if (state.viewMode !== "spine") {
        handleViewModeChange("spine");
      }
      setFocusedSectionId(target.id);
    }
    presence.acknowledge();
  }, [enrichedSections, state.viewMode, handleViewModeChange, presence]);

  // -- Reset to idle (clears run-scoped local state) -----------------------
  // ---- Stop run --------------------------------------------------------
  // Cancel the BACKEND run (releases agent resources) AND reset the
  // shell's UI state. Was a real bug pre-this-commit: handleReset
  // dispatched RESET_TO_IDLE locally but the backend run kept burning
  // tokens silently. cancelRun → DELETE /api/runs/:id is the existing
  // backend stop primitive.
  //
  // What happens after stop:
  //   - SSE stream closes (backend stops emitting)
  //   - Sections that had completed before the stop stay in the doc +
  //     manifest (Phase B will use this for resumption)
  // -- Run control: pause, resume, stop, reset ---------------------------
  const { handlePauseRun, handleResumePausedRun, handleStopRun, handleResyncHydration, markRunRestored } =
    useRunControl({
      runId: state.runId,
      runState: state.state,
      dispatch,
      setStartError,
      setStopWarning,
      setRunSubscriptionStartMode,
      setRunSubscriptionNonce,
      setRestoreInFlight,
      setRestoreHydrationProgressPct,
      setTerminalRunStatus,
      setRunStartedAt,
      setFocusedSectionId,
      scaffoldedRef,
      insertionModeRef,
      enqueueWordWrite,
      resetDraftFlowState,
      setActiveSelection,
      setDocConfig,
      setSectionCommit,
      setPlanData,
      setPlanId,
      setPersistedChatMessages,
      setPersistedSpineSnapshot,
      userIdentifier,
      sections,
      hydrateSections,
      identity,
      sessionLoading,
      bootstrapRunId,
      bootstrapDismissedRunId,
      restoredInsertionMode,
      docConfig,
    });

  // Resolve the focused section to a SpineSection record. The
  // inspector renders only when both are present (id + section data).
  const focusedSection = useMemo(() => {
    if (!focusedSectionId) return null;
    return sections.find((s) => s.id === focusedSectionId) ?? null;
  }, [focusedSectionId, sections]);

  // Scope chip + TOC should only treat inspector focus as authoritative when
  // the inspector context is actually active in spine view. Otherwise, stale
  // focusedSection from earlier spine interactions can pin scope incorrectly.
  // NOTE: this feeds tocActiveSectionNumber (the TOC's own "current section"
  // row highlight) — it deliberately does NOT include tocHoveredSectionNumber
  // below, so browsing the TOC with the mouse doesn't relabel which row is
  // the writer's actual current section.
  const inspectorScopeSectionNumber =
    state.viewMode === "spine" && state.runId && activeSelection && focusedSection ? focusedSection.number : null;

  // Rail-facing inspector value: a TOC hover takes priority over true
  // inspector focus so the scope chip previews the hovered section while
  // the writer browses. Falls back to inspectorScopeSectionNumber the
  // instant the hover ends (mouse leaves the row) or the TOC closes (see
  // the tocOpen-driven reset effect below), at which point resolveScope
  // falls through to the cursor section as before.
  const commandRailInspectorSectionNumber = tocHoveredSectionNumber ?? inspectorScopeSectionNumber;

  // Active section number for TOC highlighting and suggestion chips.
  // Priority: drafting (SSE-driven) > inspector (explicit) > cursor (passive) > first drafted section (fallback).
  // During a live run the agent's current section is most useful to highlight;
  // outside of a run the inspector/cursor context takes over.
  // In post-run, if neither inspector nor cursor has a section, use the first
  // drafted section so suggestion chips have something to work with.
  const firstDraftedSection = useMemo(() => {
    return sections.find((s) => s.status === "drafted" || s.status === "needs_approval" || s.status === "flagged");
  }, [sections]);

  // Single source of truth for "cursor section, with the post-run fallback
  // applied" — the CommandRail chip (and the resolveScope() it runs
  // internally) and the TOC popup's highlight must agree on this value.
  // Previously the CommandRail prop applied the firstDraftedSection
  // fallback below but tocActiveSectionNumber didn't, so the chip could
  // show a section the TOC wouldn't highlight.
  const effectiveCursorSectionNumber = useMemo<string | null>(
    () => cursorSectionNumber ?? (state.state === "post-run" ? (firstDraftedSection?.number ?? null) : null),
    [cursorSectionNumber, state.state, firstDraftedSection],
  );

  const tocActiveSectionNumber = useMemo<string | null>(
    () => state.activeSection?.sectionNumber ?? inspectorScopeSectionNumber ?? effectiveCursorSectionNumber ?? null,
    [state.activeSection, inspectorScopeSectionNumber, effectiveCursorSectionNumber],
  );

  // -- CommandRail content synthesis -------------------------------------
  // Whole-submission denominator for every in-run progress readout (status
  // rail, post-run banner, spine tab chip, chat stats). `docConfig.modules` IS
  // the server's `module_sections` — the exact same set the drafts card's
  // `sections_total` counts — so a run that drafted its selected 10 of a
  // 96-section doc type reads "10/96", matching the Home/Drafts card instead of
  // "10/10". Null (config not yet loaded) ⇒ callers fall back to the scoped
  // spine length, so progress never divides by zero or regresses.
  //
  // For a CUSTOM (build-on-existing-document) run there is no template
  // denominator — the submission IS the writer's own doc — so this returns
  // null and every readout falls back to ``sections.length`` (the doc's own
  // heading count). See ``submissionSectionTotal``.
  const submissionTotalSections = useMemo<number | null>(
    () => submissionSectionTotal(docConfig, runSubmissionMode),
    [docConfig, runSubmissionMode],
  );

  // Single-line "what's the agent doing right now" message. Derived
  // from reducer state + the spine's active section so the rail
  // reads contextually without the rail itself touching state.
  const assistantMessage: string | null = useMemo(() => {
    // # DECISION (2026-04-24, reverted): idle returns null.
    // # WHY: we briefly fed ``chat.lastReply`` into this slot to
    // #      make pre-run chat replies visible. That was wrong —
    // #      lastReply is the FULL agent answer body (often
    // #      multi-paragraph markdown), not a one-line status. The
    // #      rail's assistant-line is a one-line slot designed for
    // #      copy like "Working on §9.2…", not a response body.
    // #      Feeding the full reply in duplicated the timeline row
    // #      AND rendered the markdown as raw text (no markdown
    // #      support in the rail).
    // #
    // #      Correct placement: the timeline's AskWhyDetail is the
    // #      agent-reply surface. It renders markdown (via
    // #      ReactMarkdown + remark-gfm). Errors also land as
    // #      timeline rows via the ``error`` SSE branch in
    // #      handleStreamEvent, so pre-run chat failures are
    // #      visible there too.
    if (state.state === "idle") return null;
    if (state.state === "approval" && state.pendingApproval) {
      const num = state.pendingApproval.sectionNumber;
      return `I stopped at §${num} — your call needed before I continue.`;
    }
    if (state.state === "pausing") {
      const active = sections.find((s) => s.id === activeSectionId);
      if (active) {
        return `Wrapping up §${active.number} ${active.title}, then pausing.`;
      }
      return "Finishing the current module, then pausing at the next safe checkpoint…";
    }
    if (state.state === "paused") {
      return "Run paused. Review the drafted sections or resume when you're ready.";
    }
    if (state.state === "post-run" && state.runSummary) {
      const { draftedSections, totalSections, flaggedSections } = state.runSummary;
      // Numerator stays the truthful spine-drafted count; denominator is the
      // whole submission (falls back to the run-scoped total when unknown).
      const shownTotal = submissionTotalSections ?? totalSections;
      const flagSuffix = flaggedSections > 0 ? ` · ${flaggedSections} flagged for review` : "";
      return `Done. ${draftedSections}/${shownTotal} sections drafted${flagSuffix}.`;
    }
    if (state.state === "mid-run") {
      const active = sections.find((s) => s.id === activeSectionId);
      if (active) {
        return `Working on §${active.number} ${active.title}. Open ⇧⌘P to navigate or pause.`;
      }
      return "Starting your run…";
    }
    return null;
  }, [state.state, state.pendingApproval, state.runSummary, sections, activeSectionId, submissionTotalSections]);

  // Stats string — drafted/total + elapsed wall-clock from
  // runStartedAt. Re-renders every second via the elapsedTick state
  // below; we don't need finer granularity than 1s for "Xm Ys".
  const [elapsedTick, setElapsedTick] = useState(() => Date.now());
  useEffect(() => {
    if (!runStartedAt) return;
    const t = setInterval(() => setElapsedTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [runStartedAt]);

  const stats: string | null = useMemo(() => {
    if (state.state === "idle") return null;
    const drafted = sections.filter((s) => s.status === "drafted").length;
    // Progress against the whole submission, not just the selected scope.
    const total = submissionTotalSections ?? sections.length;
    if (!runStartedAt) return `${drafted}/${total}`;
    const elapsedSec = Math.floor((elapsedTick - runStartedAt) / 1000);
    const m = Math.floor(elapsedSec / 60);
    const s = elapsedSec % 60;
    const elapsed = m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
    return `${drafted}/${total} · ${elapsed}`;
  }, [state.state, sections, runStartedAt, elapsedTick, submissionTotalSections]);

  // -- Command palette command list --------------------------------------
  // Built from current state via the pure registry. Only writers in
  // idle / post-run can flip sandbox mode (server returns 409 mid-run
  // anyway). Settings + Library-approval commands omitted — wiring
  // those needs separate plumbing (lift modal state, route to classic
  // settings) that would expand this commit's scope; they appear as
  // paletteCommands retired 2026-04-23 (Spec 29 palette-merge) —
  // the slash menu catalog in commands/menu.ts is now the single
  // vocabulary. MenuRenderState + buildMenuGroups (computed above
  // as menuGroups) replace the buildCommands(CommandContext) flow.

  // -- Approval popout handlers -------------------------------------------
  // "Mark resolved": writer's call landed; spine moves on. Status
  // back to drafted; reducer flips out of approval. The audit
  // record on disk preserves the verdict that triggered this — no
  // information lost by accepting.
  const handleApprovalAccept = useCallback(() => {
    const sectionId = state.pendingApproval?.sectionId;
    if (sectionId) setSectionStatus(sectionId, "drafted");
    dispatch({ type: "APPROVAL_RESOLVED" });
    // Spec 23 P2.1 — log the approval decision to the EventLog so
    // Timeline shows "Approval accepted · §X" alongside the run's
    // other events. Writer-originated (actor="you"); sectionId
    // pins the entry to the approval's target.
    if (sectionId) {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId,
          sectionLabel: resolveSectionLabel(sectionId),
          runId: state.runId,
          payload: { kind: "approval", decision: "accepted" },
        },
      });
    }
  }, [state.pendingApproval, state.runId, setSectionStatus, resolveSectionLabel]);

  // "Open Review": writer wants to act on the findings. Focus the
  // section + inspector opens; we resolve the popout so the spine
  // is unblocked while the writer works in the inspector.
  const handleApprovalOpenReview = useCallback(() => {
    const sectionId = state.pendingApproval?.sectionId;
    if (sectionId) {
      setFocusedSectionId(sectionId);
      setSectionStatus(sectionId, "drafted");
    }
    dispatch({ type: "APPROVAL_RESOLVED" });
    // Spec 23 P2.1 — log as an "edited" decision: the writer chose
    // to dive into the Review tab rather than simply accept. Same
    // event kind; different decision value. Timeline distinguishes
    // them via formatEventTitle.
    if (sectionId) {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId,
          sectionLabel: resolveSectionLabel(sectionId),
          runId: state.runId,
          payload: { kind: "approval", decision: "edited" },
        },
      });
    }
  }, [state.pendingApproval, state.runId, setSectionStatus, resolveSectionLabel]);

  // Spine ``N⚖`` regulator badge → focus the section + open its Review tab so
  // the persisted regulatory findings render. Bumping the signal (rather than
  // only setting focus) makes the tab switch fire even when the section is
  // already focused.
  const handleOpenReview = useCallback((sectionId: string) => {
    setFocusedSectionId(sectionId);
    setReviewOpenSignal((n) => n + 1);
  }, []);
  // Handle kill confirmation — calls handleStopRun from useRunControl.
  // When drafts were kept, handleStopRun already landed on the post-run
  // confirmation banner in the spine, so we stay there. Only when nothing
  // was drafted do we fall back to the timeline greeting with action buttons.
  const handleConfirmKill = useCallback(async () => {
    setKillConfirming(true);
    try {
      const keptDrafts = await handleStopRun();
      if (!keptDrafts) {
        // No drafts to keep — show the greeting UI after kill.
        handleViewModeChange("timeline");
      }
      setShowKillConfirmation(false);
    } finally {
      setKillConfirming(false);
    }
  }, [handleStopRun, handleViewModeChange]);

  // Auto-clear startError once the writer changes selection (via reset).
  useEffect(() => {
    if (state.state === "idle" && startError && !activeSelection) {
      setStartError(null);
    }
  }, [state.state, startError, activeSelection]);

  // Clear a lingering stop-unconfirmed warning once a fresh run is under way
  // (a new run id + an active mid-run state) — it referred to the prior run.
  useEffect(() => {
    if (stopWarning && state.runId && state.state === "mid-run") {
      setStopWarning(null);
    }
  }, [stopWarning, state.runId, state.state]);

  // Detect image selection — poll for selected images in the document.
  useEffect(() => {
    let intervalHandle: number | null = null;
    const checkImageSelection = async () => {
      try {
        const selected = await isImageSelected();
        setImageSelected(selected);
      } catch {
        // Silent fail — image detection is optional UI enhancement
      }
    };
    checkImageSelection();
    intervalHandle = window.setInterval(checkImageSelection, 500);
    return () => {
      if (intervalHandle !== null) clearInterval(intervalHandle);
    };
  }, []);

  // Navigate to Images tab when "Replace Image" chip is clicked.
  const handleReplaceImage = useCallback(() => {
    dispatch({ type: "VIEW_MODE_SET", mode: "images" });
  }, []);

  // -- Render --------------------------------------------------------------
  const docMeta = deriveDocCaption(state, activeSelection);

  // Library Hub slide-over control — declared early so draft flow
  // handlers can reference it.
  const openSlideOver = useCallback((mode: HubMode) => {
    setHubSlideOverMode(mode);
    setHubSlideOverOpen(true);
  }, []);

  // ---------------------------------------------------------------------
  // Conversational /draft flow handlers (Spec 29 Phase A).
  // Declared BEFORE ``let body`` because the idle body's TimelineView
  // consumes the computed ``draftFlowCard`` prop directly — TS's
  // temporal-dead-zone check enforces the ordering.
  // ---------------------------------------------------------------------
  // Phase 7.2 — orchestrator: try to start a draft with whatever is
  // currently selected. Used both by the /draft slash command (the
  // initial trigger) AND by the picker callbacks (after each
  // standalone pick lands during a pending-draft-start orchestration).
  //
  // Branches:
  //   * Both selectedDocCode + selectedLibraryId set → synthesise
  //     the LoomFreshSelection and kick off handleStartDraft.
  //     Clears pendingDraftStart on success.
  //   * Only one is set → leaves pendingDraftStart true; opens the
  //     picker for the missing piece. The orchestration card in
  //     chat narrates which piece is still needed.
  //   * Neither → opens the doctype picker first (the canonical
  //     starting point for /draft).
  // biome-ignore lint/correctness/useExhaustiveDependencies: openSlideOver intentionally omitted
  const tryAdvanceDraft = useCallback((): void => {
    const lib = libraries.find((l) => l.id === selectedLibraryId);
    const choice = lookupDoctypeChoice(selectedDocCode);
    if (choice && lib) {
      // B_docMeta — start the draft. Defensive: lookupDoctypeChoice
      // returns null when the catalog doesn't recognise the code,
      // so we proceed only when both lookups succeeded.
      const selection = buildLoomFreshSelection(
        {
          docCode: choice.docCode,
          backendDocType: choice.backendDocType,
          docFramework: choice.docFramework,
          docName: choice.docName,
        },
        { id: lib.id, name: lib.name },
        selectedWorkspaceId,
      );
      selection.folderId = pendingFolderIdRef.current;
      setPendingDraftStart(false);
      setDoctypePickerOpen(false);
      launchDraftRef.current?.(selection);
      return;
    }
    if (!choice) {
      // Doctype missing → open DoctypePicker. The orchestration
      // card already shows "What doctype?" via the pendingDraftStart
      // branch in draftFlowCard.
      setDoctypePickerOpen(true);
      return;
    }
    // choice is set but library is missing → open slide-over.
    openSlideOver("library-list");
  }, [selectedDocCode, selectedLibraryId, libraries, selectedWorkspaceId, handleStartDraft]);

  // Spec: Blank-Document Onboarding — checklist callbacks (idle state only).
  // These route through the existing orchestrator, keeping picks separate
  // from the auto-advance trigger (pendingDraftStart).
  // biome-ignore lint/correctness/useExhaustiveDependencies: openSlideOver is stable; selectedLibraryId intentionally unobserved
  const handleChecklistPickDoctype = useCallback(() => {
    setDoctypePickerOpen(true);
    if (!selectedLibraryId) {
      openSlideOver("library-list");
    }
  }, []);

  const handleChecklistPickLibrary = useCallback(() => {
    const mode = selectedLibraryId ? "list" : "library-list";
    openSlideOver(mode);
  }, [selectedLibraryId, openSlideOver]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setPendingDraftStart is stable
  const handleChecklistReviewAndStart = useCallback(() => {
    setPendingDraftStart(true);
    tryAdvanceDraft();
  }, [tryAdvanceDraft]);

  // "Ask about this document" button from ContextCard — focus the composer
  // for conversational chat.
  const handleAskAboutDocument = useCallback(() => {
    commandRailRef.current?.prefillInput("");
  }, []);

  // "Start a fresh draft" — reset picks and launch the guided draft flow.
  // Shared by the HomeScene "＋ New document" CTA and the has-content
  // context card. pendingDraftStart=true arms the orchestration so the
  // picks auto-chain: doctype → library slide-over → section picker →
  // (plan) → run → spine. Without it the flow dead-ends after the doctype
  // pick (handleDoctypePick's advance block is gated on pendingDraftStart).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setPendingDraftStart is stable
  // The folder the writer picked in the "New document" dialog, held across the
  // doctype → library → sections orchestration and injected into the selection
  // that starts the run (useDraftFlow files the doc into it once it exists).
  // Reset each time the flow (re)starts so a stale pick can't leak into it.
  const pendingFolderIdRef = useRef<string | null>(null);

  // Foreign/existing doc: mint a fresh AsthrDocumentId BEFORE any startRun so the
  // backend sees a document with no prior runs and skips the tenancy guard
  // (server/routes/pipeline.py:1847) that otherwise 404s "No document '…'". No-op
  // unless the detector flagged this doc as foreign (foreignDocDetectedRef).
  // Rotation also strips the stale AsthrActiveRunId (wordWriter.ts). The cache-move
  // triad mirrors handleConfirmRotateProject (flush, then move BOTH _cachedDocId and
  // documentSessionId together); NO shell reset here — we're mid doctype→library
  // orchestration and must keep it intact.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters/refs/module fns are stable
  const rotateForeignDocIfNeeded = useCallback(async () => {
    if (!foreignDocDetectedRef.current) return;
    foreignDocDetectedRef.current = false;
    await performForeignDocRotation({
      rotate: rotateDocumentId,
      flush: flushAllPendingSaves,
      setCachedDocId,
      setDocumentSessionId,
      onError: (err) => console.warn("[VariantCShell] rotateForeignDocIfNeeded failed:", err),
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setPendingDraftStart is stable
  const handleContextCardStartFreshDraft = useCallback(
    async (folderId?: string | null) => {
      await rotateForeignDocIfNeeded();
      pendingFolderIdRef.current = folderId ?? null;
      customImportActiveRef.current = false;
      setSelectedDocCode(null);
      setSelectedLibraryId("");
      setPendingDraftStart(true);
      setDoctypePickerOpen(true);
    },
    [rotateForeignDocIfNeeded],
  );

  // Set true while the writer is in the "build on this document" orchestration
  // (doctype → library) so the terminal step registers a CUSTOM run from the
  // doc's existing headings instead of launching a fresh drafting run.
  const customImportActiveRef = useRef(false);

  // Enter the custom-submission orchestration: reuse the fresh-draft doctype →
  // library flow, but flag it so it ends in ``startCustomFlow`` (see the two
  // orchestration seams below).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  const handleStartCustomImport = useCallback(async () => {
    await rotateForeignDocIfNeeded();
    customImportActiveRef.current = true;
    setSelectedDocCode(null);
    setSelectedLibraryId("");
    setPendingDraftStart(true);
    setDoctypePickerOpen(true);
  }, [rotateForeignDocIfNeeded]);

  // Terminal step of the custom-import orchestration: register the custom run
  // from the live document's outline, then load it through the normal snapshot
  // hydration (which renders the spine and, for custom runs, never re-projects
  // the body — see useRunControl's submission_mode guard).
  const startCustomFlow = useCallback(
    async (selection: LoomFreshSelection) => {
      customImportActiveRef.current = false;
      // Light up the floating "Build on this document" progress card. Steps:
      // 0 outline → 1 create project → 2 build spine → 3 load sections. The two
      // internal phases (outline read, run registration) fire through onStep.
      setCustomImportError(null);
      setCustomImportDone(false);
      setCustomImportStep(0);
      const failCustomImport = (message: string, step: number) => {
        setCustomImportStep(step);
        setCustomImportError(message);
      };
      const result = await startCustomSubmission(selection, (phase) => {
        setCustomImportStep(phase === "outline" ? 1 : 2);
      });
      if (!result) {
        // startCustomSubmission surfaces the specific reason in the startError
        // banner; reflect a generic failure on the card so the writer isn't
        // left with a silent spinner.
        failCustomImport("Couldn't build on this document.", 0);
        return;
      }
      const { runId, outline } = result;
      // Stamp the Word doc so a reload maps straight back to THIS run. The
      // custom run was created under ``getCachedDocId()``; the import's doc-id
      // rotation can leave the Word ``AsthrDocumentId`` on a sibling (null-run)
      // id, so on reopen bootstrap reads that id, finds no run, and re-offers
      // "Build on this document" — orphaning the run. Force the Word id back to
      // the run's own doc_id and stamp the run id (recovery + the import
      // detector's fallback probe). Both fail-soft: a stamp failure at worst
      // reproduces the prior reopen behaviour, never blocks the flow.
      const runDocId = getCachedDocId();
      if (runDocId) {
        await saveDocumentId(runDocId).catch((err) =>
          console.warn("[VariantCShell] startCustomFlow: saveDocumentId failed (non-fatal):", err),
        );
      }
      await setActiveRunId(runId).catch((err) =>
        console.warn("[VariantCShell] startCustomFlow: setActiveRunId failed (non-fatal):", err),
      );
      // The spine IS the writer's existing document — render it straight from
      // the outline we just read, so it never depends on the server snapshot
      // round-trip (which, if it doesn't come back tagged custom, would leave
      // handleRunLoaded on the 96-module doctype template skeleton).
      const customSpineFallback = spineSectionsFromOutline(outline);
      // A custom run is registered as COMPLETED with no drafting worker — the
      // spine is the document's existing content, not something being drafted.
      // Mark it terminal + latest-only up front so the SSE hook never subscribes
      // in "history" mode and paints the live-run "Starting the drafting
      // engine…" booting card while handleRunLoaded hydrates.
      setTerminalRunStatus(true);
      setRunSubscriptionStartMode("latest");
      setCustomImportStep(2); // building the spine
      let config = docConfig;
      if (!config || config.document_type !== selection.backendDocType) {
        try {
          config = await getDocTypeConfig(selection.backendDocType);
        } catch (err) {
          console.warn("[VariantCShell] startCustomFlow: docConfig fetch failed:", err);
          failCustomImport("Couldn't load the document type configuration.", 2);
          return;
        }
      }
      setCustomImportStep(3); // loading sections
      await handleRunLoaded(runId, config, {
        libraryId: selection.libraryId,
        libraryName: selection.libraryName,
        workspaceId: selection.workspaceId ?? null,
        docType: selection.backendDocType,
        // Custom import: an empty ``libraryId`` here means the writer skipped
        // the source library. Tag the mode so handleRunLoaded unsets the
        // library instead of restoring a stale one from the doc's saved props.
        submissionMode: "custom",
        // Authoritative client-side spine — used when the server snapshot
        // doesn't come back tagged custom (see handleRunLoaded).
        customSpineFallback,
      });
      // Success frame, then dismiss (the spine has taken over the pane).
      setCustomImportDone(true);
      setCustomImportStep(CUSTOM_IMPORT_STEP_DEFS.length);
      window.setTimeout(() => {
        setCustomImportStep(-1);
        setCustomImportDone(false);
      }, 900);
    },
    // handleRunLoaded / docConfig / startError read at call time; setters stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startCustomSubmission],
  );

  // Phase 9.5 (Bundle A) — shared emitter for ``library_selected``
  // events. Three callsites in the LibraryHub JSX (dropdown change,
  // explicit "Use library", post-create) each emit the same shape;
  // extracted at the rule-of-three point to keep the chat-event
  // semantics in one place. ``created`` flips the chat verb only
  // (Created vs Selected); same payload otherwise.
  const emitLibrarySelected = useCallback(
    (library: { id: string; name: string }, created: boolean, skipSuggestion = false) => {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId: null,
          runId: null,
          payload: {
            kind: "library_selected",
            libraryId: library.id,
            libraryName: library.name,
            created,
          },
        },
      });
      // Phase 9.5 (Bundle B) — post-action suggestion. Skipped when
      // skipSuggestion=true (e.g. /draft consumed the pick) to avoid
      // an intermediate chat flash before the planner opens.
      if (!skipSuggestion) {
        emitSuggestionEvent(suggestPostAction(buildCommandSnapshot({ hasLibrary: true, hasAnyLibrary: true })));
      }
    },
    [buildCommandSnapshot, emitSuggestionEvent],
  );

  // Doctype-pick handler. Shared by:
  //   * Standalone DoctypePicker.onSelect (chip click) →
  //     pendingDraftStart=false → just save the pick.
  //   * Mid-/draft DoctypePicker.onSelect → pendingDraftStart=true
  //     → save then advance the orchestration.
  // biome-ignore lint/correctness/useExhaustiveDependencies: openSlideOver intentionally omitted
  const handleDoctypePick = useCallback(
    (choice: DoctypeChoice) => {
      // Phase 9.5 (Bundle A) — surface the pick in chat as the
      // {writer-said, asthra-acked} tuple before any state mutation
      // so the chat history records the action even if the
      // orchestration short-circuits below. Emitted here (not in the
      // picker) so the picker stays a pure presentation surface.
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId: null,
          runId: null,
          payload: {
            kind: "doctype_selected",
            docCode: choice.docCode,
            label: choice.docName,
          },
        },
      });
      // Phase 9.5 (Bundle B) — post-action suggestion. Snapshot
      // override reflects the just-confirmed doctype so the
      // suggester sees the freshest state without waiting for React.
      emitSuggestionEvent(suggestPostAction(buildCommandSnapshot({ hasDoctype: true })));
      // Changing the doctype to a DIFFERENT value invalidates any
      // library already chosen — libraries are scoped to a doctype, so
      // the writer must re-pick one for the new type. First-time
      // selection (no prior doctype) leaves any earlier library pick
      // intact (the /draft library-first path relies on that).
      const doctypeChanged = !!selectedDocCode && selectedDocCode !== choice.docCode;
      if (doctypeChanged) {
        setSelectedLibraryId("");
      }
      setSelectedDocCode(choice.docCode);
      setDoctypePickerOpen(false);
      if (pendingDraftStart) {
        // The setSelectedDocCode hasn't propagated yet — pass the
        // choice forward by calling tryAdvanceDraft on the next tick
        // would be racy. Instead, inline the same decision here using
        // the just-picked choice (no React state read needed). When the
        // doctype just changed, the prior library no longer applies —
        // skip the auto-start and route to the library picker instead.
        const lib = doctypeChanged ? undefined : libraries.find((l) => l.id === selectedLibraryId);
        if (lib) {
          const selection = buildLoomFreshSelection(
            {
              docCode: choice.docCode,
              backendDocType: choice.backendDocType,
              docFramework: choice.docFramework,
              docName: choice.docName,
            },
            { id: lib.id, name: lib.name },
            selectedWorkspaceId,
          );
          selection.folderId = pendingFolderIdRef.current;
          setPendingDraftStart(false);
          launchDraftRef.current?.(selection);
          return;
        }
        // No library yet — open slide-over for it.
        openSlideOver("library-list");
      }
    },
    [
      pendingDraftStart,
      libraries,
      selectedLibraryId,
      selectedDocCode,
      selectedWorkspaceId,
      handleStartDraft,
      buildCommandSnapshot,
      emitSuggestionEvent,
    ],
  );

  // Library-pick handler used by the LibraryHub when a writer is
  // mid-/draft. Mirror image of the doctype branch in
  // handleDoctypePick: save the pick, and when pendingDraftStart is
  // true, either start the draft (if doctype already set) or open
  // the doctype picker for the missing piece.
  //
  // Returns true when the handoff was consumed by the orchestrator
  // (caller is mid-/draft); false when there's nothing to advance
  // (standalone library-management mode). Lets Hub callbacks branch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pendingDraftStart read inline; adding it would cause stale-closure loops in the orchestration pattern
  const advanceDraftWithLibrary = useCallback(
    (library: { id: string; name: string }): boolean => {
      if (!pendingDraftStart) return false;
      setSelectedLibraryId(library.id);
      // No partial save here — persistence flows through the
      // ``activeSelection`` effect once ``handleStartDraft`` lands the
      // full tuple. Saving library alone here was the source of
      // half-populated session records (see Bug A, 2026-05-07).
      const choice = lookupDoctypeChoice(selectedDocCode);
      if (choice) {
        // Writer-readiness gate added 2026-04-28: don't blast into
        // the run if the library hasn't been ratified for THIS
        // doctype yet. The ratified signal today is "the library
        // has a target_doctype set in libraryDoctypeStore" — set
        // either at create-time (LibraryHub create form) or by an
        // explicit click on the LibraryHub's target-doctype pill.
        // Without it, the source-classification vocabulary
        // doesn't narrow correctly and the writer skipped past
        // every confirmation surface (writer report 2026-04-28
        // on CMC: "selected from the dropdown and went straight
        // to the plan — did not wait for doctyping, output
        // doctype, approve").
        //
        // Auto-ratify: if the library has no recorded doctype yet
        // but the chosen docCode is a valid TargetDoctype, write it
        // silently and proceed — no Hub interruption needed. Only
        // fall back to the slide-over when the docCode is outside
        // the known vocabulary (shouldn't happen in normal flows but
        // guards against custom/future codes).
        const selection = buildLoomFreshSelection(
          {
            docCode: choice.docCode,
            backendDocType: choice.backendDocType,
            docFramework: choice.docFramework,
            docName: choice.docName,
          },
          { id: library.id, name: library.name },
          selectedWorkspaceId,
        );
        selection.folderId = pendingFolderIdRef.current;
        setPendingDraftStart(false);
        launchDraftRef.current?.(selection);
        return true;
      }
      // No doctype yet — open DoctypePicker so writer can complete
      // the missing piece.
      setDoctypePickerOpen(true);
      return true;
    },
    [pendingDraftStart, selectedDocCode, selectedWorkspaceId, handleStartDraft],
  );

  // Skip the library step for a build-on-existing-document (custom) run. A
  // source library is optional for a custom submission — the audit library is
  // bootstrapped from the document's own content — so launch the custom flow
  // with an empty library selection. Only reachable while the custom-import
  // orchestration is active (the slide-over renders the skip action only then).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  const handleSkipLibraryForCustom = useCallback(() => {
    const choice = lookupDoctypeChoice(selectedDocCode);
    if (!choice) {
      // No doctype yet — send the writer to pick it first.
      setHubSlideOverOpen(false);
      setDoctypePickerOpen(true);
      return;
    }
    const selection = buildLoomFreshSelection(
      {
        docCode: choice.docCode,
        backendDocType: choice.backendDocType,
        docFramework: choice.docFramework,
        docName: choice.docName,
      },
      { id: "", name: "" }, // no source library
      selectedWorkspaceId,
    );
    selection.folderId = pendingFolderIdRef.current;
    // Unset the library up front so the chip can't briefly show a stale value
    // between skip and hydration (handleStartCustomImport clears it at flow
    // start; this guards the back-into-picker-then-skip path).
    setSelectedLibraryId("");
    setPendingDraftStart(false);
    setHubSlideOverOpen(false);
    launchDraftRef.current?.(selection);
  }, [selectedDocCode, selectedWorkspaceId]);

  // Terminal draft launcher shared by all three orchestration surfaces
  // (tryAdvanceDraft, handleDoctypePick, advanceDraftWithLibrary). Called
  // once the doctype + library tuple is complete.
  //
  //   * Custom-import flow → startCustomFlow (unchanged).
  //   * A fresh draft while THIS document already holds a run that isn't
  //     actively drafting (post-run / paused) → stash the selection and
  //     open the confirm modal. Confirming saves the current doc, resets
  //     to idle, then re-enters here from idle to show the section picker.
  //     Without this the section step is silently swallowed by the
  //     ``state.state === "idle"`` gate on ``sectionPickerVisible``.
  //   * A run that IS actively drafting (mid-run / approval / pausing)
  //     can't be cleanly saved mid-flight → surface the same "stop first"
  //     guidance the /draft command gives, instead of failing silently.
  //   * Idle → straight into handleStartDraft.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  const launchDraft = useCallback(
    (selection: LoomFreshSelection) => {
      if (customImportActiveRef.current) {
        void startCustomFlow(selection);
        return;
      }
      if (state.state === "post-run" || state.state === "paused") {
        setFreshDraftPending(selection);
        return;
      }
      if (state.state !== "idle") {
        setStartError("A run is in progress. Stop it first, then start a new draft.");
        return;
      }
      // Fresh (template) draft — count progress against the doctype's module
      // total, never a stale "custom" mode left over from a prior build-on run.
      setRunSubmissionMode("standard");
      void handleStartDraft(selection);
    },
    [state.state, startCustomFlow, handleStartDraft],
  );

  // Writer confirmed "Save & start fresh" — persist the current document
  // (the Save-button sequence via autoSaveRef), reset the run to idle, then
  // launch the stashed selection. Ordering is load-bearing: save reads
  // state.runId (still set), then RESET_TO_IDLE clears it, then
  // handleStartDraft re-fetches config and flips sectionPickerActive — by
  // which point the reducer has committed idle so the picker renders.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  const handleConfirmFreshDraft = useCallback(async () => {
    const selection = freshDraftPending;
    if (!selection) return;
    setFreshDraftSaving(true);
    try {
      await autoSaveRef.current?.();
    } catch (err) {
      // handleSave surfaces its own saveError banner; proceed so the
      // writer isn't stranded on the modal.
      console.warn("[VariantCShell] save before fresh draft failed:", err);
    }
    resetShellToIdle();
    setFreshDraftSaving(false);
    setFreshDraftPending(null);
    void handleStartDraft(selection);
  }, [freshDraftPending, resetShellToIdle, handleStartDraft]);

  // Wire launchDraft through the ref the orchestration callbacks call — they
  // are declared above launchDraft, so the ref avoids a TDZ in their deps.
  useEffect(() => {
    launchDraftRef.current = launchDraft;
    return () => {
      launchDraftRef.current = null;
    };
  }, [launchDraft]);

  // is in flight (pendingDraftStart === true). Tells the writer
  // what's still missing. The actual selection happens in the
  // canonical surfaces (DoctypePicker, LibraryHub) which are
  // opened directly by the orchestrator (no effects). When both
  // doctype + library are set this card is null — the orchestrator
  // synthesises the LoomFreshSelection and starts the run.
  const draftFlowCard = useMemo(() => {
    if (!pendingDraftStart) return null;
    const lib = libraries.find((l) => l.id === selectedLibraryId);
    const haveDoctype = !!selectedDocCode;
    const haveLibrary = !!lib;
    if (!haveDoctype && !haveLibrary) {
      return <AsthraBubbleSurface label="Asthra">Let&apos;s pick a doctype first.</AsthraBubbleSurface>;
    }
    if (!haveDoctype) {
      return <AsthraBubbleSurface label="Asthra">{`We have ${lib?.name}. What doctype?`}</AsthraBubbleSurface>;
    }
    if (!haveLibrary) {
      return (
        <AsthraBubbleSurface label="Asthra">
          {`${selectedDocCode} it is. Pick a library and we'll start.`}
        </AsthraBubbleSurface>
      );
    }
    // Both set — transient state while handleStartDraft fires.
    return null;
  }, [pendingDraftStart, selectedDocCode, selectedLibraryId, libraries]);

  // Drafts tab — run-agnostic. Show whenever there's a scope the
  // server can query (doc / workspace / library). Hoisted above the
  // ``body`` composition because the "drafts" branch needs the
  // scope vars; also used below to gate the TabBar's third tab.
  const draftsDocId = getCachedDocId();
  // Workspace scope follows the picker (selectedWorkspaceId), not
  // activeSelection — the latter only updates on run load / draft start,
  // so it goes stale when the user switches workspace mid-session.
  const draftsWorkspaceId = selectedWorkspaceId ?? activeSelection?.workspaceId ?? null;
  const draftsLibraryId = activeSelection?.libraryId ?? selectedLibraryId ?? null;
  // Which workspace's runs the Home / Drafts list shows. ``null`` = "All
  // workspaces" (default) so the writer sees every run they own, regardless of
  // which workspace is bound for drafting. Kept separate from
  // ``selectedWorkspaceId`` (drafting/new-doc scope) so browsing runs never
  // changes where a new draft gets created.
  const [draftsScopeWorkspaceId, setDraftsScopeWorkspaceId] = useState<string | null>(null);
  const showDraftsTab = Boolean(draftsDocId || draftsWorkspaceId || draftsLibraryId);
  // Images tab — run-agnostic, like Drafts. Figures are listed per library, so
  // it shows whenever a library is bound (the same id the pane lists from).
  const imagesLibraryId = draftsLibraryId;
  const showImagesTab = Boolean(imagesLibraryId);
  const imagesLibraryName = imagesLibraryId ? (libraries.find((l) => l.id === imagesLibraryId)?.name ?? null) : null;

  // After a Drafts-panel "Load" wipes + reprojects a run into the live
  // doc, sync the side-panel state with the loaded run: reset to idle so
  // RUN_RESTORED is a legal transition, refetch the spine, refetch
  // document chat history, then dispatch RUN_RESTORED so the spine
  // view reflects the run now in the body. The chat refetch is what
  // makes the conversational context of the loaded run reappear — the
  // hook re-seeds when ``persistedChatMessages`` changes and the new
  // ``state.runId`` arrives via RUN_RESTORED.
  const handleRunLoaded = useCallback(
    async (loadedRunId: string, config: DocTypeConfig, context?: RunLoadContext) => {
      const docId = draftsDocId;
      // Rehydrate the shell's doc-type config from the run that was just
      // loaded — Load works after Reset (when docConfig is null), and the
      // spine/inspector/further-drafting all read from docConfig.
      setDocConfig(config);

      // A Drafts "Load" can replace the current document with a different
      // run's .docx. Restore selection context so library + doctype chips
      // reflect the loaded run immediately — preferring the run row's own
      // context (passed by DraftsPanel) over the Word custom-prop
      // round-trip, whose writes are best-effort and can fail on Word web
      // right after a whole-body replace.
      // Custom import that skipped the source library: unset the library
      // explicitly rather than falling through to loadDocSelectionProps(),
      // which would resurrect a stale library from the Word custom prop (a
      // prior run on the same doc). Keep the doctype so its chip resolves; the
      // activeSelection persist effect then writes library_id="" to the session
      // doc + Word props, so a later reload/bootstrap finds no library.
      if (context?.submissionMode === "custom" && !isValidLibraryId(context?.libraryId) && context?.docType) {
        const docCode = context.docType;
        setActiveSelection(
          buildCustomSkipSelection({
            docCode,
            docName: config.display_name ?? lookupDoctypeChoice(docCode)?.docName ?? docCode,
            docFramework: lookupDoctypeChoice(docCode)?.docFramework,
            workspaceId: context.workspaceId ?? null,
          }),
        );
        setSelectedLibraryId("");
        setSelectedDocCode(docCode);
      } else
        try {
          let libraryId: string | null = null;
          let libraryName: string | null = null;
          let workspaceId: string | null = null;
          let docCode: string | null = null;
          let docName: string | null = null;
          let backendDocType: string | null = null;
          if (isValidLibraryId(context?.libraryId) && context?.docType) {
            libraryId = context.libraryId;
            libraryName = context.libraryName || context.libraryId;
            workspaceId = context.workspaceId ?? null;
            docCode = context.docType;
            backendDocType = context.docType;
            docName = config.display_name || null;
          } else {
            const saved = await loadDocSelectionProps();
            if (isValidLibraryId(saved?.libraryId) && saved?.libraryName && saved.docCode && saved.backendDocType) {
              libraryId = saved.libraryId;
              libraryName = saved.libraryName;
              workspaceId = saved.workspaceId ?? null;
              docCode = saved.docCode;
              backendDocType = saved.backendDocType;
              docName = saved.docName ?? null;
            }
          }
          if (libraryId && libraryName && docCode && backendDocType) {
            const resolvedDocName = docName ?? lookupDoctypeChoice(docCode)?.docName ?? docCode;
            const resolvedDocFramework = lookupDoctypeChoice(docCode)?.docFramework;
            const normalizedDocFramework = resolvedDocFramework ?? undefined;

            setActiveSelection({
              workspaceId,
              libraryId,
              libraryName,
              docCode,
              docName: resolvedDocName,
              docFramework: normalizedDocFramework,
              backendDocType,
            });
            // Seed the standalone picker state too. The library chip's value is
            // ``libraries.find(l => l.id === selectedLibraryId)?.name`` and the
            // doctype chip falls back to ``selectedDocCode`` — neither reads
            // ``activeSelection`` directly — so without these two setters the
            // chips stay on their "pick a…" placeholders even after a Load that
            // restored ``activeSelection``. Mirrors useSessionBootstrap.
            setSelectedLibraryId(libraryId);
            setSelectedDocCode(docCode);

            if (docId) {
              scheduleSave(docId, {
                workspace_id: workspaceId,
                library_id: libraryId,
                library_name: libraryName,
                doc_code: docCode,
                doc_name: resolvedDocName,
                doc_framework: resolvedDocFramework ?? null,
                backend_doc_type: backendDocType,
              });
            }
          }
        } catch (err) {
          console.warn("[VariantCShell] handleRunLoaded: selection restore failed (non-fatal):", err);
        }
      // Build from a fresh config skeleton, not the current in-memory
      // sections. Drafts Load can target an older run while the shell is
      // still showing a newer run's drafted rows; starting from ``sections``
      // would leak those stale statuses into the loaded run until refresh.
      const baseSections = spineSectionsFromConfig(config);

      // Prefer the loaded run's own snapshot as the authority for row data.
      // The doc-level spine endpoint is only a fallback because it can lag or
      // describe a different run for the same document session.
      //
      // The fallback is now requested ONLY when the snapshot fails, where
      // previously both went out in parallel. GET /sessions/{docId}/spine
      // answers 404 for any document without draft-run history — correct
      // server behaviour, but firing it unconditionally meant a 404 on
      // every load even when the snapshot had already answered. The
      // browser logs a failed request to the console whether or not the
      // code handles the rejection, so not making the call is the only
      // way to keep the console clean.
      //
      // Cost: the fallback is sequential rather than parallel now, so the
      // path where the snapshot fails is one round-trip slower. That path
      // is the exception, and it was already the slow one.
      let nextSections = baseSections;
      const snapshot = await getRunSnapshot(loadedRunId).catch((err) => {
        console.warn("[VariantCShell] handleRunLoaded: getRunSnapshot failed (non-fatal):", err);
        return null;
      });
      if (snapshot && snapshot.submission_mode === "custom") {
        // Custom submission: the spine IS the existing document's heading
        // outline, in document order — bypass the template-config merge and
        // number-sort (mapSpineDTO) and take the server rows verbatim. Server
        // rows are preferred over the client fallback because they carry the
        // persisted template match (matchedModuleId/Title/matchResolved).
        nextSections = mapCustomSpineDTO(snapshot);
      } else if (context?.submissionMode === "custom" && context.customSpineFallback?.length) {
        // Custom submission whose server snapshot didn't come back tagged
        // custom (null snapshot / cold start / dropped field / deploy lag).
        // Render the spine from the client-side outline the import already read
        // — green where the heading has content — instead of silently falling
        // back to the 96-module doctype template skeleton below. Placed BEFORE
        // the generic snapshot branch so reconcileSpineWithRunStatus (which is
        // keyed to template module ids) never runs against CUSTOM-N rows.
        nextSections = context.customSpineFallback;
      } else if (snapshot) {
        nextSections = mapSpineDTO(
          {
            doc_id: snapshot.doc_id,
            run_id: snapshot.run_id,
            run_status: snapshot.status,
            spine: snapshot.spine,
          },
          nextSections,
        );
        // Reconcile against the authoritative run status so sections in
        // completed_module_ids are always shown as drafted, even when the
        // spine DTO snapshot lags behind the backend.
        nextSections = reconcileSpineWithRunStatus(nextSections, snapshot);
      } else if (docId) {
        const spine = await fetchSpine(docId).catch((err) => {
          console.warn("[VariantCShell] handleRunLoaded: fetchSpine fallback failed (non-fatal):", err);
          return null;
        });
        if (spine) {
          nextSections = mapSpineDTO(spine, nextSections);
        }
      }
      // Record the run's submission mode so the progress denominator counts
      // against the right total: a custom run against its own heading count
      // (submissionTotalSections → null → falls back to sections.length), a
      // template run against the doctype's module total.
      const isCustomRun = snapshot?.submission_mode === "custom" || context?.submissionMode === "custom";
      setRunSubmissionMode(isCustomRun ? "custom" : "standard");
      hydrateSections(nextSections);
      if (docId) {
        try {
          const page =
            (await fetchRunChat(loadedRunId, { limit: CHAT_PAGE_SIZE })) ??
            (await fetchDocumentChat(docId, { limit: CHAT_PAGE_SIZE }));
          setPersistedChatMessages(page?.messages ?? []);
          setPersistedChatHasMore(page?.hasMore ?? false);
        } catch (err) {
          console.warn("[VariantCShell] handleRunLoaded: chat fetch failed (non-fatal):", err);
        }
      }
      // Sync the auto-restore dedupe so the run-restore effect in
      // useRunControl doesn't re-pick this same run id after
      // hydrateSections changes sections.length and re-fires it. Without
      // this, the effect can trigger a second loadRunIntoLiveDoc round —
      // another body-clear window in which the empty-doc watcher can pop
      // the rotate-project modal and (if confirmed) null docConfig,
      // leaving the Drafts panel Load buttons permanently disabled.
      // markRunRestored also clears the in-memory dismissal for this run;
      // clear the persisted marker too so a deliberate Load-then-reload of
      // a previously-reset run isn't re-suppressed.
      markRunRestored(loadedRunId);
      // The body now holds the loaded run — re-classify the document so
      // the mount-time "empty" verdict (a brand-new Word doc) can't route
      // a later idle transition to the full-screen Home scene. Deferred
      // until the live-doc settle window closes (spine "Load doc" brackets
      // beginLiveDocLoad across this call); runs immediately otherwise.
      deferUntilLiveDocSettled("document-context-refresh", () => {
        setDocumentContextNonce((n) => n + 1);
      });
      if (docId) {
        // Persist the loaded run id so bootstrap restore chooses this run
        // on refresh instead of a stale previously-saved run_id.
        scheduleSave(docId, {
          run_id: loadedRunId,
          dismissed_run_id: null,
        });
      }
      if (state.state !== "idle") {
        dispatch({ type: "RESET_TO_IDLE" });
      }
      dispatch({ type: "RUN_RESTORED", runId: loadedRunId });
      // Transition to the correct post-load state based on the run's actual
      // status. Without this, the shell stays stuck in "mid-run" indefinitely
      // for completed runs and the SSE subscription fires against an evicted
      // Redis stream.
      if (snapshot) {
        const runStatus = snapshot;
        const toPostRun = restoresToPostRun(runStatus);
        const toIdle = restoresToIdle(runStatus);
        if (toPostRun || toIdle) {
          // Prevent the SSE hook from subscribing — the Redis stream for a
          // terminal run is likely evicted and a subscribe attempt 404s.
          setTerminalRunStatus(true);
        }
        if (toPostRun) {
          // Completed — OR failed/cancelled with at least one drafted
          // section (same rule as the bootstrap restore in useRunControl;
          // see restoresToPostRun). The old "any terminated run → idle"
          // branch here wiped runId after a successful Load, and on a
          // document that was blank at mount the idle shell renders the
          // full-screen Home scene — the body was full but the pane went
          // Home instead of the spine.
          //
          // Tally from the section list hydrateSections just received —
          // the same list the spine footer and tab chip count — instead
          // of the run-scoped, drift-prone modules_total/modules_completed
          // scalars that made the rail say "Done. 7/7" against a "2/96"
          // footer after loading a run from the Drafts tab.
          dispatch({
            type: "RUN_COMPLETED",
            summary: deriveRunSummaryFromSpine(nextSections, runStatus.elapsed_seconds),
          });
          if (runStatus.status === "failed") {
            dispatch({ type: "RUN_FAILED", error: RUN_FAILED_AFTER_DRAFTING_ERROR });
          }
        } else if (toIdle) {
          // Terminated with nothing drafted — there is no run surface to
          // show, so idle (and Home on a blank doc) is the honest state.
          dispatch({ type: "RESET_TO_IDLE" });
        }
      } else if (context?.submissionMode === "custom") {
        // Custom run whose snapshot was unavailable (null / cold start): the
        // spine still rendered from the client fallback above, and the run was
        // registered COMPLETED with no worker. Surface the completed post-run
        // state — tallied from that fallback spine — so the "Run complete ·
        // N/M drafted" banner and status rail agree with the green spine
        // instead of being silently skipped by the ``if (snapshot)`` gate.
        setTerminalRunStatus(true);
        dispatch({
          type: "RUN_COMPLETED",
          summary: deriveRunSummaryFromSpine(nextSections),
        });
      }
      // Land on the populated spine. A Drafts-tab Load otherwise leaves
      // viewMode on "drafts" (RUN_RESTORED/RUN_COMPLETED never touch it),
      // so the writer sees no sign the spine hydrated. No-op when already
      // on the spine (the spine Load-doc path).
      dispatch({ type: "VIEW_MODE_SET", mode: "spine" });
    },
    [draftsDocId, hydrateSections, state.state, setPersistedChatMessages, setPersistedChatHasMore, markRunRestored],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: insertionModeRef read is intentionally non-reactive
  const handleLoadDraftedDocument = useCallback(async () => {
    console.log(
      `[load-draft] handleLoadDraftedDocument invoked insertionMode=${insertionModeRef.current} runId=${state.runId ?? "none"} docConfigPresent=${docConfig !== null}`,
    );
    // Both insertion modes now rebuild the live doc IN PLACE (fetchRunDocument →
    // hydrateLiveDocFromBase64, per-section loadRunIntoLiveDoc fallback below).
    // Previously section-mode routed to an external browser window instead —
    // that predates the post-run "Load doc" button being exposed for section
    // mode, where an inline re-pull (reconcile the body with the server's
    // canonical version, or restore it after a clear) is what the writer wants.
    // The rebuild only fires from the post-run banner (run complete; the banner
    // is suppressed mid-draft), so it can't clobber a still-streaming body.
    if (!state.runId || !docConfig) {
      console.log(
        `[load-draft] ABORTED missing prerequisite runIdPresent=${!!state.runId} docConfigPresent=${!!docConfig}`,
      );
      setStartError("Couldn't load drafted document into Word. Run id or document config is missing.");
      return;
    }
    // Prevent concurrent invocations (double-click, rapid re-render, or
    // auto-restore re-trigger) from running simultaneous load+sync sequences.
    if (loadDraftInFlightRef.current) {
      console.log("[load-draft] already in flight — skipping");
      return;
    }
    loadDraftInFlightRef.current = true;
    // A full Load rebuilds every section, so any pending/failed per-section
    // "loading into Word…" trackers are now moot — clear them.
    setLiveDocLoadBySection(() => new Map());
    setLoadDraftInFlight(true);
    // Hold the live-doc "loading/settling" guard across the WHOLE load window —
    // hydrate + handleRunLoaded + _waitForDocumentLoaded — not just the insert.
    // Balanced in the finally after the settle wait. Nests with the loader's own
    // begin/end (ref-counted), so background re-projection stays deferred until
    // the freshly loaded doc has actually settled.
    beginLiveDocLoad();
    // Kick off the floating LoadProgressCard: step 0 (Preparing), clear any
    // stale error/done from a prior attempt.
    setLoadDocStepIndex(0);
    setLoadDocError(null);
    setLoadDocDone(false);
    emptyDocWatcherRef.current?.acknowledge();
    pauseSync();
    const loadStartedAt = Date.now();
    // Flipped true once the draft is in Word AND the success-path steps
    // completed (either the main path or the per-section fallback). Drives the
    // terminal "Loaded ✓" frame in the finally block.
    let loadSucceeded = false;
    // Custom (build-on-existing-document) runs take a dedicated Load path — the
    // whole-file hydrate would insert a doc-type rebuild that can't place the
    // writer's ``CUSTOM-N`` sections (blank doc), and the template per-section
    // fallback would orphan every one of them. Remembered so the catch below
    // never runs the destructive template fallback on a custom run.
    let isCustomRun = false;
    try {
      rememberRunRecoveryHint(state.runId, "shell.load-drafted-document");
      setLoadDocStepIndex(1); // Downloading your draft
      // One snapshot fetch decides the path AND (for custom) supplies the spine
      // (real number/title per CUSTOM-N id). Non-fatal on failure → template path.
      const loadSnapshot = await getRunSnapshot(state.runId).catch((err) => {
        console.warn("[load-draft] getRunSnapshot failed (non-fatal):", err);
        return null;
      });
      isCustomRun = loadSnapshot?.submission_mode === "custom";
      if (isCustomRun) {
        setLoadDocStepIndex(2); // Rebuilding in Word (custom per-section)
        const customSpine = (loadSnapshot?.spine ?? []).map((s) => ({
          id: s.id,
          number: s.number,
          title: s.title,
          isTitle: s.is_title ?? undefined,
        }));
        console.log(`[load-draft] custom submission — loadCustomRunIntoLiveDoc rows=${customSpine.length}`);
        const customResult = await loadCustomRunIntoLiveDoc(state.runId, customSpine);
        console.log(
          `[load-draft] loadCustomRunIntoLiveDoc done sectionsWritten=${customResult.sectionsWritten}/${customResult.sectionsAttempted}`,
        );
      } else {
        // Prefer the canonical run-scoped .docx, read THROUGH DocService — the
        // document read/write API (Mongo stays the SSOT behind it) — then import
        // whole into Word. Fallback to section replay only when a run document is
        // unavailable.
        console.log(`[load-draft] fetchRunDocument starting runId=${state.runId}`);
        const blob = await fetchRunDocument(state.runId, { includeProvenance: false });
        setLoadDocStepIndex(2); // Rebuilding in Word (whole-file hydrate or per-section fallback)
        if (blob) {
          console.log(
            `[load-draft] fetchRunDocument returned blob sizeBytes=${blob.size} type=${blob.type || "unknown"}`,
          );
          const base64 = await blobToBase64(blob);
          console.log(`[load-draft] blobToBase64 done base64Length=${base64.length}`);
          // Pass the shared style spec so built-in styles (Normal, Heading 1-3)
          // are normalized to the server's before the insert — matching the
          // DraftsPanel Load path, so a spine Load renders with the same styles
          // and title as the download rather than drifting. The skeleton feeds
          // heading-number recovery + the native-numbering rebuild (hydrate
          // STEP3/3.5) when Word web drops the list numbering.
          const hydrateResult = await hydrateLiveDocFromBase64(base64, {
            styleSpec: docConfig.live_doc_style_spec,
            skeleton: docConfig.skeleton,
            headingNumbering: docConfig.heading_numbering,
          });
          console.log(
            `[load-draft] hydrateLiveDocFromBase64 done sectionsWritten=${hydrateResult.sectionsWritten} bookmarksPlanted=${hydrateResult.bookmarksPlanted} plantedSections=${hydrateResult.plantedSections.join(",") || "none"}`,
          );
        } else {
          console.log(
            `[load-draft] fetchRunDocument returned null (404 — no bundle yet) — falling back to loadRunIntoLiveDoc runId=${state.runId}`,
          );
          const fallbackResult = await loadRunIntoLiveDoc(state.runId, docConfig);
          console.log(
            `[load-draft] loadRunIntoLiveDoc done sectionsWritten=${fallbackResult.sectionsWritten} scaffolded=${fallbackResult.scaffolded}`,
          );
        }
      }
      setLoadDocStepIndex(3); // Restoring session (spine + state hydration)
      await handleRunLoaded(state.runId, docConfig);
      console.log(`[load-draft] handleRunLoaded done elapsedMs=${Date.now() - loadStartedAt}`);
      setLoadDocStepIndex(4); // Loading the spine (sync-engine startup)
      // The draft is now in Word for this run — mark the document ready so sync is
      // allowed (and stays allowed across reopens), then start the engine. It's
      // still paused (line ~2210) so no cycle fires against the settling body; the
      // finally block's resumeSync(runId) below unpauses + rebaselines the draft.
      // Done only on the SUCCESS path: a failed load never enables sync, so the
      // pre-load placeholder is never pushed.
      await markSyncReady(state.runId);
      // The drafted document is now in Word — the live document owns it again.
      // Flip the ownership pointer back to "live" so sync is allowed (and stays
      // allowed across a reopen) after the draft-start handler set it "server".
      await setDraftPointer("live");
      startSyncEngineForCurrentRun();
      loadSucceeded = true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.log(`[load-draft] FAILED elapsedMs=${Date.now() - loadStartedAt} error="${message}"`);
      // A custom run has no template fallback: loadCustomRunIntoLiveDoc is the
      // only path that can place its CUSTOM-N sections, and it already force-
      // cleared. Running loadRunIntoLiveDoc here would scaffold the doc-type
      // skeleton and orphan every section — re-blanking the doc. Surface the
      // error instead (its own guard also aborts before clearing when the SSOT
      // is all-placeholder, protecting the writer's live content).
      if (isCustomRun) {
        console.log(`[load-draft] custom run — no template fallback; surfacing error runId=${state.runId}`);
        setStartError(`Couldn't load drafted document: ${message}`);
        setLoadDocError(`Couldn't load drafted document: ${message}`);
        return;
      }
      // The whole-file hydrate clears the body BEFORE inserting, so a
      // mid-insert failure (Word web degrades across repeated Replace
      // inserts) leaves an EMPTY live doc — the worst outcome. Mirror the
      // DraftsPanel Load path: rebuild per-section from the backend SSOT so
      // the writer still gets content, then complete the success-path steps.
      try {
        console.log(`[load-draft] attempting per-section fallback after whole-file failure runId=${state.runId}`);
        setLoadDocStepIndex(2); // Rebuilding in Word (per-section replay)
        const fallbackResult = await loadRunIntoLiveDoc(state.runId, docConfig);
        console.log(
          `[load-draft] fallback loadRunIntoLiveDoc done sectionsWritten=${fallbackResult.sectionsWritten} scaffolded=${fallbackResult.scaffolded}`,
        );
        setLoadDocStepIndex(3); // Restoring session
        await handleRunLoaded(state.runId, docConfig);
        setLoadDocStepIndex(4); // Loading the spine
        await markSyncReady(state.runId);
        await setDraftPointer("live");
        startSyncEngineForCurrentRun();
        loadSucceeded = true;
      } catch (fallbackErr) {
        const fallbackMessage = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
        console.log(`[load-draft] per-section fallback ALSO failed error="${fallbackMessage}"`);
        setStartError(`Couldn't load drafted document: ${message}`);
        // Surface on the floating card too, frozen on the step that failed.
        setLoadDocError(`Couldn't load drafted document: ${message}`);
      }
    } finally {
      // Poll Word until the loaded document is actually rendered (paragraph
      // count > 1) before re-enabling sync.  A fixed delay is unreliable —
      // large documents can take longer than 3 s to insert.  We check every
      // 500 ms, give up after 15 s, then resume regardless so the engine
      // isn't stuck paused forever.
      const waitStartedAt = Date.now();
      await _waitForDocumentLoaded(15_000);
      console.log(`[load-draft] _waitForDocumentLoaded settled waitedMs=${Date.now() - waitStartedAt}`);
      // Pass runId so resumeSync re-seeds from DocService's current blocks
      // instead of reconciling the next cycle against a blank baseline —
      // otherwise every reload mints fresh block_ids for content DocService
      // already has stored, orphaning the previous generation (its deletion
      // is never computed since the engine has no record it existed).
      resumeSync(state.runId);
      loadDraftInFlightRef.current = false;
      setLoadDraftInFlight(false);
      // Settle window is over — release the guard and drain any re-projection
      // that was deferred while the doc was loading.
      endLiveDocLoad();
      if (loadSucceeded) {
        // Terminal "Loaded ✓" frame, then auto-dismiss — mirrors handleSave.
        setLoadDocStepIndex(LOAD_STEP_DEFS.length);
        setLoadDocDone(true);
        setTimeout(() => {
          setLoadDocStepIndex(-1);
          setLoadDocDone(false);
        }, 1500);
      }
      // On failure the index stays frozen on the failed step and loadDocError
      // is set, so the card holds until the writer dismisses it.
      console.log(`[load-draft] handleLoadDraftedDocument complete totalElapsedMs=${Date.now() - loadStartedAt}`);
    }
  }, [state.runId, docConfig, handleRunLoaded, startSyncEngineForCurrentRun]);

  const handleOpenDocument = useCallback(() => {
    if (!state.runId) {
      setStartError("Couldn't open document. Run id is missing.");
      return;
    }
    try {
      rememberRunRecoveryHint(state.runId, "shell.open-document");
      const url = runDocumentUrl(state.runId, false);
      console.info("[VariantCShell/OpenDocument] click", {
        runId: state.runId,
        includeProvenance: false,
        url,
      });
      openExternal(url);
      console.info("[VariantCShell/OpenDocument] dispatched", {
        runId: state.runId,
      });
    } catch (err) {
      console.error("[VariantCShell/OpenDocument] failed", {
        runId: state.runId,
        error: err instanceof Error ? err.message : String(err),
      });
      setStartError(`Open document failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [state.runId]);

  // Inspector drawer — shared across every spine surface (idle restored
  // run AND mid-run / approval / post-run). Previously this JSX lived only
  // inside the run-active branch, so a restored run that landed in "idle"
  // with a populated spine showed the rows but had NOTHING to consume the
  // ``focusedSectionId`` set on row click → "clicking a section does
  // nothing". Hoisting it here, gated only on the two things DraftTab's
  // ``getSectionContent(runId, section.id)`` actually needs (a focused
  // section + a runId), fixes idle and tolerates a null ``activeSelection``
  // (docCode degrades to citations-unavailable rather than blocking).
  const inspectorDocCode = activeSelection?.docCode ?? selectedDocCode ?? "";

  // "Resolve with asthra" from the Issues tab — drop the writer into the chat
  // with the issue's paragraph highlighted as context (exactly like manually
  // selecting a paragraph and telling asthra what to do), AND pre-fill the
  // composer with an issue-typed instruction (buildResolveInstruction): a
  // missing-info sweep for data gaps, or a resolve-this-specific-flag directive
  // carrying the finding's own context + recommended fix for regulatory items.
  // The instruction is seeded, not auto-sent — the writer reviews and presses
  // send, then asthra's rewrite lands the edit and the backend records the
  // issue as resolved-by-asthra.
  // Shared tail of "Resolve with asthra": show the chat, capture the Word
  // highlight into the selection pill, and pre-fill the instruction. The
  // writer reviews it and presses Send; normal intent routing takes over.
  const seedComposerWithSelection = useCallback(
    async (instruction: string) => {
      handleViewModeChange("timeline");
      await captureSelection();
      commandRailRef.current?.prefillInput(instruction);
    },
    [captureSelection, handleViewModeChange],
  );

  const handleResolveGapWithAsthra = useCallback(
    (issue: SectionIssue) => {
      // Resolve the section from the issue's own module_id so this handler
      // serves both the per-section inspector and the submission-wide Issues
      // tab (where nothing is focused). Every SectionIssue carries module_id.
      const sec = sections.find((s) => s.id === issue.module_id) ?? null;
      if (!sec) return;
      // Switch to the chat view so the selection pill + input are visible.
      handleViewModeChange("timeline");
      void (async () => {
        // Highlight the marker's whole paragraph in Word (fall back to the
        // section heading when the marker can't be located), then capture it
        // into the chat selection-context pill.
        const found = await selectMarkerParagraph(issue.marker_text);
        if (!found) await navigateToSection(sec.number);
        await seedComposerWithSelection(buildResolveInstruction(issue));
      })();
    },
    [sections, seedComposerWithSelection, handleViewModeChange],
  );

  // "Resolve missing info" chip — the same Resolve-with-asthra flow for the
  // writer's own highlight (already selected in Word, so no paragraph
  // selection step): the missing-info instruction is pre-filled for review.
  const handleResolveMissingInfoChip = useCallback(() => {
    void seedComposerWithSelection(MISSING_INFO_INSTRUCTION);
  }, [seedComposerWithSelection]);

  // A source library is required to draft anything — Asthra only acts on
  // trusted source data (mirrors the chat gate). For a custom submission that
  // skipped the library step this is false, so drafting stays disabled until a
  // library is attached.
  const hasSourceLibrary = useMemo(
    () => isValidLibraryId(activeSelection?.libraryId) || isValidLibraryId(selectedLibraryId),
    [activeSelection?.libraryId, selectedLibraryId],
  );
  // Custom submission: whether the focused section can be drafted (it needs a
  // saved instruction — there's no template instruction to fall back on — and a
  // source library). Standard runs are always draftable (template-driven).
  const isCustomRun = runSubmissionMode === "custom";
  const focusedCustomHasInstruction = Boolean(focusedSection?.instruction?.trim());
  // Whether ANY custom section carries an instruction — gates the post-run
  // "Draft more sections" button (nothing to draft until at least one does).
  const anyCustomSectionHasInstruction = useMemo(
    () => (isCustomRun ? sections.some((s) => Boolean(s.instruction?.trim())) : false),
    [isCustomRun, sections],
  );

  const inspectorNode =
    focusedSection && state.runId ? (
      <Inspector
        section={focusedSection}
        runId={state.runId}
        docCode={inspectorDocCode}
        identity={identity}
        openReviewSignal={reviewOpenSignal}
        issuesRefreshSignal={issuesRefreshSignal}
        actionsRefreshSignal={actionsRefreshSignal}
        auditActivityLines={agentActivityBySection.get(focusedSection.id) ?? EMPTY_ACTIVITY_LINES}
        onResolveGapWithAsthra={handleResolveGapWithAsthra}
        isCustomSubmission={runSubmissionMode === "custom"}
        onInstructionSaved={(sectionId, instruction) => setSectionInstruction(sectionId, instruction)}
        onClose={() => setFocusedSectionId(null)}
        onRegenerate={
          regenEnabled &&
          (focusedSection.status === "drafted" ||
            focusedSection.status === "needs_approval" ||
            focusedSection.status === "flagged")
            ? handleInspectorRegenerate
            : undefined
        }
        onGenerate={
          regenEnabled &&
          focusedSection.status !== "drafted" &&
          focusedSection.status !== "needs_approval" &&
          focusedSection.status !== "flagged" &&
          // Custom submission: the spine is the writer's OWN heading outline with
          // no doctype-template instruction to draft from, so Generate lights up
          // only once the writer has authored an instruction for this section
          // (Instructions tab) AND a source library is attached. Standard runs
          // are template-driven and always draftable.
          (isCustomRun ? focusedCustomHasInstruction && hasSourceLibrary : true)
            ? () => {
                // Close the Inspector before drafting begins, mirroring the
                // Regenerate flow (useRegenFlow.handleRegenConfirm), which
                // clears focusedSectionId once its run kicks off.
                setFocusedSectionId(null);
                void handleGenerateSection(focusedSection.id);
              }
            : undefined
        }
        isRegenInProgress={isAnyRegenInProgress}
      />
    ) : null;

  let body: React.ReactNode;
  // Plan-review takes precedence over idle picker — once the writer
  // has clicked Start Draft and we're either fetching or have a plan
  // in hand, the picker is no longer the right surface. The reducer
  // stays at "idle" because no run is in flight yet; the inline
  // pre-spine view sits in this gap honestly.
  // Plan-review activates either via the fresh-draft entry path
  // (state still idle, picker → plan flow) OR via the Phase 9.4
  // "Draft more sections" post-run flow (draftingMore explicitly
  // set by the writer; planData carried over from the prior run).
  // Both render the same LoomPlanReview component; the difference
  // is whether ``lockedModuleIds`` is pre-seeded.
  const planReviewActive =
    (state.state === "idle" && !!activeSelection && (planLoading || !!planData || !!planError)) ||
    (draftingMore && !!activeSelection && !!planData);

  // Section-picker takes precedence over LoomFresh but yields to plan-
  // review — it's the surface between "doc + library chosen" and "plan
  // requested / started". Cleared on commit (→ plan or run) or cancel
  // (→ back to LoomFresh).
  const sectionPickerVisible =
    (state.state === "idle" || (state.state === "post-run" && draftingMore)) &&
    !!activeSelection &&
    sectionPickerActive &&
    !planLoading &&
    !planData &&
    !planError;

  // Memoised selection tree + gate meta — derived from docConfig so
  // LoomSelection's useMemo boundaries don't re-fire when unrelated
  // shell state changes.
  // The writer picked which eCTD leaf this document IS.
  //
  // The identity is PERSISTED, not just held in state: the draft view, the
  // renderer and the sibling lookup all read it back off the document record,
  // and a document that never got one renders the whole doctype with no reason
  // to re-ask. A failure is surfaced rather than swallowed — a silently-lost
  // identity is indistinguishable from a stored one.
  //
  // The server does not take the id on trust: `server/leaf_identity.py` stores
  // it only when the doctype declares that leaf, and declines otherwise.
  const handleLeafSelect = useCallback(
    (nextLeafId: string) => {
      setLeafDocumentId(nextLeafId);
      // Ticks belonging to the leaf just left would be sent to the run and
      // drafted into the wrong document.
      setSectionCommit(null);
      if (!documentSessionId) return;
      void upsertDocumentSession(documentSessionId, leafIdentityPatch(nextLeafId, null)).catch((err: unknown) => {
        console.error("[VariantCShell] could not record the leaf identity", err);
      });
    },
    [documentSessionId],
  );

  // The config the pickers see: narrowed to the leaf this document IS.
  // Narrowing the CONFIG (rather than filtering rows downstream) keeps every
  // count, gate packet and commit working on one consistent set — and it is
  // the renderer's own rule, so the picker and the produced .docx cannot
  // disagree about what a leaf contains. Returns `docConfig` BY REFERENCE when
  // it does not apply (no leaf id, no leaves, or a stale id), so the memo stays
  // stable and a stale id widens back rather than emptying the picker.
  const leafScopedConfig = useMemo<DocTypeConfig | null>(
    () => scopedCatalog(docConfig, leafDocumentId) as DocTypeConfig | null,
    [docConfig, leafDocumentId],
  );
  const leafOptionsForPicker = useMemo(() => leafOptions(docConfig), [docConfig]);
  const selectionTree = useMemo<SelectionNode[]>(
    () => (leafScopedConfig ? buildSelectionTree(leafScopedConfig) : []),
    [leafScopedConfig],
  );
  const selectionGates = useMemo<GateMeta[]>(
    () => (leafScopedConfig ? buildGateMeta(leafScopedConfig) : []),
    [leafScopedConfig],
  );
  // Already-drafted section ids for the draft-more picker. They stay
  // SELECTABLE (re-drafting overwrites them) and are surfaced as an
  // informational "drafted" hint rather than a hard lock — the backend
  // continue_run_sections path resets + re-drafts any drafted module the
  // writer re-selects.
  const draftedSectionIdsForPicker = useMemo<string[]>(() => {
    if (!draftingMore || !docConfig) return [];
    const draftedModules = new Set(computeLockedModuleIds(sections));
    const out: string[] = [];
    for (const [moduleId, meta] of Object.entries(docConfig.modules ?? {})) {
      if (!draftedModules.has(moduleId)) continue;
      out.push((meta.sections ?? [])[0] ?? moduleId);
    }
    return out;
  }, [draftingMore, docConfig, sections]);
  // Hard-locked (never selectable) section ids for the Section Picker. Drafted
  // sections used to be locked here, which meant they could never be re-drafted
  // from "Draft more" — but the backend `continue_run_sections` path resets and
  // re-drafts any drafted module the writer re-selects, so drafted sections now
  // flow through `draftedSectionIdsForPicker` (selectable, with a "drafted" chip
  // and an overwrite confirmation on Start) instead. Nothing is hard-locked
  // today; the prop stays wired so a genuinely non-selectable status could be
  // added here later without touching the picker.
  const lockedSectionIdsForPicker = useMemo<string[]>(() => [], []);
  const initialStrategy: WriteStrategy = docConfig?.generation?.insertion_mode === "batch" ? "batched" : "live";

  // Per-section drafted counter — drives the TabBar's live Spine
  // progress chip ("5/38") and some downstream hints. StatusStripC's
  // phase-lifecycle derivation retired in Spec 29 Phase 1.
  const draftedCount = sections.filter((s) => s.status === "drafted").length;

  if (starting) {
    body = (
      <div className={styles.startingOverlay}>
        <div className={styles.startingMark}>{draftingMore ? "Drafting more sections…" : "Starting your run…"}</div>
        {draftingMore && <div className={styles.statusChip}>Backend busy</div>}
        <div className={styles.startingHint}>
          {draftingMore
            ? "Waiting for the continuation request to finish before the next run can start."
            : "Spinning up the agent and reading the library. The spine populates as soon as the first section starts."}
        </div>
      </div>
    );
  } else if (sectionPickerVisible && activeSelection) {
    // Spec 23 P10.3 — unified Selection picker. Sits between
    // LoomFresh and LoomPlanReview. Commit → plan-or-run; Cancel →
    // back to LoomFresh.
    body = (
      <LoomSelection
        // Remount per document so the section defaults (computed once on mount)
        // recompute against the newly-scoped tree when the writer switches
        // documents. Constant for single-document doctypes (leafDocumentId stays
        // null), so their behavior is unchanged.
        key={leafDocumentId ?? "__root__"}
        tree={selectionTree}
        gates={selectionGates}
        leaves={leafOptionsForPicker}
        leafDocumentId={leafDocumentId}
        onLeafSelect={handleLeafSelect}
        docCode={activeSelection.docCode}
        docStandard={activeSelection.docFramework ?? ""}
        initialStrategy={initialStrategy}
        initialChecked={sectionCommit?.explicitSectionIds}
        lockedSectionIds={lockedSectionIdsForPicker}
        draftedSectionIds={draftedSectionIdsForPicker}
        onStart={handleSectionCommit}
        onCancel={handleSectionCancel}
      />
    );
  } else if (planReviewActive && activeSelection) {
    body = (
      <LoomPlanReview
        plan={planData}
        loading={planLoading}
        apiHealthy={planApiHealthy}
        error={planError}
        docCode={activeSelection.docCode}
        libraryName={activeSelection.libraryName}
        onApprove={handlePlanApprove}
        onModify={handlePlanModify}
        onSkip={handlePlanSkip}
        // Spec 23 P10.4 — thread the LoomSelection strategy into the
        // narrative builder so the "Planner says" line previews how
        // the run will feel. Absent when the writer took the legacy-
        // fallback path (no config → no picker commit).
        strategy={sectionCommit?.strategy}
        // Spec 23 PR-A — pass docConfig.gates so the plan rows
        // render in coloured gate packets matching the picker.
        gates={docConfig?.gates}
        // PlanHighlights "deps pulled in" stat — diff between
        // resolver-implied and explicit picks. Zero when no
        // sectionCommit (legacy fallback) or no implied modules.
        impliedCount={
          sectionCommit
            ? Math.max(0, sectionCommit.resolvedSectionIds.length - sectionCommit.explicitSectionIds.length)
            : 0
        }
        // New compact header pill — same handler as Modify, just
        // surfaced separately in the new layout.
        onBackToPicker={handlePlanModify}
        // Phase 9.4 — locked module ids surface ONLY when the
        // post-run "Draft more sections" flow is active. Undefined
        // in the fresh-draft path → LoomPlanReview behaves exactly
        // as before (no rows locked, all modules actionable).
        lockedModuleIds={lockedModuleIdsForNextPlan ?? undefined}
      />
    );
    // Spec 29 Phase A (2026-04-23) — the /draft flow is now
    // conversational, rendered INSIDE the idle TimelineView via the
    // ``pinnedAgentCard`` prop. The LoomFresh full-body takeover is
    // retired from the draft path; LoomFresh the component is still
    // in the repo but no longer rendered. The former
    // ``draftPickerActive`` branch that lived here is gone.
  } else if (state.viewMode === "drafts") {
    // Batch-SSOT Drafts tab — run-agnostic. Renders the same
    // panel regardless of the run lifecycle (idle / mid-run /
    // approval / post-run), so the writer can browse prior runs
    // and pull bundles without leaving the shell.
    body = (
      // The Drafts tab reuses the fresh-doc Home scene verbatim (hero + card
      // grid), so the two surfaces stay pixel-identical. ``enableRunActions``
      // turns on the per-card ⋯ menu (Open in Word / Download bundle) and lists
      // every completed run rather than one card per document.
      <HomeScene
        userIdentifier={userIdentifier ?? null}
        docConfig={docConfig}
        workspaceId={draftsWorkspaceId}
        workspaces={workspaces}
        scopeWorkspaceId={draftsScopeWorkspaceId}
        currentDocId={draftsDocId}
        onScopeChange={setDraftsScopeWorkspaceId}
        onBeforeLoad={() => emptyDocWatcherRef.current?.acknowledge()}
        onRunLoaded={handleRunLoaded}
        onNewDoc={handleContextCardStartFreshDraft}
        refreshSignal={draftsRefreshNonce}
        enableRunActions
      />
    );
  } else if (state.viewMode === "images") {
    // Image search — run-agnostic, like Drafts. Browse the library's figures
    // and insert one at the cursor (insert is shell-owned via handleImageInsert).
    // When an image is selected, the pane enters replace mode so the Insert
    // button replaces the selected image instead of inserting a new one.
    body = (
      <ImageSearchPane
        libraryId={imagesLibraryId}
        libraryName={imagesLibraryName}
        onInsert={(title, replaceMode) => void handleImageInsert(title, replaceMode)}
        replaceMode={imageSelected}
      />
    );
  } else if (state.state === "idle") {
    const classification = documentContext.state === "ready" ? documentContext.classification : null;

    if (classification?.state === "empty") {
      // New Home scene: empty-document landing page with document list.
      // Replaces the old onboarding checklist (landingpage.txt spec).
      // This is a full-screen, dedicated experience with no TabBar/ContextChips.
      body = (
        <HomeScene
          userIdentifier={userIdentifier ?? null}
          docConfig={docConfig}
          workspaceId={draftsWorkspaceId}
          workspaces={workspaces}
          scopeWorkspaceId={draftsScopeWorkspaceId}
          currentDocId={draftsDocId}
          onScopeChange={setDraftsScopeWorkspaceId}
          onBeforeLoad={() => emptyDocWatcherRef.current?.acknowledge()}
          onRunLoaded={handleRunLoaded}
          onNewDoc={handleContextCardStartFreshDraft}
          refreshSignal={draftsRefreshNonce}
        />
      );
    } else if (state.viewMode === "spine" && sections.length > 0) {
      // Defensive: render the inspector drawer here too so the idle spine
      // surface isn't a dead-click. ``inspectorNode`` self-gates on
      // ``state.runId``, which is null in idle today (RESET_TO_IDLE →
      // initialState), so it stays inert unless a future idle path carries a
      // runId. The load-bearing fix for "nothing opens" lives in the
      // run-active branch below (post-run / mid-run), where the guard no
      // longer hard-requires ``activeSelection``.
      body = (
        <>
          <LoomSpine
            sections={enrichedSections}
            activeSectionId={activeSectionId}
            liveDocLoadBySection={liveDocLoadBySection}
            agentActivityBySection={agentActivityBySection}
            pauseAnimations={true}
            streamError={streamError}
            initStatus={initStatus}
            onSectionFocus={setFocusedSectionId}
            onOpenReview={handleOpenReview}
            pendingApproval={state.pendingApproval}
            onApprovalAccept={handleApprovalAccept}
            onApprovalOpenReview={handleApprovalOpenReview}
            countersBySection={state.eventLog.counters}
          />
          {inspectorNode}
        </>
      );
    } else {
      // Existing TimelineView + IdleGreeting (has-content context card, or
      // loading-classification fallback). Byte-identical to pre-HomeScene
      // behavior for non-empty docs.
      const hasDoctype = !!selectedDocCode;
      const hasLibrary = !!selectedLibraryId;
      const doctypeLabel = selectedDocCode ? (lookupDoctypeChoice(selectedDocCode)?.docName ?? null) : null;
      const libraryName = selectedLibraryId ? (libraries.find((l) => l.id === selectedLibraryId)?.name ?? null) : null;

      const greetingContent: React.ReactNode =
        classification && classification.state === "has-content" ? (
          <IdleGreeting
            classification={classification}
            hasDoctype={hasDoctype}
            hasLibrary={hasLibrary}
            doctypeLabel={doctypeLabel}
            libraryName={libraryName}
            onPickDoctype={handleChecklistPickDoctype}
            onPickLibrary={handleChecklistPickLibrary}
            onReviewAndStart={handleChecklistReviewAndStart}
            onAskAboutDocument={handleAskAboutDocument}
            showAskAboutDocument={showIncompleteActions}
            onStartFreshDraft={handleContextCardStartFreshDraft}
          />
        ) : null;

      body = (
        <>
          {startError && (
            <div className={styles.errorBanner}>
              <strong>Couldn&apos;t start the run.</strong> {startError}
            </div>
          )}
          {workspaceFetchError && (
            <div className={styles.errorBanner}>
              <strong>Couldn&apos;t load workspaces.</strong> {workspaceFetchError}
            </div>
          )}
          <TimelineView
            entries={timelineEntries}
            filterSectionLabel={state.timelineFilter.sectionId}
            replayCursor={state.replayCursor}
            runId={state.runId}
            verdictForSection={verdictForSection}
            streamingStatus={
              chat.isStreaming
                ? buildStreamingStatus(
                    chat.liveTool,
                    chat.statusLabel,
                    chat.streamingIntent ? intentLabel(chat.streamingIntent) : null,
                  )
                : (highlightEditStatus ?? sectionEditStatus ?? chatRunActivityStatus)
            }
            streamingDraft={chat.isStreaming ? chat.streamingDraft : chatRunActivity?.draftSoFar || null}
            streamStartedAt={chat.isStreaming ? chat.streamStartedAt : (chatRunActivity?.startedAt ?? null)}
            onProposalDecision={handleProposalDecision}
            proposalMessage={adhocProposalNode ?? editProposalNode ?? writeApprovalNode}
            liveActivity={liveActivityNode}
            pendingApproval={state.pendingApproval}
            onApprovalAccept={handleApprovalAccept}
            onApprovalOpenReview={handleApprovalOpenReview}
            // Spec: Blank-Document Onboarding — render the checklist/card as
            // ONE persistent interactive message so the writer's doctype /
            // library picks update it in place (steps flip green) instead of
            // tearing it down once pick events land in the log. The ephemeral
            // ``greeting`` carries only the fallback welcome text shown while
            // classification is still loading (no checklist/card yet).
            // greeting={greetingContent ? null : buildIdleGreeting(null)}
            persistentGreeting={greetingContent}
            // Spec 29 Phase A — conversational /draft flow renders
            // as a pinned card at the top of the timeline. Null
            // when the flow is idle; TimelineView falls back to
            // the greeting.
            pinnedAgentCard={draftFlowCard}
            persistedMessages={chatHistoryMessages}
            hasMoreHistory={chatHasMoreOlder}
            isLoadingHistory={chatIsLoadingOlder}
            onLoadOlder={chatLoadOlder}
            now={new Date()}
            onCommandClick={handleCommandLinkClick}
            onReply={handleReply}
          />
        </>
      );
    }
  } else {
    // mid-run / approval / post-run share the run surface. Spec 29
    // promotes TabBar (shell-level) to switch between Chat (Timeline)
    // and Spine. The body computed here contains the active tab's
    // content only — the TabBar and ContextStrip render above this
    // in the shell return block. EventLegendStrip and
    // SpineTimelineToggle retired with Spec 29 Phase 1.
    const runBodyIsSpine = state.viewMode === "spine";
    const runBodyIsIssues = state.viewMode === "issues";
    // Rule lives in postRunBanner.ts — a regen keeps the reducer in ``post-run``
    // while a section actively drafts, so the banner cannot key off state alone.
    const showPostRunBanner = shouldShowPostRunBanner({
      isSpineView: runBodyIsSpine,
      runState: state.state,
      hasRunSummary: Boolean(state.runSummary),
      sections,
    });
    body = (
      <>
        {runBodyIsSpine ? (
          <LoomSpine
            sections={enrichedSections}
            activeSectionId={activeSectionId}
            liveDocLoadBySection={liveDocLoadBySection}
            agentActivityBySection={agentActivityBySection}
            pauseAnimations={state.state === "paused" || state.state === "pausing"}
            streamError={streamError}
            initStatus={initStatus}
            onSectionFocus={setFocusedSectionId}
            onOpenReview={handleOpenReview}
            pendingApproval={state.pendingApproval}
            onApprovalAccept={handleApprovalAccept}
            onApprovalOpenReview={handleApprovalOpenReview}
            countersBySection={state.eventLog.counters}
          />
        ) : runBodyIsIssues ? (
          <SubmissionIssuesTab
            runId={state.runId}
            onResolveWithAsthra={handleResolveGapWithAsthra}
            onOpenCountChange={setIssuesOpenCount}
            onResolveSelected={(targets) => setResolveWizardTargets(targets)}
            issuesRefreshSignal={issuesRefreshSignal}
            proposalsRefreshSignal={proposalsRefreshSignal}
            libraryId={draftsLibraryId}
            onProposalApplied={() => setIssuesRefreshSignal((n) => n + 1)}
          />
        ) : (
          <TimelineView
            entries={timelineEntries}
            filterSectionLabel={state.timelineFilter.sectionId}
            replayCursor={state.replayCursor}
            runId={state.runId}
            verdictForSection={verdictForSection}
            // Spec 29 — live chat streaming indicator. The hook
            // exposes liveTool; buildStreamingStatus maps it
            // to a writer-facing phrase. Null when not streaming.
            // Intent-routed chat reuses the same transient row for
            // a chat-initiated regen / ad-hoc draft (live drafting
            // bubble) and the highlight-rewrite wait state.
            streamingStatus={
              chat.isStreaming
                ? buildStreamingStatus(
                    chat.liveTool,
                    chat.statusLabel,
                    chat.streamingIntent ? intentLabel(chat.streamingIntent) : null,
                  )
                : (highlightEditStatus ?? sectionEditStatus ?? chatRunActivityStatus)
            }
            streamingDraft={chat.isStreaming ? chat.streamingDraft : chatRunActivity?.draftSoFar || null}
            streamStartedAt={chat.isStreaming ? chat.streamStartedAt : (chatRunActivity?.startedAt ?? null)}
            onProposalDecision={handleProposalDecision}
            // Text-edit proposals — ad-hoc section draft, highlight-edit
            // rewrite, OR agent write approval, all through the shared
            // ProposalCard. They can't normally co-occur (the WS gate
            // blocks the tool loop while a chat turn runs); newest-
            // decision-surface-first if they do.
            proposalMessage={adhocProposalNode ?? editProposalNode ?? writeApprovalNode}
            liveActivity={liveActivityNode}
            // Spec 29 Phase 2 — inline approval row at the top of
            // the Timeline. Shares the same pending-approval state
            // the spine-side ApprovalPopout reads; both dispatch
            // the same handlers so clicking either resolves the
            // approval globally.
            pendingApproval={state.pendingApproval}
            onApprovalAccept={handleApprovalAccept}
            onApprovalOpenReview={handleApprovalOpenReview}
            // Flat chronological history — persisted messages merge
            // with the live stream inside TimelineView (deduped by
            // ``at:role``). Null when no document is selected;
            // TimelineView renders the live stream only in that case.
            persistedMessages={chatHistoryMessages}
            hasMoreHistory={chatHasMoreOlder}
            isLoadingHistory={chatIsLoadingOlder}
            onLoadOlder={chatLoadOlder}
            // Phase 7.5 — current-time anchor for day-banner labels
            // ("Today" / "Yesterday" / weekday). Pass a single
            // ``now`` so grouping decisions and labels stay
            // consistent within a render.
            now={new Date()}
            onCommandClick={handleCommandLinkClick}
            onReply={handleReply}
          />
        )}
        {/* Inspector remains a spine-side drawer. Focus state is
            preserved across tab flips; re-opening the Spine tab
            re-shows the drawer over the matching node. Shared
            ``inspectorNode`` (defined above) renders it identically here
            and in the idle restored-run spine branch. */}
        {runBodyIsSpine && inspectorNode}
        {/* Post-run banner only on Spine view. Chat view does not show it.
            Suppressed while a section is drafting — a regen keeps the reducer
            in ``post-run`` (see postRunBanner.ts). */}
        {showPostRunBanner && (
          <div className={styles.startingOverlay} style={{ flex: "0 0 auto", padding: "12px" }}>
            <div className={styles.startingMark}>
              Run complete · {draftedCount}/{submissionTotalSections ?? sections.length} drafted
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {/* "Load doc" re-pulls the run's canonical server body and rebuilds
                  the live doc (``handleLoadDraftedDocument``: fetchRunDocument →
                  hydrateLiveDocFromBase64, per-section loadRunIntoLiveDoc fallback).
                  Previously batch-only, because section-mode auto-paints on
                  restore so the button looked redundant. Now shown for BOTH modes:
                  it is a safe, idempotent rebuild affordance — useful for section
                  mode too (re-pull after clearing the body, or to reconcile the
                  live doc with the server's canonical version). ``handleLoadDraftedDocument``
                  no longer diverts section mode to an external window, so both
                  modes take the same inline-rebuild path. */}
              <button
                type="button"
                className={styles.resetButton}
                disabled={loadDraftInFlight}
                style={{
                  background: brandColors.primary,
                  color: "#fff",
                  borderColor: brandColors.primary,
                  fontWeight: 700,
                  opacity: loadDraftInFlight ? 0.6 : 1,
                  cursor: loadDraftInFlight ? "not-allowed" : "pointer",
                }}
                onClick={() => {
                  void handleLoadDraftedDocument();
                }}
              >
                Load doc
              </button>
              {/* Phase 9.4 — replaced "Start another draft" (which
                  called handleReset and wiped state) with a safe
                  "Draft more sections" affordance that PRESERVES
                  library + doctype + plan and locks already-drafted
                  rows in the picker so they cannot be redrafted by
                  accident. The regulatory safety case (existing
                  drafts must never be at risk of overwrite) is the
                  reason a destructive reset path is no longer
                  exposed on the post-run surface. */}
              {(() => {
                // Reachable even when every section is drafted: drafted
                // sections are now re-selectable in the picker (re-drafting
                // overwrites, behind a confirmation), so a fully-drafted
                // document must still be able to open it to redo a section.
                const busy = starting;
                // Custom submission: sections have no doctype-template
                // instruction, so "Draft more" is only meaningful once the
                // writer has authored an instruction for at least one section
                // (and a source library is attached). The picker then restricts
                // selection to those instruction-bearing sections. Standard runs
                // are template-driven and always draftable.
                const customBlocked = isCustomRun && !(anyCustomSectionHasInstruction && hasSourceLibrary);
                const disabled = busy || customBlocked;
                const label = busy ? "Drafting more sections…" : "Draft more sections";
                return (
                  <button
                    type="button"
                    className={styles.resetButton}
                    style={
                      disabled
                        ? { opacity: 0.45, cursor: "not-allowed" }
                        : {
                            background: brandColors.primary,
                            color: "#fff",
                            borderColor: brandColors.primary,
                            fontWeight: 700,
                          }
                    }
                    disabled={disabled}
                    title={
                      customBlocked
                        ? isCustomRun && !hasSourceLibrary
                          ? "Attach a source library first — Asthra drafts only from your sources."
                          : "Add a drafting instruction to at least one section (Instructions tab) to draft a custom submission."
                        : undefined
                    }
                    onClick={
                      // Custom runs use the dedicated instruction-aware picker;
                      // standard runs open the template-tree LoomSelection.
                      isCustomRun ? () => setCustomDraftPickerActive(true) : handleDraftMoreSections
                    }
                  >
                    {label}
                  </button>
                );
              })()}
            </div>
          </div>
        )}
      </>
    );
  }

  // Spec 29 — Chat is the default tab; Spine tab materialises only
  // during a run. Tab state is driven by ``state.viewMode`` in the
  // reducer (timeline → "chat" semantically) so existing /spine /
  // /timeline slash commands continue to work without translation.
  const activeTab: TabId =
    state.viewMode === "spine"
      ? "spine"
      : state.viewMode === "issues"
        ? "issues"
        : state.viewMode === "drafts"
          ? "drafts"
          : state.viewMode === "images"
            ? "images"
            : "chat";
  const showSpineTab =
    state.state === "mid-run" ||
    state.state === "approval" ||
    state.state === "pausing" ||
    state.state === "paused" ||
    state.state === "post-run" ||
    (state.state === "idle" && sections.length > 0 && !!activeSelection && !!docConfig);
  // The submission-wide Issues tab is meaningful whenever the Spine is — i.e.
  // whenever a run's sections exist to carry gap/regulatory issues.
  const showIssuesTab = showSpineTab;

  // Home scene active: idle state + empty document classification (landing page).
  // Used to hide TabBar, ContextChipsBar, and CommandRail on the clean landing view.
  const isHomeSceneActive =
    state.state === "idle" && documentContext.state === "ready" && documentContext.classification?.state === "empty";
  const takeoverActive = starting || sectionPickerVisible || planReviewActive || resolveWizardTargets !== null;

  // Context-strip slots — workspace + doctype + library + active
  // section. Resolved values render as chips; unresolved ones show
  // muted hints. When unresolved AND the writer is in idle (where
  // picking something is meaningful), the chip is clickable and
  // opens LoomFresh via the same /draft pathway the slash command
  // uses — chip-click and /draft converge on one flow. Mid-run /
  // approval / post-run states omit the click handler; the chips
  // are informational there, not actionable.
  const idleAndAvailable = state.state === "idle" && !pendingDraftStart;
  const hasDocType = !!(selectedDocCode || activeSelection?.docCode);
  // Phase 7.2 — standalone chip-click handlers. Doctype and library
  // are independent picks; clicking one chip never opens the other
  // surface or advances any orchestration. The /draft slash command
  // is the ONLY trigger that asks for both via runDraftCommand
  // (which uses pendingDraftStart to drive the orchestrator).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setState setters are guaranteed stable by React
  const openDoctypePickerStandalone = useCallback(() => {
    setPendingDraftStart(false);
    setDoctypePickerOpen(true);
  }, []);
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState<boolean>(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: setState setters are guaranteed stable by React
  const openWorkspacePicker = useCallback(() => {
    setPendingDraftStart(false);
    setWorkspacePickerOpen(true);
  }, []);
  const openLibraryPicker = useCallback(() => {
    if (!hasDocType) {
      setDoctypePickerOpen(true);
      return;
    }
    openSlideOver("library-list");
  }, [hasDocType, openSlideOver]);

  // Phase 7.2 — /draft slash command: try to start, ask for what's
  // missing if anything. Sets pendingDraftStart so picker callbacks
  // know to continue the orchestration after each pick.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setPendingDraftStart is a stable setter; tryAdvanceDraft already in deps
  const runDraftCommand = useCallback(() => {
    setPendingDraftStart(true);
    tryAdvanceDraft();
  }, [tryAdvanceDraft]);

  // Phase 4 / 7.2 — chips reflect the writer's standalone picks,
  // not just the committed activeSelection. ``selectedDocCode``
  // (independent state, set by DoctypePicker) feeds the doctype
  // chip's tentative value when no active draft has committed yet.
  const tentativeDocType = !activeSelection?.docCode && selectedDocCode ? selectedDocCode : null;
  const committedLibraryName = libraries.find((l) => l.id === selectedLibraryId)?.name ?? null;
  const libraryIsTentative =
    !!committedLibraryName && (!activeSelection || activeSelection.libraryId !== selectedLibraryId);

  // Spec 34 — chat-first Library Hub slide-over state. All entry
  // points (/library, /upload, inspect chevron, create button) now
  // route through openSlideOver; the legacy LibraryHub modal has
  // been removed.
  const [hubSlideOverOpen, setHubSlideOverOpen] = useState(false);
  const [hubSlideOverMode, setHubSlideOverMode] = useState<HubMode>("list");

  // Fetch the active run's audit library so the Hub can offer a Source↔Audit
  // toggle. Only when the Hub is open and a run exists; the panel self-hides when
  // the feature is off or the run has no audit library.
  const loadHubAuditLibrary = useCallback(() => {
    const runId = state.runId ?? null;
    if (!runId) {
      setHubAuditLibrary(null);
      return;
    }
    getRunAuditLibrary(runId)
      .then(setHubAuditLibrary)
      .catch(() => setHubAuditLibrary(null));
  }, [state.runId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch when the Hub opens or the run changes
  useEffect(() => {
    if (hubSlideOverOpen) loadHubAuditLibrary();
  }, [hubSlideOverOpen, loadHubAuditLibrary]);
  // While the Hub is open and a build is in flight, poll so the Hub's audit-library
  // view shows live step + counter progress (the in-inspector panel self-polls;
  // the Hub was previously fetch-on-open only). Mirrors the 6s inspector cadence.
  const hubAuditBuilding =
    hubAuditLibrary?.audit_library?.status === "acquiring" || hubAuditLibrary?.audit_library?.status === "processing";
  useEffect(() => {
    if (!hubSlideOverOpen || !hubAuditBuilding) return undefined;
    const t = setInterval(loadHubAuditLibrary, 6000);
    return () => clearInterval(t);
  }, [hubSlideOverOpen, hubAuditBuilding, loadHubAuditLibrary]);
  const [uploadSlots, setUploadSlots] = useState<UploadSlot[]>([]);
  // Guards against a doc-delete firing twice for the same doc_id (rapid
  // double-click, or a duplicate dispatch from the row's nested delete
  // button) while the first DELETE call is still in flight.
  const deletingDocIdsRef = useRef<Set<string>>(new Set());

  // Clear upload slots when library changes to prevent cross-library contamination.
  // When a document is in "Parsing" state during a library switch, the upload slot
  // would otherwise be merged into the new library's inventory by filename matching.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedLibraryId is the trigger
  useEffect(() => {
    setUploadSlots([]);
  }, [selectedLibraryId]);
  setPendingDraftStart;
  // Derive ``DocumentRow[]`` for the slide-over by merging the
  // active upload session with the cached inventory. Adapter
  // narrows the wire-shape DTO to the InventoryView the merger
  // expects; merge is a pure function so this re-evaluation on
  // esetPendingDraftStartvery render is cheap.
  const hubRows = useMemo(
    () =>
      mergeUploadsAndInventory({
        uploads: uploadSlots,
        inventory: inventoryDtoToView(libraryInventory),
      }),
    [uploadSlots, libraryInventory],
  );

  const activeWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId) ?? null;
  const activeLibraryForChips = libraries.find((l) => l.id === selectedLibraryId) ?? null;

  const handleNewHubAddFiles = useCallback(
    (files: File[]) => {
      if (!selectedLibraryId || files.length === 0) return;
      const libraryId = selectedLibraryId;

      for (const file of files) {
        const clientId = crypto.randomUUID();
        setUploadSlots((prev) => [
          ...prev,
          {
            client_id: clientId,
            filename: file.name,
            status: "uploading",
            upload_progress_percent: null,
            doc_id: null,
          },
        ]);

        uploadFile(libraryId, file)
          .then(() => {
            setUploadSlots((prev) =>
              prev.map((slot) =>
                slot.client_id === clientId
                  ? {
                      ...slot,
                      status: "uploaded",
                      upload_progress_percent: 100,
                    }
                  : slot,
              ),
            );
            return refreshLibraryInventory();
          })
          .catch((error: unknown) => {
            setUploadSlots((prev) =>
              prev.map((slot) =>
                slot.client_id === clientId
                  ? {
                      ...slot,
                      status: "failed_upload",
                      error_message: error instanceof Error ? error.message : "Upload failed",
                    }
                  : slot,
              ),
            );
          });
      }
    },
    [selectedLibraryId, refreshLibraryInventory],
  );

  // --- Table-artefact builder: tag tables → transform → preview → save CSV ---
  const [artefactBuilderOpen, setArtefactBuilderOpen] = useState(false);
  const [artefactBuilding, setArtefactBuilding] = useState(false);
  const [artefactBuildError, setArtefactBuildError] = useState<string | null>(null);
  const [artefactStages, setArtefactStages] = useState<ArtefactStageEvent[]>([]);
  const [artefactPreview, setArtefactPreview] = useState<TableArtefactPreview | null>(null);
  const [artefactSaving, setArtefactSaving] = useState(false);
  const [artefactSaveError, setArtefactSaveError] = useState<string | null>(null);

  const handleCreateArtefact = useCallback(() => {
    setArtefactBuildError(null);
    setArtefactStages([]);
    setArtefactPreview(null);
    setArtefactBuilderOpen(true);
  }, []);

  const handleCloseArtefact = useCallback(() => {
    setArtefactBuilderOpen(false);
    setArtefactPreview(null);
    setArtefactBuilding(false);
    setArtefactBuildError(null);
    setArtefactStages([]);
    setArtefactSaving(false);
    setArtefactSaveError(null);
  }, []);

  const handleBuildArtefact = useCallback(
    (tables: MentionAttachment[], instruction: string) => {
      if (!selectedLibraryId) return;
      setArtefactBuilding(true);
      setArtefactBuildError(null);
      setArtefactStages([]);
      streamTableArtefact(selectedLibraryId, tables, instruction, (event) => {
        setArtefactStages((prev) => [...prev, event]);
      })
        .then((preview) => {
          setArtefactPreview(preview);
          setArtefactBuilderOpen(false); // hand off to the preview modal
        })
        .catch((e: unknown) => {
          setArtefactBuildError(e instanceof Error ? e.message : "Could not build the table.");
        })
        .finally(() => setArtefactBuilding(false));
    },
    [selectedLibraryId],
  );

  const handleApproveArtefact = useCallback(
    (filename: string) => {
      if (!selectedLibraryId || !artefactPreview) return;
      setArtefactSaving(true);
      setArtefactSaveError(null);
      uploadContent(selectedLibraryId, filename, artefactPreview.csv, "text/csv")
        .then(() => {
          setArtefactSaving(false);
          setArtefactPreview(null); // closes the modal
          return refreshLibraryInventory();
        })
        .catch((e: unknown) => {
          setArtefactSaving(false);
          setArtefactSaveError(e instanceof Error ? e.message : "Could not save the CSV.");
        });
    },
    [selectedLibraryId, artefactPreview, refreshLibraryInventory],
  );

  const contextWorkspace: ContextSlot = {
    value: activeWorkspace?.name ?? null,
    hint: "pick a workspace",
    // Workspace chip → opens WorkspacePicker dialog so the writer
    // can SWITCH workspaces. Previously routed to runDraftCommand
    // (sent them to the doctype picker), which mismatched the
    // writer's mental model — clicking a workspace label means
    // "change workspace", not "start drafting".
    onClick: idleAndAvailable ? openWorkspacePicker : undefined,
  };
  const contextDocType: ContextSlot = {
    value: activeSelection?.docCode ?? tentativeDocType,
    hint: "pick a doctype",
    // Phase 7.2 — doctype chip click opens DoctypePicker standalone
    // (no library auto-open). The pick lands as ``selectedDocCode``;
    // ``runDraftCommand`` later picks it up if the writer fires /draft.
    onClick: idleAndAvailable ? openDoctypePickerStandalone : undefined,
    tentative: !activeSelection?.docCode && !!tentativeDocType,
  };
  const contextLibrary: ContextSlot = {
    value: committedLibraryName,
    hint: "pick a library",
    // Library chip → library-only flow. (Doctype chip still
    // routes to openDraftPicker since doctype selection implies
    // intent to start a draft.)
    onClick: idleAndAvailable ? openLibraryPicker : undefined,
    tentative: libraryIsTentative,
  };
  const contextActiveSection = state.activeSection?.sectionNumber
    ? {
        number: state.activeSection.sectionNumber,
        live: state.state === "mid-run" || state.state === "approval",
      }
    : null;

  // Spine-tab progress chip: "5/96" when both counts are known — the
  // denominator is the whole submission (falls back to the scoped spine
  // length when docConfig hasn't loaded), matching the drafts card.
  const spineCounter =
    showSpineTab && sections.length > 0 ? `${draftedCount}/${submissionTotalSections ?? sections.length}` : null;
  // Issues-tab badge: only the ledger's open count is authoritative (event
  // counters over-/under-count vs the open/resolved/dismissed/stale lifecycle),
  // so drive it off ``issuesOpenCount``. Hide the chip at zero — a "0" badge is
  // noise, not a call to action.
  const issuesCounter: string | null =
    showIssuesTab && issuesOpenCount != null && issuesOpenCount > 0 ? `${issuesOpenCount}` : null;

  // Proactively fetch the open-issue count for the badge whenever the Issues tab
  // is available, re-running when the ledger may have changed (issuesRefreshSignal
  // bumps after an approved edit). Race-guarded so a stale response can't clobber
  // a newer count. SubmissionIssuesTab reports in-tab dismiss/reopen via
  // onOpenCountChange, so the badge stays live between these fetches.
  const issuesCountReqRef = useRef(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: issuesRefreshSignal is a deliberate extra trigger — it is not read in the body, it exists to force a re-fetch after an approved edit.
  useEffect(() => {
    if (!showIssuesTab || !state.runId) {
      setIssuesOpenCount(null);
      setRegulatoryOpenByModule(null);
      return;
    }
    const reqId = ++issuesCountReqRef.current;
    void getRunIssues(state.runId, "open")
      .then((resp) => {
        if (issuesCountReqRef.current !== reqId) return;
        setIssuesOpenCount(resp.status_counts.open);
        // Bridge the ledger's per-module OPEN regulatory counts onto the spine.
        // ``open_counts`` is always the OPEN tally regardless of the status
        // filter, keyed by module_id (== SpineSection.id). A module absent from
        // the map has no open issues → 0, so this also clears a section whose
        // flags were resolved.
        const regByModule: Record<string, number> = {};
        for (const [mid, counts] of Object.entries(resp.open_counts ?? {})) {
          regByModule[mid] = counts?.regulatory ?? 0;
        }
        setRegulatoryOpenByModule(regByModule);
      })
      .catch(() => {
        if (issuesCountReqRef.current === reqId) setIssuesOpenCount(null);
      });
  }, [showIssuesTab, state.runId, issuesRefreshSignal]);

  // ---------------------------------------------------------------------
  // Menu composition (Spec 29 palette-merge).
  // ---------------------------------------------------------------------
  // The slash menu is now the single command surface; ⇧⌘P retired,
  // palette retired. Compose a narrow MenuRenderState from the
  // shell's reducer + controlled state, then run it through
  // ``buildMenuGroups`` — groups that aren't meaningful in the
  // current state are hidden; items within a visible group can
  // be disabled with a reason. The rail applies
  // filter-as-you-type on top.
  const advancedCommandsEnabled = useFlag("advanced-commands");
  const menuRenderState: MenuRenderState = useMemo(
    () => ({
      runState: state.state,
      hasEvents: state.eventLog.entries.length > 0,
      hasSections: sections.length > 0,
      workspaces: workspaces.map((w) => ({
        id: w.id,
        name: w.name,
        isCurrent: w.id === selectedWorkspaceId,
      })),
      isSandbox: !!appConfig?.sandbox,
      modeToggling,
      modeToggleAllowed: state.state === "idle" || state.state === "post-run",
      advancedCommandsEnabled,
    }),
    [
      state.state,
      state.eventLog.entries.length,
      sections.length,
      workspaces,
      selectedWorkspaceId,
      appConfig?.sandbox,
      modeToggling,
      advancedCommandsEnabled,
    ],
  );
  const menuGroups = useMemo(() => buildMenuGroups(menuRenderState), [menuRenderState]);

  // ---_contextActiveSection----------------------------------------------
  // Slash-command dispatcher. Declared here (late) because it closes
  // over chat, handleStopRun, planReviewActive — each of which is
  // defined earlier in the shell but after the place the dispatcher
  // used to live. The rail does the parsing; the shell is
  // authoritative on what each intent does.
  // ---------------------------------------------------------------------
  // Insert or replace a figure in the doc: fetch its bytes via the cached
  // /api/find-image proxy as a data-URI, then insert at cursor or replace
  // selected image via Office.js. When replaceMode is true and an image is
  // selected, replace it; otherwise insert as a new image. The result is
  // surfaced as an agent_note (the inserted image is the main signal).
  const handleImageInsert = useCallback(
    async (title: string, replaceMode?: boolean) => {
      const note = (text: string) =>
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: Date.now(),
            actor: "asthra",
            sectionId: null,
            runId: state.runId,
            payload: { kind: "agent_note", text },
          },
        });
      if (!title || !imagesLibraryId) {
        note("Couldn't insert the figure — select a library first.");
        return;
      }
      try {
        const dataUri = await fetchImageDataUri(imagesLibraryId, title);
        let ok = false;
        if (replaceMode && imageSelected) {
          ok = await replaceSelectedImage(dataUri);
          if (!ok) {
            note("Couldn't replace the figure — no image is selected.");
          }
        } else {
          ok = await insertInlineImageAtSelection(dataUri);
          if (!ok) {
            note("Couldn't insert the figure — place your cursor in the document body and try again.");
          }
        }
        if (ok) {
          note(
            replaceMode && imageSelected
              ? `Replaced the selected image with "${title}".`
              : `Inserted "${title}" into the document.`,
          );
          setImageSelected(false);
        }
      } catch {
        note("Couldn't insert the figure — the image couldn't be fetched.");
      }
    },
    [state.runId, imagesLibraryId, imageSelected],
  );

  // The spine section the writer's cursor is in (falling back to the focused
  // section). Read on demand — the add-in keeps no ambient cursor state (it
  // froze Word on long drafts). Shared by every section-scoped chip.
  // Set when the last click-time section read hit the timeout, so the
  // caller can say "Word didn't respond" instead of "put your cursor…".
  const sectionReadTimedOutRef = useRef(false);
  const noSectionMessage = useCallback(
    () =>
      sectionReadTimedOutRef.current
        ? "Word didn't respond in time — try again in a moment."
        : "Put your cursor in a drafted section, then try again.",
    [],
  );

  const resolveCursorSection = useCallback(async () => {
    const log = getLogger("addin.chips");
    const startedAt = Date.now();
    // Through queueWordRead like every other read — the background refresh
    // is coalesced (refreshCursorSectionNumber), so the queue stays short.
    // Bounded: a Word read that never returns must not wedge the chip (and,
    // through the rail's in-flight guard, every other chip).
    let timedOut = false;
    const cursorNumber = await Promise.race([
      detectCursorSectionNumberWithFallback(queueWordRead),
      new Promise<null>((resolve) =>
        window.setTimeout(() => {
          timedOut = true;
          resolve(null);
        }, CURSOR_SECTION_READ_TIMEOUT_MS),
      ),
    ]);
    sectionReadTimedOutRef.current = timedOut && cursorNumber === null;
    log.info("chip.section_resolved", {
      section: cursorNumber,
      ms: Date.now() - startedAt,
      timedOut: sectionReadTimedOutRef.current,
    });
    return cursorNumber
      ? (sections.find((s) => s.number === cursorNumber) ?? null)
      : (sections.find((s) => s.id === focusedSectionId) ?? null);
  }, [sections, focusedSectionId]);

  // Full-section redraft of the section the cursor is in, via the
  // proposal-mode ad-hoc pipeline (``adhocDraftSection`` mode "redraft"):
  // the configured instructions stay in effect, ``guidance`` rides along,
  // and the result lands as an Approve/Reject card — no spine flip, SSOT
  // untouched until approve. Single path for both ``/regen <comments>``
  // and the "Regenerate this section" chip. Returns null on a clean start
  // or a writer-facing error.
  //
  // NB: this is NOT the Inspector's Regenerate button (useRegenFlow →
  // regenerateSection), which re-runs drafting and writes the section
  // directly without an approve step.
  const redraftCursorSection = useCallback(
    async (guidance: string): Promise<string | null> => {
      if (!state.runId || state.state !== "post-run") {
        return "Redrafting needs a completed run — finish or load one first.";
      }
      const targetSection = await resolveCursorSection();
      if (!targetSection) {
        return noSectionMessage();
      }
      if (targetSection.status === "drafting") {
        return "That section is mid-draft — wait for it to settle, then try again.";
      }
      // Mark the redraft in flight BEFORE the request: its terminal SSE event
      // clears this, and a fast redraft could otherwise finish before the
      // activity was recorded — leaving it (and the busy chips) stuck.
      handleChatRunActivity({
        moduleId: targetSection.id,
        kind: "regen",
        instruction: guidance,
      });
      try {
        // SSE reattach for terminal runs — same as the chat
        // draft_content path.
        handleChatRegenReconnect();
        await adhocDraftSection(state.runId, targetSection.id, {
          instruction: guidance,
          mode: "redraft",
        });
        return null;
      } catch (err) {
        handleChatRunActivity(null);
        const detailObj = (err as { detail?: { error?: string } }).detail;
        if (detailObj?.error === "no_draft_to_redraft") {
          return "That section hasn't been drafted yet — nothing to redraft.";
        }
        const detail = err instanceof Error ? err.message : String(err);
        return `Couldn't start the redraft: ${detail}`;
      }
    },
    [state.runId, state.state, resolveCursorSection, noSectionMessage, handleChatRegenReconnect, handleChatRunActivity],
  );

  // "Regenerate this section" chip — the SAME workflow as the Inspector's
  // Regenerate button on the Spine: the RegenOne dialog (optional steering
  // instructions) → handleRegenConfirm → regenerateSection. Opened for the
  // cursor's section; switches to the Spine, where the regeneration shows.
  const handleRegenerateSectionChip = useCallback(async (): Promise<string | null> => {
    if (!state.runId || !regenEnabled) {
      return "Regenerating needs a completed run — finish or load one first.";
    }
    const target = await resolveCursorSection();
    if (!target) return noSectionMessage();
    if (target.status === "drafting") {
      return "That section is mid-draft — wait for it to settle, then try again.";
    }
    // Same eligibility as the Inspector's Regenerate button.
    if (target.status !== "drafted" && target.status !== "needs_approval" && target.status !== "flagged") {
      return `§${target.number} hasn't been drafted yet — nothing to regenerate.`;
    }
    handleViewModeChange("spine");
    openRegenFor(target.id);
    return null;
  }, [state.runId, regenEnabled, resolveCursorSection, noSectionMessage, handleViewModeChange, openRegenFor]);

  // "Resolve flags" chip — the Issues tab's bulk "Resolve selected" scoped to
  // the cursor section: every open issue in it goes to the same
  // ResolveIssuesWizard (document scope → Start → resolve pass on the spine,
  // fixes land as proposals to review).
  const handleResolveSectionFlagsChip = useCallback(async (): Promise<string | null> => {
    if (!state.runId || state.state !== "post-run") {
      return "Resolving flags needs a completed run — finish or load one first.";
    }
    const target = await resolveCursorSection();
    if (!target) return noSectionMessage();
    try {
      const { issues } = await getSectionIssues(state.runId, target.id, "open");
      const openIds = issues.filter((i) => i.status === "open").map((i) => i.id);
      if (openIds.length === 0) return `§${target.number} has no open flags.`;
      setResolveWizardTargets([
        { module_id: target.id, number: target.number, title: target.title, issue_ids: openIds },
      ]);
      return null;
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      return `Couldn't load the flags for §${target.number}: ${detail}`;
    }
  }, [state.runId, state.state, resolveCursorSection, noSectionMessage]);

  // "See all flags" chip — the cursor section's Issues tab in the Inspector
  // (same surface the spine's flag badge opens). The Inspector only renders
  // in the Spine view, so switch there first — the chips live in the chat
  // view, where opening it alone showed nothing.
  const handleSeeSectionFlagsChip = useCallback(async (): Promise<string | null> => {
    if (!state.runId) return "Flags need a run — load one first.";
    const target = await resolveCursorSection();
    if (!target) return noSectionMessage();
    handleViewModeChange("spine");
    handleOpenReview(target.id);
    return null;
  }, [state.runId, resolveCursorSection, noSectionMessage, handleViewModeChange, handleOpenReview]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: openLibraryPicker/runDraftCommand/selectedLibraryId intentionally omitted
  const handleCommand = useCallback(
    async (intent: CommandIntent, rawText: string): Promise<string | null> => {
      // Contract: return null on clean dispatch, or a writer-facing
      // error string when the command was recognised but couldn't
      // be honoured (surfaced in the rail's error-hint banner).
      //
      // Spec 29 follow-up (2026-04-23) — emit a ``prompt`` EventLog
      // entry so the writer sees "you: /library" land in the
      // timeline when they run a command. Matches the freeform-chat
      // promise emission from useChatBridge so the audit trail is
      // consistent: every writer action (chat or command) shows up
      // as a row. Fire-and-forget; the dispatch can still fail with
      // a string return and the prompt event stays — that's the
      // honest audit ("you tried /jump §99", even though it didn't
      // match a section).
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId: null,
          runId: state.runId,
          payload: { kind: "prompt", text: rawText },
        },
      });
      switch (intent.kind) {
        case "navigate": {
          const nav = intent.intent;
          if (nav.t === "spine" || nav.t === "timeline") {
            dispatch({ type: "VIEW_MODE_SET", mode: nav.t });
            return null;
          }
          if (nav.t === "jumpSection") {
            const match = resolveJumpTarget(sections, nav.sectionId);
            if (!match) return buildJumpErrorMessage(nav.sectionId);
            setFocusedSectionId(match.id);
            navigateToSection(match.number).catch(() => {});
            return null;
          }
          if (nav.t === "next") {
            const target = resolveNextTarget(sections);
            if (!target) return "No sections to jump to yet.";
            setFocusedSectionId(target.id);
            navigateToSection(target.number).catch(() => {});
            return null;
          }
          if (nav.t === "latest") {
            dispatch({ type: "VIEW_MODE_SET", mode: "timeline" });
            return null;
          }
          return null;
        }
        case "filter":
          dispatch({ type: "TIMELINE_FILTER_APPLIED", intent: intent.intent });
          dispatch({ type: "VIEW_MODE_SET", mode: "timeline" });
          return null;
        case "act": {
          const act = intent.intent;
          if (act.t === "draft") {
            if (state.state !== "idle") {
              return "A run is already in flight. Type /stop first, then /draft.";
            }
            if (planReviewActive) {
              return "Plan review is open. Approve, modify, or skip first, then /draft.";
            }
            if (sectionPickerActive) {
              return "Section picker is already open — finish there first.";
            }
            // Phase 7.2 — /draft orchestration: ask only for the
            // missing piece(s) and start when both are set.
            runDraftCommand();
            return null;
          }
          if (act.t === "replay") {
            dispatch({ type: "VIEW_MODE_SET", mode: "timeline" });
            const next = state.replayCursor === null ? 0 : state.replayCursor + 1;
            const atOrPastEnd = next >= timelineEntries.length;
            dispatch({
              type: "REPLAY_CURSOR_SET",
              index: atOrPastEnd ? null : next,
            });
            return null;
          }
          if (act.t === "export") {
            const all = state.eventLog.entries;
            const content = act.format === "csv" ? toCsv(all) : toJson(all);
            const mime = act.format === "csv" ? "text/csv;charset=utf-8" : "application/json;charset=utf-8";
            const name = exportFilename(act.format, state.runId);
            const ok = triggerDownload(name, mime, content);
            if (!ok) {
              return "Download could not start — your browser blocked it or there's no document context.";
            }
            return null;
          }
          if (act.t === "stop") {
            if (state.state !== "mid-run" && state.state !== "approval" && state.state !== "paused") {
              return "No run to stop — you're not in a run right now.";
            }
            void handleStopRun();
            return null;
          }
          if (act.t === "web") {
            // Canned prompt that triggers search_literature via the
            // chat bridge. Writer's approval modal fires before any
            // external call. Empty query → honest nudge.
            if (!act.query || act.query.length === 0) {
              return "Add a query — try /web ICH E3 §2.1";
            }
            const target: ChatDispatchTarget = {
              sectionNumber: state.activeSection?.sectionNumber ?? undefined,
              sectionId: state.activeSection?.sectionId ?? null,
            };
            const documentScope = {
              kind: "document" as const,
              label: "doc",
              hint: "Whole document",
            };
            const err = await chat.sendChat(
              `Search external literature for: ${act.query}. Use the search_literature tool.`,
              documentScope,
              target,
            );
            return err;
          }
          if (act.t === "citations") {
            // Scopes to the focused section when one is open,
            // otherwise whole document.
            const focusedNow = focusedSectionId ? (sections.find((s) => s.id === focusedSectionId) ?? null) : null;
            const focusedNumber = focusedNow?.number;
            const focusedId = focusedNow?.id;
            const target: ChatDispatchTarget = {
              sectionNumber: focusedNumber,
              sectionId: focusedId ?? null,
            };
            const scope = focusedNumber
              ? {
                  kind: "cursor" as const,
                  sectionNumber: focusedNumber,
                  label: `§${focusedNumber}`,
                  hint: "Currently inspecting this section",
                }
              : {
                  kind: "document" as const,
                  label: "doc",
                  hint: "Whole document",
                };
            const prompt = focusedNumber
              ? `List every citation used in §${focusedNumber}. Include the source title and where it appears.`
              : "List every citation used in this document. Group them by section and include source titles.";
            const err = await chat.sendChat(prompt, scope, target);
            return err;
          }
          if (act.t === "images") {
            // Open the Image Search view (browse + insert the library's figures).
            if (!imagesLibraryId) {
              return "Pick a library first — figures are listed per library.";
            }
            dispatch({ type: "VIEW_MODE_SET", mode: "images" });
            return null;
          }
          if (act.t === "abbreviations") {
            // Build the abbreviations table from the drafted SSOT. The
            // stream drives all UI (live tool-step rows → an Insert-able
            // table bubble); fire-and-forget so the dispatcher returns
            // immediately. Works on any run with stable drafted bodies —
            // a completed run OR a paused one (mirrors abbreviationsActItem).
            if (!state.runId || (state.state !== "post-run" && state.state !== "paused")) {
              return "The abbreviations table needs some drafted sections — finish or pause a run first.";
            }
            void chat.runAbbreviations(state.runId);
            return null;
          }
          if (act.t === "regen") {
            // Full-section redraft via the proposal-mode pipeline —
            // the explicit replacement for the retired regen_section
            // chat intent. Shared with the "Regenerate this section" chip.
            if (!act.guidance) {
              return "Add your comments — e.g. /regen tighten the tone and expand the AE narrative.";
            }
            return redraftCursorSection(act.guidance);
          }
          return buildComingSoonMessage(act.t);
        }
        case "pick": {
          const pick = intent.intent;
          if (pick.t === "library") {
            // Require doctype before library selection. If not set,
            // open the doctype picker so the writer completes that first.
            if (!hasDocType) {
              setDoctypePickerOpen(true);
              return null;
            }
            openLibraryPicker();
            return null;
          }
          if (pick.t === "workspace") {
            if (pick.workspaceId === null) {
              if (workspaces.length <= 1) {
                return "Only one workspace available — nothing to switch to.";
              }
              return "Pick a workspace from the menu, or type /workspace <name>.";
            }
            const byId = workspaces.find((w) => w.id === pick.workspaceId);
            const byName = workspaces.find((w) => w.name.toLowerCase() === pick.workspaceId?.toLowerCase());
            const match = byId ?? byName;
            if (!match) {
              return `Workspace "${pick.workspaceId}" not found.`;
            }
            if (match.id === selectedWorkspaceId) {
              return `"${match.name}" is already the active workspace.`;
            }
            handleSelectedWorkspaceIdChange(match.id);
            return null;
          }
          if (pick.t === "mode") {
            if (state.state === "mid-run" || state.state === "approval" || state.state === "paused") {
              return "Finish or /stop the current run before switching mode.";
            }
            if (pick.target !== null) {
              const currentIsSandbox = !!appConfig?.sandbox;
              const wantSandbox = pick.target === "sandbox";
              if (currentIsSandbox === wantSandbox) {
                return `Already in ${pick.target.toUpperCase()} mode.`;
              }
            }
            void handleModeToggle();
            return null;
          }
          if (pick.t === "upload") {
            // /upload is an alias of /library — both open the
            // Library Hub. Kept as a distinct intent so the slash
            // menu offers "upload" as a discoverable verb (writers
            // thinking "I want to upload a file" shouldn't have to
            // know the Hub's canonical name).
            // # DECISION: /upload and /library route to the exact
            // #   same handler — one action under two muscle-
            // #   memory keystrokes.
            // # WHY: writer decision (2026-04-23). Previously
            // #      /upload opened a separate Manager modal; the
            // #      duplication made the UI feel scattered.
            // # DATE: 2026-04-23
            //
            // Guards:
            //   - Library must be selected — the Hub needs one to
            //     fetch inventory against.
            //   - Mid-run + approval are locked — uploading during
            //     a live run would race Asthra's reads.
            if (!selectedLibraryId) {
              return "Pick a library first — type /library or /draft.";
            }
            if (state.state === "mid-run" || state.state === "approval" || state.state === "paused") {
              return "Library is locked while a run is live — /stop first or wait for it to finish.";
            }
            openLibraryPicker();
            return null;
          }
          return null;
        }
        case "system": {
          const sys = intent.intent;
          if (sys.t === "settings") {
            return "Settings isn't wired in Loom/Thread yet — use the classic UI for now.";
          }
          if (sys.t === "logout") {
            void handleLogout();
            return null;
          }
          return null;
        }
      }
    },
    [
      sections,
      state.replayCursor,
      state.eventLog.entries,
      state.runId,
      state.state,
      state.activeSection?.sectionNumber,
      state.activeSection?.sectionId,
      timelineEntries.length,
      planReviewActive,
      sectionPickerActive,
      handleStopRun,
      handleSelectedWorkspaceIdChange,
      handleModeToggle,
      handleLogout,
      workspaces,
      selectedWorkspaceId,
      appConfig?.sandbox,
      focusedSectionId,
      chat,
      hasDocType,
      redraftCursorSection,
    ],
  );

  // ───────────────────────────────────────────────────────────────────
  // Save button — flushes pending session patches and any buffered
  // write-back-bridge edits in one go. Gives the writer a single
  // affordance for "all my changes are committed" without depending on
  // debounce windows. ``saveState`` drives the TabBar label so a brief
  // "Saved" confirmation flashes after a successful flush.
  // ───────────────────────────────────────────────────────────────────
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  // Live progress for the floating SaveProgressCard: which checkpoint is in
  // flight (0 prepare → 1 read → 2 save → 3 issues; 4 = all done) and the 0–100
  // percent driving the progress bar. The whole save is one bulk request now, so
  // the bar is eased optimistically during the in-flight save and snapped to 100
  // on completion (no per-section counter).
  const [saveStepIndex, setSaveStepIndex] = useState(-1);
  const [savePercent, setSavePercent] = useState(0);
  // Re-entrancy guard: the Save button double-click AND the post-approve
  // auto-save can otherwise overlap, interleaving Word reads.
  const saveBusyRef = useRef(false);
  const handleSave = useCallback(async () => {
    if (saveBusyRef.current) return;
    saveBusyRef.current = true;
    const _saveT0 = performance.now();
    // Braced body so the console call is a statement rather than the
    // arrow's return value — see the same note in writeBackBridge.ts.
    // A concise arrow survives the production console strip.
    const _sstep = (m: string): void => {
      console.info(`[Save·shell +${Math.round(performance.now() - _saveT0)}ms] ${m}`);
    };
    _sstep("Save clicked");
    setSaveState("saving");
    setSaveError(null);
    setSaveStepIndex(0); // Preparing
    setSavePercent(8);
    try {
      flushAllPendingSaves();
      // Suspend the typing auto-save (syncEngine) for the duration: it runs its
      // own whole-document Word read OUTSIDE the single-writer queue, so a
      // keystroke ≤1.5 s before the click would race the Save scan with a
      // second full read. Save is a complete sweep, so nothing pending is lost.
      // Resumed in ``finally`` (same pair the Load path uses).
      pauseSync();
      _sstep("flushed pending auto-saves + paused typing auto-save; loading writeBackBridge");
      const { captureAndSaveSections } = await import("./services/writeBackBridge");
      // SSOT write-through (2026-07-14): capture the live Word content of every
      // inserted section and persist the CHANGED ones straight to Mongo —
      // markdown to ``draft_full`` (the markdown-only SSOT, 2026-08-05; the
      // formatting overlay was dropped as the main Save hang). Changed-sections-
      // only, last-write-wins; the ledger reconcile marks writer-deleted markers
      // as resolved. The document is scanned ONCE (text only) behind the write
      // queue (via ``enqueueWordWrite``) so the read can't interleave with
      // in-flight section inserts; the per-section ``/edit`` writes run outside
      // the lock. ``onProgress`` drives the floating SaveProgressCard.
      if (state.runId) {
        const runId = state.runId;
        // Hand the live spine's section numbers so the SSOT fetch and the Word
        // document scan run CONCURRENTLY (halves the pre-save wait). Read via
        // ref so handleSave isn't rebuilt on every spine update.
        const knownSectionNumbers = sectionsRef.current
          .map((s) => s.number)
          .filter((n): n is string => !!n && n !== "TITLE");
        const counts = await captureAndSaveSections(runId, {
          enqueueWordWrite,
          knownSectionNumbers,
          onProgress: (p) => {
            if (p.phase === "reading") {
              setSaveStepIndex(1); // Reading your document
              setSavePercent(p.percent);
            } else {
              setSaveStepIndex(2); // Saving changes
              setSavePercent(p.percent);
            }
          },
        });
        console.info(
          `[Save] sections → SSOT: ${counts.saved} saved, ${counts.skippedUnchanged} unchanged, ` +
            `${counts.skippedMissing} missing, ${counts.skippedTrackChanges} track-changes, ` +
            `${counts.conflicted} changed-by-someone-else, ${counts.failed} failed`,
        );
        // Failure surfacing (stabilization, 2026-07-24): a Save that could
        // not compare (fetch failed) or dropped sections must NOT flash
        // "Saved" — that reads as durability the SSOT doesn't have.
        if (counts.captureFailed) {
          // Word-side capture threw (e.g. a RichApi InvalidArgument while
          // reading section bodies) — the request never reached the server, so
          // don't mislabel it as a connectivity problem.
          setSaveError("Couldn't read the document to save — please try again. If it persists, reload the doc.");
          setSaveState("error");
          return;
        }
        if (counts.fetchFailed) {
          setSaveError("Save couldn't reach the server — nothing was compared. Try again.");
          setSaveState("error");
          return;
        }
        // Reported before ``failed``, and worded nothing like it. A failure means
        // *try again*; a conflict means somebody else's work is in that section
        // and yours is not. Telling a writer to retry a conflict is how the other
        // edit gets destroyed on the second press — which, before this Save
        // presented a version guard at all, is what happened on the first.
        if (counts.conflicted > 0) {
          const many = counts.conflictedSections.length !== 1;
          setSaveError(
            `Section${many ? "s" : ""} ${counts.conflictedSections.join(", ")} ` +
              `${many ? "were" : "was"} changed by someone else while you were editing, so ` +
              `${many ? "they were" : "it was"} not overwritten. Your text is still in this ` +
              `document — reload to see their version before saving again.`,
          );
          setSaveState("error");
          return;
        }
        if (counts.failed > 0) {
          setSaveError(
            `Save failed for section${counts.failedSections.length === 1 ? "" : "s"} ` +
              `${counts.failedSections.join(", ")} — content is still in the doc; try Save again.`,
          );
          setSaveState("error");
          return;
        }
        if (counts.skippedMissing > 0) {
          // Benign but worth telling the writer: those sections weren't
          // found in the open doc (renamed heading / never inserted), so
          // their SSOT copy was left untouched.
          console.warn(`[Save] ${counts.skippedMissing} section(s) not found in the open doc — SSOT left as-is`);
        }
        if (counts.skippedTrackChanges > 0) {
          // Track Changes is ON, so ``paragraph.text`` can fold in deleted text;
          // without an OOXML capture to derive the accepted text those sections
          // were left as-is rather than saving revision-polluted markdown.
          console.warn(`[Save] ${counts.skippedTrackChanges} section(s) skipped — Track Changes is on`);
          setSaveError(
            `${counts.skippedTrackChanges} edited section${counts.skippedTrackChanges === 1 ? "" : "s"} couldn't be saved while Track Changes is on — accept or reject your changes (or turn Track Changes off), then Save again.`,
          );
          setSaveState("error");
          return;
        }
        // The server reconciled the issue ledger on each ``/edit`` (writer-
        // deleted markers → resolved, new ones → open); re-fetch so the Issues
        // badge + Data Gaps panel reflect it without waiting for another
        // trigger (matches the accepted-edit path's refresh).
        setSaveStepIndex(3); // Updating issues
        setSavePercent(92);
        setIssuesRefreshSignal((n) => n + 1);
        _sstep(`persist done: ${counts.saved} saved, ${counts.failed} failed — refreshing issues`);
      }
      setSaveStepIndex(4); // all done → "Saved ✓"
      setSavePercent(100);
      setSaveState("saved");
      _sstep("Save complete ✓");
      window.setTimeout(() => {
        setSaveState("idle");
        setSaveStepIndex(-1);
      }, 1500);
    } catch (err) {
      console.warn("[Save] flush failed:", err);
      setSaveError("Save failed — try again.");
      setSaveState("error");
    } finally {
      resumeSync(state.runId);
      saveBusyRef.current = false;
    }
  }, [state.runId, enqueueWordWrite]);

  // Optimistic ease for the Save bar: the persist is ONE bulk request with no
  // intermediate acks, so while the "Saving changes" step is in flight nudge the
  // bar toward ~88% (it snaps to 100 on completion). Cheap timer, cleared when
  // the step advances or the save ends.
  useEffect(() => {
    if (saveState !== "saving" || saveStepIndex !== 2) return;
    const id = window.setInterval(() => {
      setSavePercent((p) => (p < 88 ? Math.min(88, p + 3) : p));
    }, 160);
    return () => window.clearInterval(id);
  }, [saveState, saveStepIndex]);

  // Section-scoped auto-save: an accepted highlight edit or full-section
  // proposal persists just its own section (markdown → ``draft_full`` +
  // formatting → ``section_ooxml``) rather than sweeping the whole document.
  // The scoped save hash-compares, so the just-applied section is the only
  // real write; other sections are untouched. Routed through the Word write
  // queue so its section reads can't interleave with in-flight inserts.
  const autoSaveModule = useCallback(
    async (moduleId: string | null) => {
      if (!moduleId || !state.runId) {
        // No module id to scope to — fall back to the full-document sweep so
        // an approval never silently skips persistence.
        void autoSaveRef.current?.();
        return;
      }
      const runId = state.runId;
      try {
        const { captureAndSaveOneModule } = await import("./services/writeBackBridge");
        await enqueueWordWrite(() => captureAndSaveOneModule(runId, moduleId));
      } catch (err) {
        console.warn("[auto-save] scoped module save failed:", err);
      }
    },
    [state.runId, enqueueWordWrite],
  );

  // Post-approve auto-save: chat-driven section changes (adhoc /
  // redraft / edit proposals + highlight edits) trigger a Save
  // automatically so the SSOT stays proactively synced. Ref
  // pattern because the decision handlers are declared ~2600 lines
  // above these callbacks (same as adhocProposalOfferRef).
  useEffect(() => {
    autoSaveRef.current = handleSave;
    autoSaveModuleRef.current = autoSaveModule;
    return () => {
      autoSaveRef.current = null;
      autoSaveModuleRef.current = null;
    };
  }, [handleSave, autoSaveModule]);

  return (
    <div className={styles.root}>
      <TopBarC
        mode={appConfig?.sandbox ? "sandbox" : "live"}
        onModeToggle={state.state === "idle" || state.state === "post-run" ? handleModeToggle : undefined}
        modeToggling={modeToggling}
        // Phase 4 — visible logout affordance. Re-uses the same
        // ``handleLogout`` previously reachable only via the
        // ``/logout`` slash command.
        onLogout={handleLogout}
        // Billing & usage surface — plan, provider portal, payment history.
        onOpenBilling={() => setBillingOpen(true)}
        // Identity label so the writer can confirm WHICH account is
        // signed in. Sourced from App via prop drilling — App owns
        // the auth SSOT (getSessionUsername); the shell just relays.
        userIdentifier={userIdentifier ?? null}
        // LIVE/SANDBOX pill + model-backend switcher — internal
        // plumbing, hidden unless show.internal-tools says otherwise.
        showInternalTools={showInternalTools}
        // "DEV" instead of "LIVE" on the pill when this bundle is served by
        // the Vite dev server (pnpm dev) rather than the compose build.
        devBuild={isDevBuild}
        // Live token counter in header. Shows real-time accumulation
        // during active runs; displays zero when idle.
        liveTokens={liveTokens}
      />
      {/* Spec 34 — chat-first ContextChipsBar replaces the legacy
          ContextStrip breadcrumb. Library chip owns its dropdown
          internally; "Manage all sources →" opens the new
          LibraryHubSlideOver below. Hide alongside the other
          chrome during full-body takeovers (starting overlay /
          section-picker / plan-review) AND during Home landing
          (which is a clean full-screen scene with no tabs/composer). */}
      {!takeoverActive && !isHomeSceneActive && (
        <ContextChipsBar
          workspace={activeWorkspace}
          onWorkspaceClick={openWorkspacePicker}
          doctypeLabel={contextDocType.value ?? null}
          onDoctypeClick={openDoctypePickerStandalone}
          activeLibrary={activeLibraryForChips}
          onManageLibraries={() => {
            const mode = selectedLibraryId ? "list" : "library-list";
            openSlideOver(mode);
          }}
          libraryDisabled={!hasDocType}
          libraryDisabledTooltip="Select the Document Type first"
        />
      )}
      {!takeoverActive && (showSpineTab || showIssuesTab || showDraftsTab || showImagesTab) && !isHomeSceneActive && (
        <TabBar
          activeTab={activeTab}
          onTabChange={(tab) =>
            handleViewModeChange(
              tab === "spine"
                ? "spine"
                : tab === "issues"
                  ? "issues"
                  : tab === "drafts"
                    ? "drafts"
                    : tab === "images"
                      ? "images"
                      : "timeline",
            )
          }
          showSpine={showSpineTab}
          showIssues={showIssuesTab}
          issuesCounter={issuesCounter}
          showDrafts={showDraftsTab}
          showImages={showImagesTab}
          spineCounter={spineCounter}
          // Phase 9.1 — pulsing indicator on the Spine tab while a
          // run is drafting. Mid-run is the only state that emits
          // continuous activity; approval pauses progress (writer
          // intervention required) and post-run is terminal — both
          // suppress the dot. Drives off the same lifecycle
          // discriminator the rest of the shell uses.
          spineActive={isActiveRun(state.state)}
          onSave={handleSave}
          saveBusy={saveState === "saving"}
          saveLabel={saveState === "saved" ? "Saved" : saveState === "error" ? "Save failed — retry" : "Save"}
          saveError={saveState === "error" ? saveError : null}
          // Block Save while a live draft is in flight — snapshotting a
          // half-built draft would leave the persisted state mid-section.
          // ``isActiveRun`` covers mid-run + pausing; idle/approval/
          // paused/post-run leave the button enabled.
          saveDisabled={isActiveRun(state.state)}
          saveDisabledTooltip="Come back when the draft is complete to save work on top of it!"
        />
      )}
      {/* Floating Save progress card — narrates the whole-document sweep
          (prepare → read → save → update issues → done) so a multi-second Save
          is legible instead of a frozen "Saving…" label. */}
      {saveState !== "idle" && (
        <SaveProgressCard
          steps={buildSaveSteps(saveStepIndex)}
          error={saveState === "error" ? saveError : null}
          done={saveState === "saved"}
          percent={savePercent}
          onDismiss={() => {
            setSaveState("idle");
            setSaveStepIndex(-1);
            setSavePercent(0);
            setSaveError(null);
          }}
        />
      )}
      {/* Floating Load progress card — narrates the spine "Load doc" sweep
          (prepare → download → rebuild → restore → spine) so a multi-second
          load is legible instead of a dimmed button. */}
      {loadDocStepIndex !== -1 && (
        <LoadProgressCard
          steps={buildLoadSteps(loadDocStepIndex)}
          error={loadDocError}
          done={loadDocDone}
          onDismiss={() => {
            setLoadDocStepIndex(-1);
            setLoadDocError(null);
            setLoadDocDone(false);
          }}
        />
      )}
      {/* Floating "Build on this document" progress card — narrates the custom
          import sweep (read outline → create project → build spine → load
          sections) after the writer picks a doctype + library. Renders in place
          of the live-run booting card, which the custom flow suppresses. */}
      {customImportStep !== -1 && (
        <LoadProgressCard
          steps={buildCustomImportSteps(customImportStep)}
          error={customImportError}
          done={customImportDone}
          header="Building your document"
          errorHeader="Couldn't build your document"
          doneHeader="Ready"
          onDismiss={() => {
            setCustomImportStep(-1);
            setCustomImportError(null);
            setCustomImportDone(false);
          }}
        />
      )}
      {/* Bulk resolve control bar — pause/stop/resume a resolve pass. Separate
          from SpineControlBar (which controls the drafting run) because a
          resolve pass never enters the reducer's mid-run state. */}
      {!takeoverActive && activeTab === "spine" && (
        <ResolveControlBar
          phase={resolveIssuesFlow.phase}
          progress={resolveIssuesFlow.progress}
          remainingCount={resolveIssuesFlow.remainingCount}
          onPause={resolveIssuesFlow.pause}
          onStop={resolveIssuesFlow.stop}
          onResume={resolveIssuesFlow.resume}
          onDiscard={resolveIssuesFlow.discard}
        />
      )}
      {/* Spine control bar — pause/resume/stop buttons under the spine tab. */}
      {!takeoverActive &&
        activeTab === "spine" &&
        (state.state === "mid-run" ||
          state.state === "approval" ||
          state.state === "pausing" ||
          state.state === "paused") && (
          <SpineControlBar
            runState={state.state === "paused" ? "paused" : state.state === "pausing" ? "pausing" : "mid-run"}
            onPause={state.state === "mid-run" || state.state === "approval" ? handlePauseRun : undefined}
            onResume={state.state === "paused" ? handleResumePausedRun : undefined}
            onShowKillConfirmation={
              state.state === "mid-run" ||
              state.state === "approval" ||
              state.state === "pausing" ||
              state.state === "paused"
                ? () => setShowKillConfirmation(true)
                : undefined
            }
            disabled={state.state === "pausing"}
          />
        )}
      {/* Variant D Phase 3 + Phase 4 — persistent library bar between
          the tab strip and chat content. Renders on the Chat tab when
          there is EITHER a committed activeSelection OR a lined-up
          library (selectedLibraryId resolves to a known library
          object). Phase 4 added the lined-up case so the bar tracks
          the same in-progress signal the ContextStrip's tentative
          chip already surfaces; ``tentative`` styles the bar
          accordingly. Hidden during full-body takeovers for the
          same reason TabBar/ContextStrip hide. */}

      {/* Mode-toggle error banner. Renders when the backend rejects
          a sandbox/live flip (typically the 409 active-run guard).
          Without this, the pill silently doesn't change and the
          writer thinks it's broken. Dismissable. */}
      {modeToggleError && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "#FEF2F2",
            borderBottom: "1px solid #FCA5A5",
            color: "#7F1D1D",
            fontSize: "11.5px",
            fontFamily: fonts.body,
            flexShrink: 0,
          }}
        >
          <span style={{ flex: 1 }}>{modeToggleError}</span>
          <button
            type="button"
            onClick={() => dismissModeToggleError()}
            style={{
              background: "transparent",
              border: "none",
              color: "#7F1D1D",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0 4px",
            }}
            aria-label="Dismiss mode toggle error"
          >
            ×
          </button>
        </div>
      )}
      {planModeToggleError && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "#FEF2F2",
            borderBottom: "1px solid #FCA5A5",
            color: "#7F1D1D",
            fontSize: "11.5px",
            fontFamily: fonts.body,
            flexShrink: 0,
          }}
        >
          <span style={{ flex: 1 }}>{planModeToggleError}</span>
          <button
            type="button"
            onClick={() => dismissPlanModeToggleError()}
            style={{
              background: "transparent",
              border: "none",
              color: "#7F1D1D",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0 4px",
            }}
            aria-label="Dismiss plan mode toggle error"
          >
            ×
          </button>
        </div>
      )}
      {/* Stop-unconfirmed warning. Raised by handleStopRun when the DELETE
          cancel could not be confirmed against the backend after retries —
          the run may still be drafting server-side. Amber (warn), not red:
          the local UI did reset, but the writer should retry the stop. */}
      {stopWarning && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "#FFFBEB",
            borderBottom: "1px solid #FCD34D",
            color: "#78350F",
            fontSize: "11.5px",
            fontFamily: fonts.body,
            flexShrink: 0,
          }}
        >
          <span style={{ flex: 1 }}>{stopWarning}</span>
          <button
            type="button"
            onClick={() => setStopWarning(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "#78350F",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0 4px",
            }}
            aria-label="Dismiss stop warning"
          >
            ×
          </button>
        </div>
      )}
      {body}
      {/* 2026-05-06 — bridge-driven user-input requests (the agent
          asking for approval before doing something — e.g.
          LiteratureSearchGate from "Find newer evidence" chip,
          WriteGate from drafting, future request_user_input calls).
          Sourced from useUserInputBridge.pendingApproval, NOT from
          state.pendingApproval (which is for section-write approvals
          rendered inside TimelineView).

          # DECISION: render at shell-level, above CommandRail.
          # WHY: section-write approvals (state.pendingApproval) are
          #   already rendered inside TimelineView/LoomSpine. But
          #   agent-initiated user_input requests (literature search
          #   approval, generic free_text, future request_user_input
          #   kinds) come from useUserInputBridge — a distinct state
          #   source. Wiring them inside TimelineView would cross-
          #   couple two distinct approval pathways and violate SRP.
          #   Rendering at shell level above the rail keeps the
          #   boundary clean: TimelineView owns section-write
          #   approvals; this row owns bridge-driven approvals. The
          #   placement above the rail also sits the textarea right
          #   where the writer's next action is required.
          # ALTERNATIVE: route bridge approvals through state.* via
          #   a reducer action — rejected; doubles the SSOT (bridge
          #   already owns the state, reducer would mirror it).
          # DATE: 2026-05-06

          UserInputInlineRow returns null when ``request`` is null,
          so no chrome leaks into the layout when nothing's pending.
          Write approvals with a parseable payload render IN the chat
          thread through the shared ProposalCard (writeApprovalNode)
          instead — suppressed here so the decision has one surface. */}
      <UserInputInlineRow
        request={writeApprovalHandledInThread ? null : pendingApproval}
        onApprove={submitApproval}
        onReject={rejectApproval}
      />
      {/* DisclosureBanner retired with Spec 29 Phase 1; its copy
          relocates into the agent's greeting message (Phase 3).
          CommandRail is the chat surface below the body.

          # DECISION (2026-05-06): hide CommandRail ONLY when the
          # LoomSpine surface is actually rendering (writer is on
          # the Spine tab during an active or completed run). At
          # idle / plan-review / pre-run, the rail must be visible
          # so the writer can type /draft, /library, or chat with
          # the assistant. The initial value of state.viewMode is
          # "spine" (see state.ts:289) — gating on viewMode alone
          # would hide the rail at idle and break the welcome
          # surface. Gating on (viewMode === "spine" && runId) ties
          # the hide condition to the actual LoomSpine render, since
          # LoomSpine is only mounted when state.state is run-
          # related (and runId is non-null in those states).
          # WHY: the rail's affordances are all conversational —
          # slash commands, chat send, suggestion chips, citation
          # popover. None belong on the spine view (section-status
          # canvas). But idle / plan-review / post-run pre-tab
          # surfaces still need the rail.
          # ALTERNATIVE: change initialState.viewMode from "spine"
          # to "timeline". Rejected — would change first-tab default
          # for active runs too, which might surprise writers who
          # expect spine-first when a run starts.
          # DATE: 2026-05-06
          #
          # The rail is ALSO hidden on the run-agnostic browsing
          # tabs — "drafts" and "images" — where it is not a chat
          # surface and would otherwise render over the panel body. */}
      {!(state.viewMode === "spine" && state.runId) &&
        state.viewMode !== "drafts" &&
        state.viewMode !== "images" &&
        !isHomeSceneActive && (
          <CommandRail
            ref={commandRailRef}
            assistantMessage={assistantMessage}
            stats={stats}
            restoreHydrationProgressPct={restoreHydrationProgressPct}
            onRestoreHydrationSync={handleResyncHydration}
            // Spec 26 C2 — Inspector wins; cursor section fills in when no
            // inspector is open so the chip reads §X.X.X while the writer
            // browses the doc post-draft. Cursor section is sourced by a
            // debounced + run-gated subscriber that calls the single-sync
            // detectCursorSectionLight (ASTHRA_SEC_* bookmark lookup), so it
            // does NOT reintroduce the old useDocumentCursor freeze.
            // handleSendChat still does a fresh on-demand cursor read at
            // chat-send time as ground truth.
            // In post-run, fallback to first drafted section if neither inspector
            // nor cursor has a section, so suggestion chips always have context.
            inspectorSectionNumber={commandRailInspectorSectionNumber}
            cursorSectionNumber={effectiveCursorSectionNumber}
            onScopeChipClick={docConfig ? handleScopeChipClick : undefined}
            // Spec 26 C6 — citation popover driven by useSelectionCitations.
            citations={selectionCitations.citations}
            citationsLoading={selectionCitations.isLoading}
            citationsError={selectionCitations.error}
            citationsFetchedText={selectionCitations.fetchedText}
            citationsStatus={selectionCitations.status}
            citationsMessage={selectionCitations.message}
            citationsTransactionIdsUsed={selectionCitations.transactionIdsUsed}
            // 2026-05-06 — chips restoration. fetchNow drives both the
            // SuggestionChips "View citations for selected text" chip and
            // the CitationPopover error-state Retry button. Same
            // primitive — both are "fetch this selection's citations now"
            // intents, just surfaced from different UI affordances.
            // Hidden (not a dead button) when the document has no
            // (trial, session) identity — the lookup would silently no-op.
            onCite={selectionCitations.available ? selectionCitations.fetchNow : undefined}
            onCitationsDismiss={selectionCitations.clear}
            // Gates the SuggestionChips render in the rail. Pre-generation
            // (picker / setup screens) there is no drafted content to act
            // on. Once any section has been drafted, the chips apply.
            // Status set matches the existing post-draft predicate at L1927.
            hasDraftedContent={hasDraftedContent}
            // Context chips — selection actions read the live highlight;
            // section actions (redraft / edit) need a completed run.
            chipSelectionText={chipSelectionText}
            sectionActionsEnabled={state.state === "post-run" && Boolean(state.runId)}
            onRegenerateSection={handleRegenerateSectionChip}
            onResolveSectionFlags={handleResolveSectionFlagsChip}
            onSeeSectionFlags={handleSeeSectionFlagsChip}
            onResolveMissingInfo={handleResolveMissingInfoChip}
            offChatEditNotice={offChatEditNotice}
            onReviewEdit={() => handleViewModeChange("timeline")}
            // Spec 29 — slash commands are always wired, not gated on
            // run state. /draft is the primary idle-state command (no
            // run yet, writer needs it to open LoomFresh) and other
            // idle-hostile commands (/spine, /jump, /filter) are
            // handled per-command by handleCommand's dispatcher — each
            // returns a writer-facing error string when invoked in an
            // invalid state. Pre-Spec-29 the rail's input was disabled
            // in idle, which blocked /draft entirely.
            onCommand={handleCommand}
            // Spec 29 menu-merge — state-aware menu groups composed
            // from the shell's reducer + controlled state. Rail applies
            // filter-as-you-type on top.
            menuGroups={menuGroups}
            // Spec 29 follow-up — always wired. The rail no longer
            // silently ignores non-slash text; useChatBridge's sendChat
            // returns an honest error when docx_path isn't resolved yet
            // (idle / pre-run), so the writer sees what's needed rather
            // than nothing. Mid-run chat is still specced but blocked
            // by the backend read-only filter (Spec 28); useChatBridge's
            // internal guard returns the right copy there too.
            onSendChat={handleSendChat}
            chatStreamingStatus={
              chat.isStreaming
                ? buildStreamingStatus(
                    chat.liveTool,
                    chat.statusLabel,
                    chat.streamingIntent ? intentLabel(chat.streamingIntent) : null,
                  )
                : null
            }
            // Composer Stop button — wired only while a chat reply streams.
            // handleStopChat aborts the fetch (→ backend cancels the tool
            // loop), rejects any pending approval, and posts a "you stopped"
            // notice. The button reverts to Send once ``chat.isStreaming``
            // clears.
            onStopChat={chat.isStreaming ? handleStopChat : undefined}
            // Bridge lifecycle drives the rail's status pill + the soft
            // "queued" hint when the writer submits before the WS has
            // connected. ``queuedMessage`` is non-null only while a held
            // message is waiting for the next connect.
            bridgeStatus={bridgeStatus}
            chatQueuedMessage={chat.queuedMessage}
            // Spec 33 — v3 planner task list + progress. Empty/zero when
            // the v3 agent isn't engaged or hasn't emitted ``plan_ready``
            // yet, in which case CommandRail hides the strip entirely.
            chatTasks={chat.tasks}
            chatCompletedTasks={chat.completedTasks}
            chatTotalTasks={chat.totalTasks}
            chatCurrentTaskTitle={chat.currentTaskTitle}
            chatDraftingStatus={chat.draftingStatus}
            onInputFocus={() => {
              void emptyDocWatcherRef.current?.check();
              void captureSelection();
              void refreshCursorSectionNumber();
            }}
            selectionContext={capturedSelection}
            onDismissSelectionContext={dismissSelection}
            // State-aware: "stop" (mid-run / approval) → handleStopRun
            // cancels the backend run; "reset" (post-run) → handleReset
            // is just local cleanup.
            onStop={state.state === "mid-run" || state.state === "approval" ? handleStopRun : undefined}
            stopLabel={state.state === "mid-run" || state.state === "approval" ? "stop" : undefined}
            imageSelected={imageSelected}
            onReplaceImage={handleReplaceImage}
            // @-mention picker (attach source section/table). Same library
            // pick as useChatSystem: run selection wins, else the writer's
            // library choice.
            libraryId={activeSelection?.libraryId ?? (selectedLibraryId || null)}
          />
        )}
      {/* Spec 29 palette-merge — CommandPalette retired. The slash
          menu is now the single command surface; see commands/menu.ts
          and CommandMenu.tsx. */}

      {/* TOC / Section Navigator — opened by clicking the scope chip. */}
      {docConfig && (
        <TocPopup
          open={tocOpen}
          onClose={() => setTocOpen(false)}
          skeleton={docConfig.skeleton}
          activeSectionNumber={tocActiveSectionNumber}
          onHoverSection={setTocHoveredSectionNumber}
          onNavigate={(sectionNumber) => {
            setTocOpen(false);
            navigateToSection(sectionNumber).catch(() => {});
          }}
        />
      )}

      {/* Sole "fresh project" prompt — fires when the empty-doc watcher
          observes the writer cleared the document body. */}
      {newProjectConfirmOpen && (
        <NewProjectConfirmModal
          currentProjectLabel={activeSelection?.docName ?? activeSelection?.docCode ?? null}
          onConfirmRotate={handleConfirmRotateProject}
          onCancelRestore={handleCancelRestoreProject}
        />
      )}

      {customDraftPickerActive && (
        <CustomDraftPicker
          sections={sections}
          onCancel={() => setCustomDraftPickerActive(false)}
          onStart={(sectionIds) => {
            setCustomDraftPickerActive(false);
            if (!activeSelection || sectionIds.length === 0) return;
            // Route through the shared run-start primitive with the current run
            // id as ``continueRunId`` — useDraftFlow sees submissionMode==="custom"
            // and dispatches customDraftSections (which drafts each CUSTOM-N
            // section from its saved instruction), reusing this run's SSE stream.
            void actuallyStartRun(activeSelection, null, sectionIds, state.runId);
          }}
        />
      )}

      {importExistingDocOpen && (
        <ImportExistingDocModal
          onConfirmImport={() => {
            setImportExistingDocOpen(false);
            handleStartCustomImport();
          }}
          onStartFresh={() => {
            setImportExistingDocOpen(false);
            handleContextCardStartFreshDraft();
          }}
          onDismiss={() => setImportExistingDocOpen(false)}
        />
      )}

      {/* Native checkout — the order to confirm, then the payment watch. Both
          only mount for a paid (custom-workspace) draft; a demo/exempt draft
          never opens them. Prices shown are doc_pricing.json's; the charge is
          server-side. */}
      <OrderDialog
        order={orderRequest}
        busy={checkout.phase.kind !== "idle"}
        onPay={() => {
          const cart = pendingCartRef.current;
          if (cart) void checkout.begin(cart.items, cart.run);
        }}
        onCancel={() => {
          setOrderRequest(null);
          pendingCartRef.current = null;
          checkout.reset();
        }}
      />
      <CheckoutDialog
        phase={checkout.phase}
        onCheckNow={checkout.checkNow}
        onRetryDraft={() => void checkout.retryDraft()}
        onClose={() => {
          checkout.reset();
          setOrderRequest(null);
          pendingCartRef.current = null;
        }}
      />
      {billingOpen && <BillingDialog onDismiss={() => setBillingOpen(false)} />}

      {/* Table-artefact builder — tag source tables, describe a transform,
          preview the derived table, and (only on approval) save it into the
          library as a CSV. The build call is preview-only; save reuses
          uploadContent(..., "text/csv"). */}
      {artefactBuilderOpen && (
        <ArtefactBuilderPanel
          libraryId={selectedLibraryId}
          building={artefactBuilding}
          buildError={artefactBuildError}
          stages={artefactStages}
          onCancel={handleCloseArtefact}
          onBuild={handleBuildArtefact}
        />
      )}
      {artefactPreview && (
        <ArtefactPreviewModal
          preview={artefactPreview}
          saving={artefactSaving}
          saveError={artefactSaveError}
          onCancel={handleCloseArtefact}
          onApprove={handleApproveArtefact}
        />
      )}

      {/* Spec 23 P10.5 — single-section regen dialog. Opens from the
          Inspector's "Regen" button (post-run only). Confirm
          dispatches the regen EventLog entry and starts a fresh
          run scoped to that one section via actuallyStartRun
          (same primitive Draft more sections uses). */}
      {regenTarget && <RegenOne target={regenTarget} onConfirm={handleRegenConfirm} onCancel={handleRegenCancel} />}

      {/* Bulk "Resolve issues" wizard — opened from the global Issues tab's
          "Resolve selected". On Start it runs the orchestrator live on the
          spine (useResolveIssuesFlow) and closes. */}
      {resolveWizardTargets !== null && (
        <ResolveIssuesWizard
          targets={resolveWizardTargets}
          documents={inventoryDtoToView(libraryInventory).documents}
          busy={resolveIssuesFlow.busy}
          onCancel={() => setResolveWizardTargets(null)}
          onStart={(documentScope) => {
            const targets = resolveWizardTargets;
            setResolveWizardTargets(null);
            resolvePassRef.current = {
              targets: targets.map((t) => ({ module_id: t.module_id, number: t.number, issue_ids: t.issue_ids })),
              startedAt: Date.now(),
            };
            void resolveIssuesFlow.startResolve(targets, documentScope).then((accepted) => {
              if (!accepted) resolvePassRef.current = null;
            });
          }}
        />
      )}

      {/* Spec 23 P9 — back-after digest pill. Floats above the body,
          anchored bottom-left of the shell's positioning context.
          Renders only when (a) the hook caught a qualifying return
          AND (b) there's actually something wanting the writer —
          otherwise we're announcing nothing, which would just be
          noise. Both paths (Review click + × dismiss) acknowledge
          so the pill clears without auto-re-firing on the next
          visibility flicker. */}
      {presence.isBack && presence.awayMs !== null && thingsWantYouCount > 0 && (
        <DigestReturnPill
          awayMs={presence.awayMs}
          count={thingsWantYouCount}
          onReview={handleDigestReview}
          onDismiss={presence.acknowledge}
        />
      )}

      {/* Spec 34 — chat-first Library Hub slide-over. Opened by the
          ContextChipsBar's "Manage all sources →" / "Create new
          library" actions. Coexists with the legacy modal above
          during M7's additive integration; M5/M6 of the cleanup
          MR will retire the legacy modal once all entry points
          route through the slide-over. */}
      <LibraryHubSlideOver
        open={hubSlideOverOpen}
        onClose={() => setHubSlideOverOpen(false)}
        library={activeLibraryForChips}
        libraries={libraries}
        mode={hubSlideOverMode}
        rows={hubRows}
        workspaceName={activeWorkspace?.name ?? "workspace"}
        // Workspace switcher atop the library-list view. Changing it
        // persists the pick and cascades getLibraries(newWorkspaceId)
        // (useWorkspaceLibrary), so the ``libraries`` prop above updates
        // in place while the slide-over stays open.
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onSelectWorkspace={handleSelectedWorkspaceIdChange}
        // Live SSE progress for the in-Hub HubList. ``loading`` covers
        // the gap between fetch start and the first SSE event;
        // ``libraryResolveEvent`` carries the per-doc ticks after that.
        // ``finally`` block in refreshLibraryInventory clears both, so
        // the spinner disappears the moment ``done`` lands or the
        // stream errors.
        loading={libraryInventoryLoading || !!libraryResolveEvent}
        loadingMessage={resolveProgressLabel(libraryResolveEvent)}
        resolveEvent={libraryResolveEvent}
        // Run-gated Source↔Audit toggle: the active run's audit library, or null
        // when no run / feature off (the toggle + panel self-hide).
        runId={state.runId ?? null}
        auditLibrary={hubAuditLibrary}
        onRefreshAuditLibrary={loadHubAuditLibrary}
        docTypeOptions={deriveDocTypeOptions(getLibraryDoctype(selectedLibraryId), [])}
        onSelectLibrary={(id) => {
          const lib = libraries.find((l) => l.id === id);
          if (!lib) return;
          // Check /draft orchestration FIRST. If it consumes the
          // pick, emit with skipSuggestion=true to avoid a chat
          // flash before the planner opens.
          const consumed = advanceDraftWithLibrary(lib);
          if (consumed) {
            emitLibrarySelected(lib, false, true);
            setHubSlideOverOpen(false);
            return;
          }
          emitLibrarySelected(lib, false);
          setSelectedLibraryId(id);
          setHubSlideOverOpen(false);
        }}
        // Build-on-existing-document: the source library is optional, so offer
        // "Continue without a library" while the custom-import orchestration is
        // active. Absent (undefined) for a normal fresh draft — a library is
        // required there.
        onSkipLibrary={customImportActiveRef.current ? handleSkipLibraryForCustom : undefined}
        onInspectLibrary={(id) => {
          const lib = libraries.find((l) => l.id === id);
          if (!lib) return;
          setSelectedLibraryId(id);
        }}
        onCreateLibrary={async (input) => {
          // Create the library in the selected workspace, then make
          // it the active one so the slide-over's optimistic flip to
          // ``uploading`` (and handleNewHubAddFiles, which targets
          // ``selectedLibraryId``) drops files into THIS new library.
          const workspaceId = selectedWorkspaceId;
          if (!workspaceId) {
            console.error("[Library Hub] Cannot create library: no workspace selected.");
            return;
          }
          // Custom libraries belong in a custom workspace — the demo workspace's
          // sample libraries are shared and free. Block here and open the
          // workspace picker's "+ New workspace" flow. Throwing keeps the writer
          // on the create form with the message.
          if (activeWorkspaceExempt) {
            setWorkspacePickerOpen(true);
            throw new Error(
              "Create a custom workspace first — the demo workspace's sample libraries are shared and free.",
            );
          }
          const name = input.name.trim();
          if (!name) return;
          try {
            const result = await createLibrary(workspaceId, name);
            setSelectedLibraryId(result.library.id);
            // Best-effort: persist the writer's scope pick as the
            // library's target doctype. Only CSR/CMC/IB overlap the
            // TargetDoctype enum; Protocol/General are skipped by the
            // ``isTargetDoctype`` guard. Client-side only — advisory UX.
            if (isTargetDoctype(input.scope)) {
              setLibraryDoctype(result.library.id, input.scope);
            }
            // Re-fetch the workspace's library list so the brand-new
            // library shows up in library-list immediately (previously
            // it only appeared after a re-login / workspace switch).
            await refreshLibraries();
            await refreshLibraryInventory();
          } catch (err) {
            console.error("[Library Hub] Library creation failed:", err);
            // Rethrow so the slide-over keeps the writer on the create
            // form (with an inline error) instead of navigating to a
            // library that was never created.
            throw err;
          }
        }}
        onAddFiles={handleNewHubAddFiles}
        onCreateArtefact={handleCreateArtefact}
        onPickDoc={(_docId) => {
          // The slide-over flips to detail mode internally on row
          // pick; this callback is here for any cross-surface
          // analytics hook. No-op for now.
        }}
        onRefreshLibrary={() => {
          invalidateResolveGuard();
          return refreshLibraryInventory();
        }}
        onVerifyDoc={(input) => {
          console.warn(
            "[Spec 34 M7] onVerifyDoc received %o — wiring to the " +
              "approve/reclassify API lands in the next commit.",
            input,
          );
        }}
        onDeleteDoc={async (row) => {
          if (!selectedLibraryId || !row.doc_id) return;
          // A delete is already in flight for this doc — ignore the
          // duplicate call instead of hitting the API twice.
          if (deletingDocIdsRef.current.has(row.doc_id)) return;
          deletingDocIdsRef.current.add(row.doc_id);
          try {
            await deleteDocument(selectedLibraryId, row.doc_id);
            // Drop it from the in-memory inventory immediately so the
            // Hub list reflects the delete right away, instead of
            // waiting on the full SSE re-resolve below.
            removeDocumentFromInventory(row.doc_id);
            invalidateResolveGuard();
            await refreshLibraryInventory();
          } catch (error) {
            console.error("Failed to delete document:", error);
          } finally {
            deletingDocIdsRef.current.delete(row.doc_id);
          }
        }}
        onDownloadDoc={async (row) => {
          if (!row.doc_id) return;
          try {
            const { blob, filename } = await fetchDocumentDownload(row.doc_id);
            downloadBlob(blob, filename);
          } catch (error) {
            console.error("Failed to download document:", error);
          }
        }}
      />
      {/* Phase 7.2 — DoctypePicker: the canonical doctype-selection
          surface. Opened by:
            * Standalone chip-click on the doctype ContextStrip chip
              (via openDoctypePickerStandalone) — pendingDraftStart
              stays false; the pick lands in selectedDocCode and
              that's it.
            * The /draft orchestrator (via runDraftCommand →
              tryAdvanceDraft) when doctype is the missing piece.
              pendingDraftStart=true; on pick, the orchestration
              continues toward starting the run.
          On cancel: closes the picker. Standalone picks preserve
          whatever was already in selectedDocCode. Mid-/draft cancel
          clears pendingDraftStart so the writer isn't trapped. */}
      <DoctypePicker
        open={doctypePickerOpen}
        selectedDocCode={activeSelection?.docCode ?? selectedDocCode}
        appConfig={appConfig}
        // Tier-scoped: only the doctypes GET /doc-types returned for this
        // user (already filtered by doc_pricing.json for free_user). Empty
        // array while loading / on error = show nothing, never fall open.
        allowedDocCodes={tierAllowedDocCodes ?? []}
        // When building on an existing doc, the picker identifies what the
        // document IS rather than what to draft — swap the header copy.
        customImport={customImportActiveRef.current}
        onSelect={handleDoctypePick}
        onCancel={() => {
          setDoctypePickerOpen(false);
          if (pendingDraftStart) {
            setPendingDraftStart(false);
          }
        }}
      />

      <WorkspacePicker
        open={workspacePickerOpen}
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onSelect={(id) => {
          handleSelectedWorkspaceIdChange(id);
          setWorkspacePickerOpen(false);
        }}
        onCreate={async (name) => {
          await createAndSelectWorkspace(name);
          setWorkspacePickerOpen(false);
        }}
        onCancel={() => setWorkspacePickerOpen(false)}
      />
      {/* Kill confirmation dialog — extracted to separate component. */}
      <KillRunConfirmation
        open={showKillConfirmation}
        onConfirm={handleConfirmKill}
        onCancel={() => setShowKillConfirmation(false)}
        confirming={killConfirming}
      />
      <SaveAndStartFreshModal
        open={!!freshDraftPending}
        onConfirm={handleConfirmFreshDraft}
        onCancel={() => setFreshDraftPending(null)}
        confirming={freshDraftSaving}
      />
      {/* Dev-only overlay — visible when
          localStorage["asthra:debug:word-ops"] === "1". Renders nothing
          otherwise so the writer never sees it. */}
      <WordOpsPanel />
    </div>
  );
};

export default VariantCShell;
